import Uppy, { Plugin } from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import StatusBar from '@uppy/status-bar';
import GoogleDrive from '@uppy/google-drive';
import Dropbox from '@uppy/dropbox';
import Facebook from '@uppy/facebook';
import Instagram from '@uppy/instagram';
import AwsS3Multipart from '@uppy/aws-s3-multipart';
import { Provider } from '@uppy/companion-client';
import * as ProviderViews from '@uppy/provider-views';

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

/**
 * Takes a file object and turns it into fileID, by converting file.name to lowercase,
 * removing extra characters and adding type, size and lastModified
 *
 * @param {object} file
 * @returns {string} the fileID
 */

var generateFileID = function generateFileID(file) {
  // It's tempting to do `[items].filter(Boolean).join('-')` here, but that
  // is slower! simple string concatenation is fast
  var id = 'uppy';

  if (typeof file.name === 'string') {
    id += "-" + encodeFilename(file.name.toLowerCase());
  }

  if (file.type !== undefined) {
    id += "-" + file.type;
  }

  if (file.meta && typeof file.meta.relativePath === 'string') {
    id += "-" + encodeFilename(file.meta.relativePath.toLowerCase());
  }

  if (file.data.size !== undefined) {
    id += "-" + file.data.size;
  }

  if (file.data.lastModified !== undefined) {
    id += "-" + file.data.lastModified;
  }

  return id;
};

function encodeFilename(name) {
  var suffix = '';
  return name.replace(/[^A-Z0-9]/ig, function (character) {
    suffix += "-" + encodeCharacter(character);
    return '/';
  }) + suffix;
}

function encodeCharacter(character) {
  return character.charCodeAt(0).toString(32);
}

/**
 * Takes a full filename string and returns an object {name, extension}
 *
 * @param {string} fullFileName
 * @returns {object} {name, extension}
 */

var getFileNameAndExtension$1 = function getFileNameAndExtension(fullFileName) {
  var lastDot = fullFileName.lastIndexOf('.'); // these count as no extension: "no-dot", "trailing-dot."

  if (lastDot === -1 || lastDot === fullFileName.length - 1) {
    return {
      name: fullFileName,
      extension: undefined
    };
  }

  return {
    name: fullFileName.slice(0, lastDot),
    extension: fullFileName.slice(lastDot + 1)
  };
};

// ___Why not add the mime-types package?
//    It's 19.7kB gzipped, and we only need mime types for well-known extensions (for file previews).
// ___Where to take new extensions from?
//    https://github.com/jshttp/mime-db/blob/master/db.json
var mimeTypes$1 = {
  md: 'text/markdown',
  markdown: 'text/markdown',
  mp4: 'video/mp4',
  mp3: 'audio/mp3',
  svg: 'image/svg+xml',
  jpg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  heic: 'image/heic',
  heif: 'image/heif',
  yaml: 'text/yaml',
  yml: 'text/yaml',
  csv: 'text/csv',
  tsv: 'text/tab-separated-values',
  tab: 'text/tab-separated-values',
  avi: 'video/x-msvideo',
  mks: 'video/x-matroska',
  mkv: 'video/x-matroska',
  mov: 'video/quicktime',
  doc: 'application/msword',
  docm: 'application/vnd.ms-word.document.macroenabled.12',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  dot: 'application/msword',
  dotm: 'application/vnd.ms-word.template.macroenabled.12',
  dotx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
  xla: 'application/vnd.ms-excel',
  xlam: 'application/vnd.ms-excel.addin.macroenabled.12',
  xlc: 'application/vnd.ms-excel',
  xlf: 'application/x-xliff+xml',
  xlm: 'application/vnd.ms-excel',
  xls: 'application/vnd.ms-excel',
  xlsb: 'application/vnd.ms-excel.sheet.binary.macroenabled.12',
  xlsm: 'application/vnd.ms-excel.sheet.macroenabled.12',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xlt: 'application/vnd.ms-excel',
  xltm: 'application/vnd.ms-excel.template.macroenabled.12',
  xltx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
  xlw: 'application/vnd.ms-excel',
  txt: 'text/plain',
  text: 'text/plain',
  conf: 'text/plain',
  log: 'text/plain',
  pdf: 'application/pdf',
  zip: 'application/zip',
  '7z': 'application/x-7z-compressed',
  rar: 'application/x-rar-compressed',
  tar: 'application/x-tar',
  gz: 'application/gzip',
  dmg: 'application/x-apple-diskimage'
};

var getFileNameAndExtension = getFileNameAndExtension$1;

var mimeTypes = mimeTypes$1;

var getFileType = function getFileType(file) {
  var fileExtension = file.name ? getFileNameAndExtension(file.name).extension : null;
  fileExtension = fileExtension ? fileExtension.toLowerCase() : null;

  if (file.type) {
    // if mime type is set in the file object already, use that
    return file.type;
  }

  if (fileExtension && mimeTypes[fileExtension]) {
    // else, see if we can map extension to a mime type
    return mimeTypes[fileExtension];
  } // if all fails, fall back to a generic byte stream type


  return 'application/octet-stream';
};

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */

function isObject$3(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var isObject_1 = isObject$3;

/** Detect free variable `global` from Node.js. */

var freeGlobal$1 = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

var _freeGlobal = freeGlobal$1;

var freeGlobal = _freeGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root$2 = freeGlobal || freeSelf || Function('return this')();

var _root = root$2;

var root$1 = _root;

/**
 * Gets the timestamp of the number of milliseconds that have elapsed since
 * the Unix epoch (1 January 1970 00:00:00 UTC).
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Date
 * @returns {number} Returns the timestamp.
 * @example
 *
 * _.defer(function(stamp) {
 *   console.log(_.now() - stamp);
 * }, _.now());
 * // => Logs the number of milliseconds it took for the deferred invocation.
 */
var now$1 = function() {
  return root$1.Date.now();
};

var now_1 = now$1;

/** Used to match a single whitespace character. */

var reWhitespace = /\s/;

/**
 * Used by `_.trim` and `_.trimEnd` to get the index of the last non-whitespace
 * character of `string`.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {number} Returns the index of the last non-whitespace character.
 */
function trimmedEndIndex$1(string) {
  var index = string.length;

  while (index-- && reWhitespace.test(string.charAt(index))) {}
  return index;
}

var _trimmedEndIndex = trimmedEndIndex$1;

var trimmedEndIndex = _trimmedEndIndex;

/** Used to match leading whitespace. */
var reTrimStart = /^\s+/;

/**
 * The base implementation of `_.trim`.
 *
 * @private
 * @param {string} string The string to trim.
 * @returns {string} Returns the trimmed string.
 */
function baseTrim$1(string) {
  return string
    ? string.slice(0, trimmedEndIndex(string) + 1).replace(reTrimStart, '')
    : string;
}

var _baseTrim = baseTrim$1;

var root = _root;

/** Built-in value references. */
var Symbol$2 = root.Symbol;

var _Symbol = Symbol$2;

var Symbol$1 = _Symbol;

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto$1.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$1.toString;

/** Built-in value references. */
var symToStringTag$1 = Symbol$1 ? Symbol$1.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag$1(value) {
  var isOwn = hasOwnProperty.call(value, symToStringTag$1),
      tag = value[symToStringTag$1];

  try {
    value[symToStringTag$1] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString$1.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag$1] = tag;
    } else {
      delete value[symToStringTag$1];
    }
  }
  return result;
}

var _getRawTag = getRawTag$1;

/** Used for built-in method references. */

var objectProto = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString$1(value) {
  return nativeObjectToString.call(value);
}

var _objectToString = objectToString$1;

var Symbol = _Symbol,
    getRawTag = _getRawTag,
    objectToString = _objectToString;

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag = Symbol ? Symbol.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag$1(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag && symToStringTag in Object(value))
    ? getRawTag(value)
    : objectToString(value);
}

var _baseGetTag = baseGetTag$1;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */

function isObjectLike$1(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1 = isObjectLike$1;

var baseGetTag = _baseGetTag,
    isObjectLike = isObjectLike_1;

/** `Object#toString` result references. */
var symbolTag = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol$1(value) {
  return typeof value == 'symbol' ||
    (isObjectLike(value) && baseGetTag(value) == symbolTag);
}

var isSymbol_1 = isSymbol$1;

var baseTrim = _baseTrim,
    isObject$2 = isObject_1,
    isSymbol = isSymbol_1;

/** Used as references for various `Number` constants. */
var NAN = 0 / 0;

/** Used to detect bad signed hexadecimal string values. */
var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

/** Used to detect binary string values. */
var reIsBinary = /^0b[01]+$/i;

/** Used to detect octal string values. */
var reIsOctal = /^0o[0-7]+$/i;

/** Built-in method references without a dependency on `root`. */
var freeParseInt = parseInt;

/**
 * Converts `value` to a number.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {number} Returns the number.
 * @example
 *
 * _.toNumber(3.2);
 * // => 3.2
 *
 * _.toNumber(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toNumber(Infinity);
 * // => Infinity
 *
 * _.toNumber('3.2');
 * // => 3.2
 */
function toNumber$1(value) {
  if (typeof value == 'number') {
    return value;
  }
  if (isSymbol(value)) {
    return NAN;
  }
  if (isObject$2(value)) {
    var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
    value = isObject$2(other) ? (other + '') : other;
  }
  if (typeof value != 'string') {
    return value === 0 ? value : +value;
  }
  value = baseTrim(value);
  var isBinary = reIsBinary.test(value);
  return (isBinary || reIsOctal.test(value))
    ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
    : (reIsBadHex.test(value) ? NAN : +value);
}

var toNumber_1 = toNumber$1;

var isObject$1 = isObject_1,
    now = now_1,
    toNumber = toNumber_1;

/** Error message constants. */
var FUNC_ERROR_TEXT$1 = 'Expected a function';

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax = Math.max,
    nativeMin = Math.min;

/**
 * Creates a debounced function that delays invoking `func` until after `wait`
 * milliseconds have elapsed since the last time the debounced function was
 * invoked. The debounced function comes with a `cancel` method to cancel
 * delayed `func` invocations and a `flush` method to immediately invoke them.
 * Provide `options` to indicate whether `func` should be invoked on the
 * leading and/or trailing edge of the `wait` timeout. The `func` is invoked
 * with the last arguments provided to the debounced function. Subsequent
 * calls to the debounced function return the result of the last `func`
 * invocation.
 *
 * **Note:** If `leading` and `trailing` options are `true`, `func` is
 * invoked on the trailing edge of the timeout only if the debounced function
 * is invoked more than once during the `wait` timeout.
 *
 * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
 * until to the next tick, similar to `setTimeout` with a timeout of `0`.
 *
 * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
 * for details over the differences between `_.debounce` and `_.throttle`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to debounce.
 * @param {number} [wait=0] The number of milliseconds to delay.
 * @param {Object} [options={}] The options object.
 * @param {boolean} [options.leading=false]
 *  Specify invoking on the leading edge of the timeout.
 * @param {number} [options.maxWait]
 *  The maximum time `func` is allowed to be delayed before it's invoked.
 * @param {boolean} [options.trailing=true]
 *  Specify invoking on the trailing edge of the timeout.
 * @returns {Function} Returns the new debounced function.
 * @example
 *
 * // Avoid costly calculations while the window size is in flux.
 * jQuery(window).on('resize', _.debounce(calculateLayout, 150));
 *
 * // Invoke `sendMail` when clicked, debouncing subsequent calls.
 * jQuery(element).on('click', _.debounce(sendMail, 300, {
 *   'leading': true,
 *   'trailing': false
 * }));
 *
 * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
 * var debounced = _.debounce(batchLog, 250, { 'maxWait': 1000 });
 * var source = new EventSource('/stream');
 * jQuery(source).on('message', debounced);
 *
 * // Cancel the trailing debounced invocation.
 * jQuery(window).on('popstate', debounced.cancel);
 */
function debounce$1(func, wait, options) {
  var lastArgs,
      lastThis,
      maxWait,
      result,
      timerId,
      lastCallTime,
      lastInvokeTime = 0,
      leading = false,
      maxing = false,
      trailing = true;

  if (typeof func != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT$1);
  }
  wait = toNumber(wait) || 0;
  if (isObject$1(options)) {
    leading = !!options.leading;
    maxing = 'maxWait' in options;
    maxWait = maxing ? nativeMax(toNumber(options.maxWait) || 0, wait) : maxWait;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }

  function invokeFunc(time) {
    var args = lastArgs,
        thisArg = lastThis;

    lastArgs = lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }

  function leadingEdge(time) {
    // Reset any `maxWait` timer.
    lastInvokeTime = time;
    // Start the timer for the trailing edge.
    timerId = setTimeout(timerExpired, wait);
    // Invoke the leading edge.
    return leading ? invokeFunc(time) : result;
  }

  function remainingWait(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime,
        timeWaiting = wait - timeSinceLastCall;

    return maxing
      ? nativeMin(timeWaiting, maxWait - timeSinceLastInvoke)
      : timeWaiting;
  }

  function shouldInvoke(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime;

    // Either this is the first call, activity has stopped and we're at the
    // trailing edge, the system time has gone backwards and we're treating
    // it as the trailing edge, or we've hit the `maxWait` limit.
    return (lastCallTime === undefined || (timeSinceLastCall >= wait) ||
      (timeSinceLastCall < 0) || (maxing && timeSinceLastInvoke >= maxWait));
  }

  function timerExpired() {
    var time = now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    // Restart the timer.
    timerId = setTimeout(timerExpired, remainingWait(time));
  }

  function trailingEdge(time) {
    timerId = undefined;

    // Only invoke if we have `lastArgs` which means `func` has been
    // debounced at least once.
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = undefined;
    return result;
  }

  function cancel() {
    if (timerId !== undefined) {
      clearTimeout(timerId);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timerId = undefined;
  }

  function flush() {
    return timerId === undefined ? result : trailingEdge(now());
  }

  function debounced() {
    var time = now(),
        isInvoking = shouldInvoke(time);

    lastArgs = arguments;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId === undefined) {
        return leadingEdge(lastCallTime);
      }
      if (maxing) {
        // Handle invocations in a tight loop.
        clearTimeout(timerId);
        timerId = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    if (timerId === undefined) {
      timerId = setTimeout(timerExpired, wait);
    }
    return result;
  }
  debounced.cancel = cancel;
  debounced.flush = flush;
  return debounced;
}

var debounce_1 = debounce$1;

var debounce = debounce_1,
    isObject = isObject_1;

/** Error message constants. */
var FUNC_ERROR_TEXT = 'Expected a function';

/**
 * Creates a throttled function that only invokes `func` at most once per
 * every `wait` milliseconds. The throttled function comes with a `cancel`
 * method to cancel delayed `func` invocations and a `flush` method to
 * immediately invoke them. Provide `options` to indicate whether `func`
 * should be invoked on the leading and/or trailing edge of the `wait`
 * timeout. The `func` is invoked with the last arguments provided to the
 * throttled function. Subsequent calls to the throttled function return the
 * result of the last `func` invocation.
 *
 * **Note:** If `leading` and `trailing` options are `true`, `func` is
 * invoked on the trailing edge of the timeout only if the throttled function
 * is invoked more than once during the `wait` timeout.
 *
 * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
 * until to the next tick, similar to `setTimeout` with a timeout of `0`.
 *
 * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
 * for details over the differences between `_.throttle` and `_.debounce`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to throttle.
 * @param {number} [wait=0] The number of milliseconds to throttle invocations to.
 * @param {Object} [options={}] The options object.
 * @param {boolean} [options.leading=true]
 *  Specify invoking on the leading edge of the timeout.
 * @param {boolean} [options.trailing=true]
 *  Specify invoking on the trailing edge of the timeout.
 * @returns {Function} Returns the new throttled function.
 * @example
 *
 * // Avoid excessively updating the position while scrolling.
 * jQuery(window).on('scroll', _.throttle(updatePosition, 100));
 *
 * // Invoke `renewToken` when the click event is fired, but not more than once every 5 minutes.
 * var throttled = _.throttle(renewToken, 300000, { 'trailing': false });
 * jQuery(element).on('click', throttled);
 *
 * // Cancel the trailing throttled invocation.
 * jQuery(window).on('popstate', throttled.cancel);
 */
function throttle(func, wait, options) {
  var leading = true,
      trailing = true;

  if (typeof func != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  if (isObject(options)) {
    leading = 'leading' in options ? !!options.leading : leading;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }
  return debounce(func, wait, {
    'leading': leading,
    'maxWait': wait,
    'trailing': trailing
  });
}

var throttle_1 = throttle;

var VNode = function VNode() {};

var options = {};

var stack = [];

var EMPTY_CHILDREN = [];

function h$1(nodeName, attributes) {
	var children = EMPTY_CHILDREN,
	    lastSimple,
	    child,
	    simple,
	    i;
	for (i = arguments.length; i-- > 2;) {
		stack.push(arguments[i]);
	}
	if (attributes && attributes.children != null) {
		if (!stack.length) stack.push(attributes.children);
		delete attributes.children;
	}
	while (stack.length) {
		if ((child = stack.pop()) && child.pop !== undefined) {
			for (i = child.length; i--;) {
				stack.push(child[i]);
			}
		} else {
			if (typeof child === 'boolean') child = null;

			if (simple = typeof nodeName !== 'function') {
				if (child == null) child = '';else if (typeof child === 'number') child = String(child);else if (typeof child !== 'string') simple = false;
			}

			if (simple && lastSimple) {
				children[children.length - 1] += child;
			} else if (children === EMPTY_CHILDREN) {
				children = [child];
			} else {
				children.push(child);
			}

			lastSimple = simple;
		}
	}

	var p = new VNode();
	p.nodeName = nodeName;
	p.children = children;
	p.attributes = attributes == null ? undefined : attributes;
	p.key = attributes == null ? undefined : attributes.key;

	if (options.vnode !== undefined) options.vnode(p);

	return p;
}

function extend(obj, props) {
  for (var i in props) {
    obj[i] = props[i];
  }return obj;
}

function applyRef(ref, value) {
  if (ref) {
    if (typeof ref == 'function') ref(value);else ref.current = value;
  }
}

var defer = typeof Promise == 'function' ? Promise.resolve().then.bind(Promise.resolve()) : setTimeout;

var IS_NON_DIMENSIONAL = /acit|ex(?:s|g|n|p|$)|rph|ows|mnc|ntw|ine[ch]|zoo|^ord/i;

var items = [];

function enqueueRender(component) {
	if (!component._dirty && (component._dirty = true) && items.push(component) == 1) {
		(options.debounceRendering || defer)(rerender);
	}
}

function rerender() {
	var p;
	while (p = items.pop()) {
		if (p._dirty) renderComponent(p);
	}
}

function isSameNodeType(node, vnode, hydrating) {
	if (typeof vnode === 'string' || typeof vnode === 'number') {
		return node.splitText !== undefined;
	}
	if (typeof vnode.nodeName === 'string') {
		return !node._componentConstructor && isNamedNode(node, vnode.nodeName);
	}
	return hydrating || node._componentConstructor === vnode.nodeName;
}

function isNamedNode(node, nodeName) {
	return node.normalizedNodeName === nodeName || node.nodeName.toLowerCase() === nodeName.toLowerCase();
}

function getNodeProps(vnode) {
	var props = extend({}, vnode.attributes);
	props.children = vnode.children;

	var defaultProps = vnode.nodeName.defaultProps;
	if (defaultProps !== undefined) {
		for (var i in defaultProps) {
			if (props[i] === undefined) {
				props[i] = defaultProps[i];
			}
		}
	}

	return props;
}

function createNode(nodeName, isSvg) {
	var node = isSvg ? document.createElementNS('http://www.w3.org/2000/svg', nodeName) : document.createElement(nodeName);
	node.normalizedNodeName = nodeName;
	return node;
}

function removeNode(node) {
	var parentNode = node.parentNode;
	if (parentNode) parentNode.removeChild(node);
}

function setAccessor(node, name, old, value, isSvg) {
	if (name === 'className') name = 'class';

	if (name === 'key') ; else if (name === 'ref') {
		applyRef(old, null);
		applyRef(value, node);
	} else if (name === 'class' && !isSvg) {
		node.className = value || '';
	} else if (name === 'style') {
		if (!value || typeof value === 'string' || typeof old === 'string') {
			node.style.cssText = value || '';
		}
		if (value && typeof value === 'object') {
			if (typeof old !== 'string') {
				for (var i in old) {
					if (!(i in value)) node.style[i] = '';
				}
			}
			for (var i in value) {
				node.style[i] = typeof value[i] === 'number' && IS_NON_DIMENSIONAL.test(i) === false ? value[i] + 'px' : value[i];
			}
		}
	} else if (name === 'dangerouslySetInnerHTML') {
		if (value) node.innerHTML = value.__html || '';
	} else if (name[0] == 'o' && name[1] == 'n') {
		var useCapture = name !== (name = name.replace(/Capture$/, ''));
		name = name.toLowerCase().substring(2);
		if (value) {
			if (!old) node.addEventListener(name, eventProxy, useCapture);
		} else {
			node.removeEventListener(name, eventProxy, useCapture);
		}
		(node._listeners || (node._listeners = {}))[name] = value;
	} else if (name !== 'list' && name !== 'type' && !isSvg && name in node) {
		try {
			node[name] = value == null ? '' : value;
		} catch (e) {}
		if ((value == null || value === false) && name != 'spellcheck') node.removeAttribute(name);
	} else {
		var ns = isSvg && name !== (name = name.replace(/^xlink:?/, ''));

		if (value == null || value === false) {
			if (ns) node.removeAttributeNS('http://www.w3.org/1999/xlink', name.toLowerCase());else node.removeAttribute(name);
		} else if (typeof value !== 'function') {
			if (ns) node.setAttributeNS('http://www.w3.org/1999/xlink', name.toLowerCase(), value);else node.setAttribute(name, value);
		}
	}
}

function eventProxy(e) {
	return this._listeners[e.type](options.event && options.event(e) || e);
}

var mounts = [];

var diffLevel = 0;

var isSvgMode = false;

var hydrating = false;

function flushMounts() {
	var c;
	while (c = mounts.shift()) {
		if (options.afterMount) options.afterMount(c);
		if (c.componentDidMount) c.componentDidMount();
	}
}

function diff(dom, vnode, context, mountAll, parent, componentRoot) {
	if (!diffLevel++) {
		isSvgMode = parent != null && parent.ownerSVGElement !== undefined;

		hydrating = dom != null && !('__preactattr_' in dom);
	}

	var ret = idiff(dom, vnode, context, mountAll, componentRoot);

	if (parent && ret.parentNode !== parent) parent.appendChild(ret);

	if (! --diffLevel) {
		hydrating = false;

		if (!componentRoot) flushMounts();
	}

	return ret;
}

function idiff(dom, vnode, context, mountAll, componentRoot) {
	var out = dom,
	    prevSvgMode = isSvgMode;

	if (vnode == null || typeof vnode === 'boolean') vnode = '';

	if (typeof vnode === 'string' || typeof vnode === 'number') {
		if (dom && dom.splitText !== undefined && dom.parentNode && (!dom._component || componentRoot)) {
			if (dom.nodeValue != vnode) {
				dom.nodeValue = vnode;
			}
		} else {
			out = document.createTextNode(vnode);
			if (dom) {
				if (dom.parentNode) dom.parentNode.replaceChild(out, dom);
				recollectNodeTree(dom, true);
			}
		}

		out['__preactattr_'] = true;

		return out;
	}

	var vnodeName = vnode.nodeName;
	if (typeof vnodeName === 'function') {
		return buildComponentFromVNode(dom, vnode, context, mountAll);
	}

	isSvgMode = vnodeName === 'svg' ? true : vnodeName === 'foreignObject' ? false : isSvgMode;

	vnodeName = String(vnodeName);
	if (!dom || !isNamedNode(dom, vnodeName)) {
		out = createNode(vnodeName, isSvgMode);

		if (dom) {
			while (dom.firstChild) {
				out.appendChild(dom.firstChild);
			}
			if (dom.parentNode) dom.parentNode.replaceChild(out, dom);

			recollectNodeTree(dom, true);
		}
	}

	var fc = out.firstChild,
	    props = out['__preactattr_'],
	    vchildren = vnode.children;

	if (props == null) {
		props = out['__preactattr_'] = {};
		for (var a = out.attributes, i = a.length; i--;) {
			props[a[i].name] = a[i].value;
		}
	}

	if (!hydrating && vchildren && vchildren.length === 1 && typeof vchildren[0] === 'string' && fc != null && fc.splitText !== undefined && fc.nextSibling == null) {
		if (fc.nodeValue != vchildren[0]) {
			fc.nodeValue = vchildren[0];
		}
	} else if (vchildren && vchildren.length || fc != null) {
			innerDiffNode(out, vchildren, context, mountAll, hydrating || props.dangerouslySetInnerHTML != null);
		}

	diffAttributes(out, vnode.attributes, props);

	isSvgMode = prevSvgMode;

	return out;
}

function innerDiffNode(dom, vchildren, context, mountAll, isHydrating) {
	var originalChildren = dom.childNodes,
	    children = [],
	    keyed = {},
	    keyedLen = 0,
	    min = 0,
	    len = originalChildren.length,
	    childrenLen = 0,
	    vlen = vchildren ? vchildren.length : 0,
	    j,
	    c,
	    f,
	    vchild,
	    child;

	if (len !== 0) {
		for (var i = 0; i < len; i++) {
			var _child = originalChildren[i],
			    props = _child['__preactattr_'],
			    key = vlen && props ? _child._component ? _child._component.__key : props.key : null;
			if (key != null) {
				keyedLen++;
				keyed[key] = _child;
			} else if (props || (_child.splitText !== undefined ? isHydrating ? _child.nodeValue.trim() : true : isHydrating)) {
				children[childrenLen++] = _child;
			}
		}
	}

	if (vlen !== 0) {
		for (var i = 0; i < vlen; i++) {
			vchild = vchildren[i];
			child = null;

			var key = vchild.key;
			if (key != null) {
				if (keyedLen && keyed[key] !== undefined) {
					child = keyed[key];
					keyed[key] = undefined;
					keyedLen--;
				}
			} else if (min < childrenLen) {
					for (j = min; j < childrenLen; j++) {
						if (children[j] !== undefined && isSameNodeType(c = children[j], vchild, isHydrating)) {
							child = c;
							children[j] = undefined;
							if (j === childrenLen - 1) childrenLen--;
							if (j === min) min++;
							break;
						}
					}
				}

			child = idiff(child, vchild, context, mountAll);

			f = originalChildren[i];
			if (child && child !== dom && child !== f) {
				if (f == null) {
					dom.appendChild(child);
				} else if (child === f.nextSibling) {
					removeNode(f);
				} else {
					dom.insertBefore(child, f);
				}
			}
		}
	}

	if (keyedLen) {
		for (var i in keyed) {
			if (keyed[i] !== undefined) recollectNodeTree(keyed[i], false);
		}
	}

	while (min <= childrenLen) {
		if ((child = children[childrenLen--]) !== undefined) recollectNodeTree(child, false);
	}
}

function recollectNodeTree(node, unmountOnly) {
	var component = node._component;
	if (component) {
		unmountComponent(component);
	} else {
		if (node['__preactattr_'] != null) applyRef(node['__preactattr_'].ref, null);

		if (unmountOnly === false || node['__preactattr_'] == null) {
			removeNode(node);
		}

		removeChildren(node);
	}
}

function removeChildren(node) {
	node = node.lastChild;
	while (node) {
		var next = node.previousSibling;
		recollectNodeTree(node, true);
		node = next;
	}
}

function diffAttributes(dom, attrs, old) {
	var name;

	for (name in old) {
		if (!(attrs && attrs[name] != null) && old[name] != null) {
			setAccessor(dom, name, old[name], old[name] = undefined, isSvgMode);
		}
	}

	for (name in attrs) {
		if (name !== 'children' && name !== 'innerHTML' && (!(name in old) || attrs[name] !== (name === 'value' || name === 'checked' ? dom[name] : old[name]))) {
			setAccessor(dom, name, old[name], old[name] = attrs[name], isSvgMode);
		}
	}
}

var recyclerComponents = [];

function createComponent(Ctor, props, context) {
	var inst,
	    i = recyclerComponents.length;

	if (Ctor.prototype && Ctor.prototype.render) {
		inst = new Ctor(props, context);
		Component.call(inst, props, context);
	} else {
		inst = new Component(props, context);
		inst.constructor = Ctor;
		inst.render = doRender;
	}

	while (i--) {
		if (recyclerComponents[i].constructor === Ctor) {
			inst.nextBase = recyclerComponents[i].nextBase;
			recyclerComponents.splice(i, 1);
			return inst;
		}
	}

	return inst;
}

function doRender(props, state, context) {
	return this.constructor(props, context);
}

function setComponentProps(component, props, renderMode, context, mountAll) {
	if (component._disable) return;
	component._disable = true;

	component.__ref = props.ref;
	component.__key = props.key;
	delete props.ref;
	delete props.key;

	if (typeof component.constructor.getDerivedStateFromProps === 'undefined') {
		if (!component.base || mountAll) {
			if (component.componentWillMount) component.componentWillMount();
		} else if (component.componentWillReceiveProps) {
			component.componentWillReceiveProps(props, context);
		}
	}

	if (context && context !== component.context) {
		if (!component.prevContext) component.prevContext = component.context;
		component.context = context;
	}

	if (!component.prevProps) component.prevProps = component.props;
	component.props = props;

	component._disable = false;

	if (renderMode !== 0) {
		if (renderMode === 1 || options.syncComponentUpdates !== false || !component.base) {
			renderComponent(component, 1, mountAll);
		} else {
			enqueueRender(component);
		}
	}

	applyRef(component.__ref, component);
}

function renderComponent(component, renderMode, mountAll, isChild) {
	if (component._disable) return;

	var props = component.props,
	    state = component.state,
	    context = component.context,
	    previousProps = component.prevProps || props,
	    previousState = component.prevState || state,
	    previousContext = component.prevContext || context,
	    isUpdate = component.base,
	    nextBase = component.nextBase,
	    initialBase = isUpdate || nextBase,
	    initialChildComponent = component._component,
	    skip = false,
	    snapshot = previousContext,
	    rendered,
	    inst,
	    cbase;

	if (component.constructor.getDerivedStateFromProps) {
		state = extend(extend({}, state), component.constructor.getDerivedStateFromProps(props, state));
		component.state = state;
	}

	if (isUpdate) {
		component.props = previousProps;
		component.state = previousState;
		component.context = previousContext;
		if (renderMode !== 2 && component.shouldComponentUpdate && component.shouldComponentUpdate(props, state, context) === false) {
			skip = true;
		} else if (component.componentWillUpdate) {
			component.componentWillUpdate(props, state, context);
		}
		component.props = props;
		component.state = state;
		component.context = context;
	}

	component.prevProps = component.prevState = component.prevContext = component.nextBase = null;
	component._dirty = false;

	if (!skip) {
		rendered = component.render(props, state, context);

		if (component.getChildContext) {
			context = extend(extend({}, context), component.getChildContext());
		}

		if (isUpdate && component.getSnapshotBeforeUpdate) {
			snapshot = component.getSnapshotBeforeUpdate(previousProps, previousState);
		}

		var childComponent = rendered && rendered.nodeName,
		    toUnmount,
		    base;

		if (typeof childComponent === 'function') {

			var childProps = getNodeProps(rendered);
			inst = initialChildComponent;

			if (inst && inst.constructor === childComponent && childProps.key == inst.__key) {
				setComponentProps(inst, childProps, 1, context, false);
			} else {
				toUnmount = inst;

				component._component = inst = createComponent(childComponent, childProps, context);
				inst.nextBase = inst.nextBase || nextBase;
				inst._parentComponent = component;
				setComponentProps(inst, childProps, 0, context, false);
				renderComponent(inst, 1, mountAll, true);
			}

			base = inst.base;
		} else {
			cbase = initialBase;

			toUnmount = initialChildComponent;
			if (toUnmount) {
				cbase = component._component = null;
			}

			if (initialBase || renderMode === 1) {
				if (cbase) cbase._component = null;
				base = diff(cbase, rendered, context, mountAll || !isUpdate, initialBase && initialBase.parentNode, true);
			}
		}

		if (initialBase && base !== initialBase && inst !== initialChildComponent) {
			var baseParent = initialBase.parentNode;
			if (baseParent && base !== baseParent) {
				baseParent.replaceChild(base, initialBase);

				if (!toUnmount) {
					initialBase._component = null;
					recollectNodeTree(initialBase, false);
				}
			}
		}

		if (toUnmount) {
			unmountComponent(toUnmount);
		}

		component.base = base;
		if (base && !isChild) {
			var componentRef = component,
			    t = component;
			while (t = t._parentComponent) {
				(componentRef = t).base = base;
			}
			base._component = componentRef;
			base._componentConstructor = componentRef.constructor;
		}
	}

	if (!isUpdate || mountAll) {
		mounts.push(component);
	} else if (!skip) {

		if (component.componentDidUpdate) {
			component.componentDidUpdate(previousProps, previousState, snapshot);
		}
		if (options.afterUpdate) options.afterUpdate(component);
	}

	while (component._renderCallbacks.length) {
		component._renderCallbacks.pop().call(component);
	}if (!diffLevel && !isChild) flushMounts();
}

function buildComponentFromVNode(dom, vnode, context, mountAll) {
	var c = dom && dom._component,
	    originalComponent = c,
	    oldDom = dom,
	    isDirectOwner = c && dom._componentConstructor === vnode.nodeName,
	    isOwner = isDirectOwner,
	    props = getNodeProps(vnode);
	while (c && !isOwner && (c = c._parentComponent)) {
		isOwner = c.constructor === vnode.nodeName;
	}

	if (c && isOwner && (!mountAll || c._component)) {
		setComponentProps(c, props, 3, context, mountAll);
		dom = c.base;
	} else {
		if (originalComponent && !isDirectOwner) {
			unmountComponent(originalComponent);
			dom = oldDom = null;
		}

		c = createComponent(vnode.nodeName, props, context);
		if (dom && !c.nextBase) {
			c.nextBase = dom;

			oldDom = null;
		}
		setComponentProps(c, props, 1, context, mountAll);
		dom = c.base;

		if (oldDom && dom !== oldDom) {
			oldDom._component = null;
			recollectNodeTree(oldDom, false);
		}
	}

	return dom;
}

function unmountComponent(component) {
	if (options.beforeUnmount) options.beforeUnmount(component);

	var base = component.base;

	component._disable = true;

	if (component.componentWillUnmount) component.componentWillUnmount();

	component.base = null;

	var inner = component._component;
	if (inner) {
		unmountComponent(inner);
	} else if (base) {
		if (base['__preactattr_'] != null) applyRef(base['__preactattr_'].ref, null);

		component.nextBase = base;

		removeNode(base);
		recyclerComponents.push(component);

		removeChildren(base);
	}

	applyRef(component.__ref, null);
}

function Component(props, context) {
	this._dirty = true;

	this.context = context;

	this.props = props;

	this.state = this.state || {};

	this._renderCallbacks = [];
}

extend(Component.prototype, {
	setState: function setState(state, callback) {
		if (!this.prevState) this.prevState = this.state;
		this.state = extend(extend({}, this.state), typeof state === 'function' ? state(this.state, this.props) : state);
		if (callback) this._renderCallbacks.push(callback);
		enqueueRender(this);
	},
	forceUpdate: function forceUpdate(callback) {
		if (callback) this._renderCallbacks.push(callback);
		renderComponent(this, 2);
	},
	render: function render() {}
});

const { ProviderViews: ProviderViewsClass } = ProviderViews;
class GooglePhotosProviderViews extends ProviderViewsClass {
    constructor(plugin, opts) {
        super(plugin, opts);
        this.icon = plugin.icon;
    }
    toggleCheckbox(e, file) {
        e.stopPropagation();
        e.preventDefault();
        // Shared Drives aren't selectable; for all else, defer to the base ProviderView.
        // @todo isTeamDrive is left for backward compatibility. We should remove it in the next
        // major release.
        if (!file.custom.isTeamDrive && !file.custom.isSharedDrive) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            super.toggleCheckbox(e, file);
        }
    }
}

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
// /** @jsx h */
class GooglePhotos extends Plugin {
    constructor(uppy, options) {
        super(uppy, options);
        this.options = options;
        this.id = options.id ?? 'GooglePhotosPlugin';
        this.title = options.title ?? 'Google Photos';
        Provider.initPlugin(this, options);
        this.title = options.title ?? 'Google Photos';
        this.type = options.type ?? 'acquirer';
        this.icon = () => (h$1("svg", { xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 48 48", width: "32px", height: "32px" },
            h$1("path", { fill: "#f44336", d: "M37,24h-6.936C26.715,24,24,21.285,24,17.936V0.573c0-0.219,0.26-0.333,0.421-0.184L37,12V24z" }),
            h$1("path", { fill: "#ad1457", d: "M37,24h-6.936c-1.436,0-2.755-0.499-3.794-1.333L37,12V24z" }),
            h$1("path", { fill: "#8bc34a", d: "M11,24h6.936C21.285,24,24,26.715,24,30.064v17.362c0,0.219-0.26,0.333-0.421,0.184L11,36V24z" }),
            h$1("path", { fill: "#009688", d: "M11,24h6.936c1.436,0,2.755,0.499,3.794,1.333L11,36V24z" }),
            h$1("path", { fill: "#ffc107", d: "M24,11v6.936C24,21.285,21.285,24,17.936,24H0.573c-0.219,0-0.333-0.26-0.184-0.421L12,11H24z" }),
            h$1("path", { fill: "#ff9800", d: "M24,11v6.936c0,1.436-0.499,2.755-1.333,3.794L12,11H24z" }),
            h$1("path", { fill: "#448aff", d: "M24,37v-6.936C24,26.715,26.715,24,30.064,24h17.362c0.219,0,0.333,0.26,0.184,0.421L36,37H24z" }),
            h$1("path", { fill: "#1565c0", d: "M24,37v-6.936c0-1.436,0.499-2.755,1.333-3.794L36,37H24z" })));
        this.provider = new Provider(uppy, {
            companionUrl: this.options.companionUrl,
            companionHeaders: this.options.companionHeaders ?? this.options.serverHeaders ?? {},
            provider: options?.provider ?? 'googlePhotos',
            pluginId: this.id,
        });
        this.render = this.render.bind(this);
        this.install = this.install.bind(this);
    }
    install() {
        this.view = new GooglePhotosProviderViews(this, {
            provider: this.provider,
        });
        const target = this.options.target;
        if (target) {
            this.mount(target, this);
        }
    }
    uninstall() {
        this.view.tearDown();
        this.unmount();
    }
    onFirstRender() {
        return this.view.getFolder();
    }
    render(state) {
        const viewOptions = {};
        const pluginState = this.getPluginState();
        const { files, folders } = pluginState;
        if (files.length && !folders.length) {
            viewOptions['viewType'] = 'grid';
        }
        return this.view.render(state, viewOptions);
    }
}

/**
 * Check if a URL string is an object URL from `URL.createObjectURL`.
 *
 * @param {string} url
 * @returns {boolean}
 */

var isObjectURL = function isObjectURL(url) {
  return url.indexOf('blob:') === 0;
};

var thumbnailWorker = "function resizeImage(image, targetWidth, targetHeight, allowTransparency) {\n    const canvas = new OffscreenCanvas(targetWidth, targetHeight);\n    const context = canvas.getContext('2d');\n    if (!allowTransparency && context) {\n        context.fillStyle = 'white';\n        context.fillRect(0, 0, canvas.width, canvas.height);\n    }\n    context?.drawImage(image, 0, 0, canvas.width, canvas.height);\n    return canvas;\n}\nfunction getProportionalDimensions(img, width, height) {\n    const aspect = img.width / img.height;\n    if (width != null) {\n        return {\n            width,\n            height: Math.round(width / aspect),\n        };\n    }\n    if (height != null) {\n        return {\n            width: Math.round(height * aspect),\n            height,\n        };\n    }\n    return {\n        width: 400,\n        height: Math.round(400 / aspect),\n    };\n}\nlet running = false;\nconst queue = [];\nasync function processFile(input) {\n    if (running) {\n        queue.push(input);\n        return;\n    }\n    running = true;\n    const { file, id } = input;\n    try {\n        const image = await createImageBitmap(file);\n        const { width, height } = image;\n        const dimensions = getProportionalDimensions(image, 400, 400);\n        const allowTransparency = file.type === 'image/png';\n        const resizedImage = resizeImage(image, dimensions.width, dimensions.height, allowTransparency);\n        const blob = await resizedImage.convertToBlob({ type: file.type });\n        const url = URL.createObjectURL(blob);\n        const output = {\n            state: 'success',\n            id,\n            type: file.type,\n            url,\n            width,\n            height,\n        };\n        self.postMessage(output);\n    }\n    catch (err) {\n        const output = { state: 'error', id, error: err.message };\n        self.postMessage(output);\n    }\n    finally {\n        running = false;\n        if (queue.length) {\n            const nextInput = queue.shift();\n            if (nextInput) {\n                void processFile(nextInput);\n            }\n        }\n    }\n}\nself.onmessage = ({ data }) => {\n    void processFile(data);\n};\nexport default null;";

var e="undefined"!=typeof self?self:global;const t="undefined"!=typeof navigator,i=t&&"undefined"==typeof HTMLImageElement,n=!("undefined"==typeof global||"undefined"==typeof process||!process.versions||!process.versions.node),s=e.Buffer,r=e.BigInt,a=!!s,o=e=>e;function l(e,t=o){if(n)try{return "function"==typeof require?Promise.resolve(t(require(e))):import(/* webpackIgnore: true */ e).then(t)}catch(t){console.warn(`Couldn't load ${e}`);}}let h=e.fetch;const u=e=>h=e;if(!e.fetch){const e=l("http",(e=>e)),t=l("https",(e=>e)),i=(n,{headers:s}={})=>new Promise((async(r,a)=>{let{port:o,hostname:l,pathname:h,protocol:u,search:c}=new URL(n);const f={method:"GET",hostname:l,path:encodeURI(h)+c,headers:s};""!==o&&(f.port=Number(o));const d=("https:"===u?await t:await e).request(f,(e=>{if(301===e.statusCode||302===e.statusCode){let t=new URL(e.headers.location,n).toString();return i(t,{headers:s}).then(r).catch(a)}r({status:e.statusCode,arrayBuffer:()=>new Promise((t=>{let i=[];e.on("data",(e=>i.push(e))),e.on("end",(()=>t(Buffer.concat(i))));}))});}));d.on("error",a),d.end();}));u(i);}function c(e,t,i){return t in e?Object.defineProperty(e,t,{value:i,enumerable:!0,configurable:!0,writable:!0}):e[t]=i,e}const f=e=>p(e)?void 0:e,d=e=>void 0!==e;function p(e){return void 0===e||(e instanceof Map?0===e.size:0===Object.values(e).filter(d).length)}function g(e){let t=new Error(e);throw delete t.stack,t}function m(e){return ""===(e=function(e){for(;e.endsWith("\0");)e=e.slice(0,-1);return e}(e).trim())?void 0:e}function S(e){let t=function(e){let t=0;return e.ifd0.enabled&&(t+=1024),e.exif.enabled&&(t+=2048),e.makerNote&&(t+=2048),e.userComment&&(t+=1024),e.gps.enabled&&(t+=512),e.interop.enabled&&(t+=100),e.ifd1.enabled&&(t+=1024),t+2048}(e);return e.jfif.enabled&&(t+=50),e.xmp.enabled&&(t+=2e4),e.iptc.enabled&&(t+=14e3),e.icc.enabled&&(t+=6e3),t}const C=e=>String.fromCharCode.apply(null,e),y="undefined"!=typeof TextDecoder?new TextDecoder("utf-8"):void 0;function b(e){return y?y.decode(e):a?Buffer.from(e).toString("utf8"):decodeURIComponent(escape(C(e)))}class I{static from(e,t){return e instanceof this&&e.le===t?e:new I(e,void 0,void 0,t)}constructor(e,t=0,i,n){if("boolean"==typeof n&&(this.le=n),Array.isArray(e)&&(e=new Uint8Array(e)),0===e)this.byteOffset=0,this.byteLength=0;else if(e instanceof ArrayBuffer){void 0===i&&(i=e.byteLength-t);let n=new DataView(e,t,i);this._swapDataView(n);}else if(e instanceof Uint8Array||e instanceof DataView||e instanceof I){void 0===i&&(i=e.byteLength-t),(t+=e.byteOffset)+i>e.byteOffset+e.byteLength&&g("Creating view outside of available memory in ArrayBuffer");let n=new DataView(e.buffer,t,i);this._swapDataView(n);}else if("number"==typeof e){let t=new DataView(new ArrayBuffer(e));this._swapDataView(t);}else g("Invalid input argument for BufferView: "+e);}_swapArrayBuffer(e){this._swapDataView(new DataView(e));}_swapBuffer(e){this._swapDataView(new DataView(e.buffer,e.byteOffset,e.byteLength));}_swapDataView(e){this.dataView=e,this.buffer=e.buffer,this.byteOffset=e.byteOffset,this.byteLength=e.byteLength;}_lengthToEnd(e){return this.byteLength-e}set(e,t,i=I){return e instanceof DataView||e instanceof I?e=new Uint8Array(e.buffer,e.byteOffset,e.byteLength):e instanceof ArrayBuffer&&(e=new Uint8Array(e)),e instanceof Uint8Array||g("BufferView.set(): Invalid data argument."),this.toUint8().set(e,t),new i(this,t,e.byteLength)}subarray(e,t){return t=t||this._lengthToEnd(e),new I(this,e,t)}toUint8(){return new Uint8Array(this.buffer,this.byteOffset,this.byteLength)}getUint8Array(e,t){return new Uint8Array(this.buffer,this.byteOffset+e,t)}getString(e=0,t=this.byteLength){return b(this.getUint8Array(e,t))}getLatin1String(e=0,t=this.byteLength){let i=this.getUint8Array(e,t);return C(i)}getUnicodeString(e=0,t=this.byteLength){const i=[];for(let n=0;n<t&&e+n<this.byteLength;n+=2)i.push(this.getUint16(e+n));return C(i)}getInt8(e){return this.dataView.getInt8(e)}getUint8(e){return this.dataView.getUint8(e)}getInt16(e,t=this.le){return this.dataView.getInt16(e,t)}getInt32(e,t=this.le){return this.dataView.getInt32(e,t)}getUint16(e,t=this.le){return this.dataView.getUint16(e,t)}getUint32(e,t=this.le){return this.dataView.getUint32(e,t)}getFloat32(e,t=this.le){return this.dataView.getFloat32(e,t)}getFloat64(e,t=this.le){return this.dataView.getFloat64(e,t)}getFloat(e,t=this.le){return this.dataView.getFloat32(e,t)}getDouble(e,t=this.le){return this.dataView.getFloat64(e,t)}getUintBytes(e,t,i){switch(t){case 1:return this.getUint8(e,i);case 2:return this.getUint16(e,i);case 4:return this.getUint32(e,i);case 8:return this.getUint64&&this.getUint64(e,i)}}getUint(e,t,i){switch(t){case 8:return this.getUint8(e,i);case 16:return this.getUint16(e,i);case 32:return this.getUint32(e,i);case 64:return this.getUint64&&this.getUint64(e,i)}}toString(e){return this.dataView.toString(e,this.constructor.name)}ensureChunk(){}}function P(e,t){g(`${e} '${t}' was not loaded, try using full build of exifr.`);}class k extends Map{constructor(e){super(),this.kind=e;}get(e,t){return this.has(e)||P(this.kind,e),t&&(e in t||function(e,t){g(`Unknown ${e} '${t}'.`);}(this.kind,e),t[e].enabled||P(this.kind,e)),super.get(e)}keyList(){return Array.from(this.keys())}}var w=new k("file parser"),T=new k("segment parser"),A=new k("file reader");function D(e,n){return "string"==typeof e?O(e,n):t&&!i&&e instanceof HTMLImageElement?O(e.src,n):e instanceof Uint8Array||e instanceof ArrayBuffer||e instanceof DataView?new I(e):t&&e instanceof Blob?x(e,n,"blob",R):void g("Invalid input argument")}function O(e,i){return (s=e).startsWith("data:")||s.length>1e4?v(e,i,"base64"):n&&e.includes("://")?x(e,i,"url",M):n?v(e,i,"fs"):t?x(e,i,"url",M):void g("Invalid input argument");var s;}async function x(e,t,i,n){return A.has(i)?v(e,t,i):n?async function(e,t){let i=await t(e);return new I(i)}(e,n):void g(`Parser ${i} is not loaded`)}async function v(e,t,i){let n=new(A.get(i))(e,t);return await n.read(),n}const M=e=>h(e).then((e=>e.arrayBuffer())),R=e=>new Promise(((t,i)=>{let n=new FileReader;n.onloadend=()=>t(n.result||new ArrayBuffer),n.onerror=i,n.readAsArrayBuffer(e);}));class L extends Map{get tagKeys(){return this.allKeys||(this.allKeys=Array.from(this.keys())),this.allKeys}get tagValues(){return this.allValues||(this.allValues=Array.from(this.values())),this.allValues}}function U(e,t,i){let n=new L;for(let[e,t]of i)n.set(e,t);if(Array.isArray(t))for(let i of t)e.set(i,n);else e.set(t,n);return n}function F(e,t,i){let n,s=e.get(t);for(n of i)s.set(n[0],n[1]);}const E=new Map,B=new Map,N=new Map,G=["chunked","firstChunkSize","firstChunkSizeNode","firstChunkSizeBrowser","chunkSize","chunkLimit"],V=["jfif","xmp","icc","iptc","ihdr"],z=["tiff",...V],H=["ifd0","ifd1","exif","gps","interop"],j=[...z,...H],W=["makerNote","userComment"],K=["translateKeys","translateValues","reviveValues","multiSegment"],X=[...K,"sanitize","mergeOutput","silentErrors"];class _{get translate(){return this.translateKeys||this.translateValues||this.reviveValues}}class Y extends _{get needed(){return this.enabled||this.deps.size>0}constructor(e,t,i,n){if(super(),c(this,"enabled",!1),c(this,"skip",new Set),c(this,"pick",new Set),c(this,"deps",new Set),c(this,"translateKeys",!1),c(this,"translateValues",!1),c(this,"reviveValues",!1),this.key=e,this.enabled=t,this.parse=this.enabled,this.applyInheritables(n),this.canBeFiltered=H.includes(e),this.canBeFiltered&&(this.dict=E.get(e)),void 0!==i)if(Array.isArray(i))this.parse=this.enabled=!0,this.canBeFiltered&&i.length>0&&this.translateTagSet(i,this.pick);else if("object"==typeof i){if(this.enabled=!0,this.parse=!1!==i.parse,this.canBeFiltered){let{pick:e,skip:t}=i;e&&e.length>0&&this.translateTagSet(e,this.pick),t&&t.length>0&&this.translateTagSet(t,this.skip);}this.applyInheritables(i);}else !0===i||!1===i?this.parse=this.enabled=i:g(`Invalid options argument: ${i}`);}applyInheritables(e){let t,i;for(t of K)i=e[t],void 0!==i&&(this[t]=i);}translateTagSet(e,t){if(this.dict){let i,n,{tagKeys:s,tagValues:r}=this.dict;for(i of e)"string"==typeof i?(n=r.indexOf(i),-1===n&&(n=s.indexOf(Number(i))),-1!==n&&t.add(Number(s[n]))):t.add(i);}else for(let i of e)t.add(i);}finalizeFilters(){!this.enabled&&this.deps.size>0?(this.enabled=!0,ee(this.pick,this.deps)):this.enabled&&this.pick.size>0&&ee(this.pick,this.deps);}}var $={jfif:!1,tiff:!0,xmp:!1,icc:!1,iptc:!1,ifd0:!0,ifd1:!1,exif:!0,gps:!0,interop:!1,ihdr:void 0,makerNote:!1,userComment:!1,multiSegment:!1,skip:[],pick:[],translateKeys:!0,translateValues:!0,reviveValues:!0,sanitize:!0,mergeOutput:!0,silentErrors:!0,chunked:!0,firstChunkSize:void 0,firstChunkSizeNode:512,firstChunkSizeBrowser:65536,chunkSize:65536,chunkLimit:5},J=new Map;class q extends _{static useCached(e){let t=J.get(e);return void 0!==t||(t=new this(e),J.set(e,t)),t}constructor(e){super(),!0===e?this.setupFromTrue():void 0===e?this.setupFromUndefined():Array.isArray(e)?this.setupFromArray(e):"object"==typeof e?this.setupFromObject(e):g(`Invalid options argument ${e}`),void 0===this.firstChunkSize&&(this.firstChunkSize=t?this.firstChunkSizeBrowser:this.firstChunkSizeNode),this.mergeOutput&&(this.ifd1.enabled=!1),this.filterNestedSegmentTags(),this.traverseTiffDependencyTree(),this.checkLoadedPlugins();}setupFromUndefined(){let e;for(e of G)this[e]=$[e];for(e of X)this[e]=$[e];for(e of W)this[e]=$[e];for(e of j)this[e]=new Y(e,$[e],void 0,this);}setupFromTrue(){let e;for(e of G)this[e]=$[e];for(e of X)this[e]=$[e];for(e of W)this[e]=!0;for(e of j)this[e]=new Y(e,!0,void 0,this);}setupFromArray(e){let t;for(t of G)this[t]=$[t];for(t of X)this[t]=$[t];for(t of W)this[t]=$[t];for(t of j)this[t]=new Y(t,!1,void 0,this);this.setupGlobalFilters(e,void 0,H);}setupFromObject(e){let t;for(t of(H.ifd0=H.ifd0||H.image,H.ifd1=H.ifd1||H.thumbnail,Object.assign(this,e),G))this[t]=Z(e[t],$[t]);for(t of X)this[t]=Z(e[t],$[t]);for(t of W)this[t]=Z(e[t],$[t]);for(t of z)this[t]=new Y(t,$[t],e[t],this);for(t of H)this[t]=new Y(t,$[t],e[t],this.tiff);this.setupGlobalFilters(e.pick,e.skip,H,j),!0===e.tiff?this.batchEnableWithBool(H,!0):!1===e.tiff?this.batchEnableWithUserValue(H,e):Array.isArray(e.tiff)?this.setupGlobalFilters(e.tiff,void 0,H):"object"==typeof e.tiff&&this.setupGlobalFilters(e.tiff.pick,e.tiff.skip,H);}batchEnableWithBool(e,t){for(let i of e)this[i].enabled=t;}batchEnableWithUserValue(e,t){for(let i of e){let e=t[i];this[i].enabled=!1!==e&&void 0!==e;}}setupGlobalFilters(e,t,i,n=i){if(e&&e.length){for(let e of n)this[e].enabled=!1;let t=Q(e,i);for(let[e,i]of t)ee(this[e].pick,i),this[e].enabled=!0;}else if(t&&t.length){let e=Q(t,i);for(let[t,i]of e)ee(this[t].skip,i);}}filterNestedSegmentTags(){let{ifd0:e,exif:t,xmp:i,iptc:n,icc:s}=this;this.makerNote?t.deps.add(37500):t.skip.add(37500),this.userComment?t.deps.add(37510):t.skip.add(37510),i.enabled||e.skip.add(700),n.enabled||e.skip.add(33723),s.enabled||e.skip.add(34675);}traverseTiffDependencyTree(){let{ifd0:e,exif:t,gps:i,interop:n}=this;n.needed&&(t.deps.add(40965),e.deps.add(40965)),t.needed&&e.deps.add(34665),i.needed&&e.deps.add(34853),this.tiff.enabled=H.some((e=>!0===this[e].enabled))||this.makerNote||this.userComment;for(let e of H)this[e].finalizeFilters();}get onlyTiff(){return !V.map((e=>this[e].enabled)).some((e=>!0===e))&&this.tiff.enabled}checkLoadedPlugins(){for(let e of z)this[e].enabled&&!T.has(e)&&P("segment parser",e);}}function Q(e,t){let i,n,s,r,a=[];for(s of t){for(r of(i=E.get(s),n=[],i))(e.includes(r[0])||e.includes(r[1]))&&n.push(r[0]);n.length&&a.push([s,n]);}return a}function Z(e,t){return void 0!==e?e:void 0!==t?t:void 0}function ee(e,t){for(let i of t)e.add(i);}c(q,"default",$);class te{constructor(e){c(this,"parsers",{}),c(this,"output",{}),c(this,"errors",[]),c(this,"pushToErrors",(e=>this.errors.push(e))),this.options=q.useCached(e);}async read(e){this.file=await D(e,this.options);}setup(){if(this.fileParser)return;let{file:e}=this,t=e.getUint16(0);for(let[i,n]of w)if(n.canHandle(e,t))return this.fileParser=new n(this.options,this.file,this.parsers),e[i]=!0;this.file.close&&this.file.close(),g("Unknown file format");}async parse(){let{output:e,errors:t}=this;return this.setup(),this.options.silentErrors?(await this.executeParsers().catch(this.pushToErrors),t.push(...this.fileParser.errors)):await this.executeParsers(),this.file.close&&this.file.close(),this.options.silentErrors&&t.length>0&&(e.errors=t),f(e)}async executeParsers(){let{output:e}=this;await this.fileParser.parse();let t=Object.values(this.parsers).map((async t=>{let i=await t.parse();t.assignToOutput(e,i);}));this.options.silentErrors&&(t=t.map((e=>e.catch(this.pushToErrors)))),await Promise.all(t);}async extractThumbnail(){this.setup();let{options:e,file:t}=this,i=T.get("tiff",e);var n;if(t.tiff?n={start:0,type:"tiff"}:t.jpeg&&(n=await this.fileParser.getOrFindSegment("tiff")),void 0===n)return;let s=await this.fileParser.ensureSegmentChunk(n),r=this.parsers.tiff=new i(s,e,t),a=await r.extractThumbnail();return t.close&&t.close(),a}}async function ie(e,t){let i=new te(t);return await i.read(e),i.parse()}var ne=Object.freeze({__proto__:null,parse:ie,Exifr:te,fileParsers:w,segmentParsers:T,fileReaders:A,tagKeys:E,tagValues:B,tagRevivers:N,createDictionary:U,extendDictionary:F,fetchUrlAsArrayBuffer:M,readBlobAsArrayBuffer:R,chunkedProps:G,otherSegments:V,segments:z,tiffBlocks:H,segmentsAndBlocks:j,tiffExtractables:W,inheritables:K,allFormatters:X,Options:q});class se{constructor(e,t,i){c(this,"errors",[]),c(this,"ensureSegmentChunk",(async e=>{let t=e.start,i=e.size||65536;if(this.file.chunked)if(this.file.available(t,i))e.chunk=this.file.subarray(t,i);else try{e.chunk=await this.file.readChunk(t,i);}catch(t){g(`Couldn't read segment: ${JSON.stringify(e)}. ${t.message}`);}else this.file.byteLength>t+i?e.chunk=this.file.subarray(t,i):void 0===e.size?e.chunk=this.file.subarray(t):g("Segment unreachable: "+JSON.stringify(e));return e.chunk})),this.extendOptions&&this.extendOptions(e),this.options=e,this.file=t,this.parsers=i;}injectSegment(e,t){this.options[e].enabled&&this.createParser(e,t);}createParser(e,t){let i=new(T.get(e))(t,this.options,this.file);return this.parsers[e]=i}createParsers(e){for(let t of e){let{type:e,chunk:i}=t,n=this.options[e];if(n&&n.enabled){let t=this.parsers[e];t&&t.append||t||this.createParser(e,i);}}}async readSegments(e){let t=e.map(this.ensureSegmentChunk);await Promise.all(t);}}class re{static findPosition(e,t){let i=e.getUint16(t+2)+2,n="function"==typeof this.headerLength?this.headerLength(e,t,i):this.headerLength,s=t+n,r=i-n;return {offset:t,length:i,headerLength:n,start:s,size:r,end:s+r}}static parse(e,t={}){return new this(e,new q({[this.type]:t}),e).parse()}normalizeInput(e){return e instanceof I?e:new I(e)}constructor(e,t={},i){c(this,"errors",[]),c(this,"raw",new Map),c(this,"handleError",(e=>{if(!this.options.silentErrors)throw e;this.errors.push(e.message);})),this.chunk=this.normalizeInput(e),this.file=i,this.type=this.constructor.type,this.globalOptions=this.options=t,this.localOptions=t[this.type],this.canTranslate=this.localOptions&&this.localOptions.translate;}translate(){this.canTranslate&&(this.translated=this.translateBlock(this.raw,this.type));}get output(){return this.translated?this.translated:this.raw?Object.fromEntries(this.raw):void 0}translateBlock(e,t){let i=N.get(t),n=B.get(t),s=E.get(t),r=this.options[t],a=r.reviveValues&&!!i,o=r.translateValues&&!!n,l=r.translateKeys&&!!s,h={};for(let[t,r]of e)a&&i.has(t)?r=i.get(t)(r):o&&n.has(t)&&(r=this.translateValue(r,n.get(t))),l&&s.has(t)&&(t=s.get(t)||t),h[t]=r;return h}translateValue(e,t){return t[e]||t.DEFAULT||e}assignToOutput(e,t){this.assignObjectToOutput(e,this.constructor.type,t);}assignObjectToOutput(e,t,i){if(this.globalOptions.mergeOutput)return Object.assign(e,i);e[t]?Object.assign(e[t],i):e[t]=i;}}c(re,"headerLength",4),c(re,"type",void 0),c(re,"multiSegment",!1),c(re,"canHandle",(()=>!1));function ae(e){return 192===e||194===e||196===e||219===e||221===e||218===e||254===e}function oe(e){return e>=224&&e<=239}function le(e,t,i){for(let[n,s]of T)if(s.canHandle(e,t,i))return n}class he extends se{constructor(...e){super(...e),c(this,"appSegments",[]),c(this,"jpegSegments",[]),c(this,"unknownSegments",[]);}static canHandle(e,t){return 65496===t}async parse(){await this.findAppSegments(),await this.readSegments(this.appSegments),this.mergeMultiSegments(),this.createParsers(this.mergedAppSegments||this.appSegments);}setupSegmentFinderArgs(e){!0===e?(this.findAll=!0,this.wanted=new Set(T.keyList())):(e=void 0===e?T.keyList().filter((e=>this.options[e].enabled)):e.filter((e=>this.options[e].enabled&&T.has(e))),this.findAll=!1,this.remaining=new Set(e),this.wanted=new Set(e)),this.unfinishedMultiSegment=!1;}async findAppSegments(e=0,t){this.setupSegmentFinderArgs(t);let{file:i,findAll:n,wanted:s,remaining:r}=this;if(!n&&this.file.chunked&&(n=Array.from(s).some((e=>{let t=T.get(e),i=this.options[e];return t.multiSegment&&i.multiSegment})),n&&await this.file.readWhole()),e=this.findAppSegmentsInRange(e,i.byteLength),!this.options.onlyTiff&&i.chunked){let t=!1;for(;r.size>0&&!t&&(i.canReadNextChunk||this.unfinishedMultiSegment);){let{nextChunkOffset:n}=i,s=this.appSegments.some((e=>!this.file.available(e.offset||e.start,e.length||e.size)));if(t=e>n&&!s?!await i.readNextChunk(e):!await i.readNextChunk(n),void 0===(e=this.findAppSegmentsInRange(e,i.byteLength)))return}}}findAppSegmentsInRange(e,t){t-=2;let i,n,s,r,a,o,{file:l,findAll:h,wanted:u,remaining:c,options:f}=this;for(;e<t;e++)if(255===l.getUint8(e))if(i=l.getUint8(e+1),oe(i)){if(n=l.getUint16(e+2),s=le(l,e,n),s&&u.has(s)&&(r=T.get(s),a=r.findPosition(l,e),o=f[s],a.type=s,this.appSegments.push(a),!h&&(r.multiSegment&&o.multiSegment?(this.unfinishedMultiSegment=a.chunkNumber<a.chunkCount,this.unfinishedMultiSegment||c.delete(s)):c.delete(s),0===c.size)))break;f.recordUnknownSegments&&(a=re.findPosition(l,e),a.marker=i,this.unknownSegments.push(a)),e+=n+1;}else if(ae(i)){if(n=l.getUint16(e+2),218===i&&!1!==f.stopAfterSos)return;f.recordJpegSegments&&this.jpegSegments.push({offset:e,length:n,marker:i}),e+=n+1;}return e}mergeMultiSegments(){if(!this.appSegments.some((e=>e.multiSegment)))return;let e=function(e,t){let i,n,s,r=new Map;for(let a=0;a<e.length;a++)i=e[a],n=i[t],r.has(n)?s=r.get(n):r.set(n,s=[]),s.push(i);return Array.from(r)}(this.appSegments,"type");this.mergedAppSegments=e.map((([e,t])=>{let i=T.get(e,this.options);if(i.handleMultiSegments){return {type:e,chunk:i.handleMultiSegments(t)}}return t[0]}));}getSegment(e){return this.appSegments.find((t=>t.type===e))}async getOrFindSegment(e){let t=this.getSegment(e);return void 0===t&&(await this.findAppSegments(0,[e]),t=this.getSegment(e)),t}}c(he,"type","jpeg"),w.set("jpeg",he);const ue=[void 0,1,1,2,4,8,1,1,2,4,8,4,8,4];class ce extends re{parseHeader(){var e=this.chunk.getUint16();18761===e?this.le=!0:19789===e&&(this.le=!1),this.chunk.le=this.le,this.headerParsed=!0;}parseTags(e,t,i=new Map){let{pick:n,skip:s}=this.options[t];n=new Set(n);let r=n.size>0,a=0===s.size,o=this.chunk.getUint16(e);e+=2;for(let l=0;l<o;l++){let o=this.chunk.getUint16(e);if(r){if(n.has(o)&&(i.set(o,this.parseTag(e,o,t)),n.delete(o),0===n.size))break}else !a&&s.has(o)||i.set(o,this.parseTag(e,o,t));e+=12;}return i}parseTag(e,t,i){let{chunk:n}=this,s=n.getUint16(e+2),r=n.getUint32(e+4),a=ue[s];if(a*r<=4?e+=8:e=n.getUint32(e+8),(s<1||s>13)&&g(`Invalid TIFF value type. block: ${i.toUpperCase()}, tag: ${t.toString(16)}, type: ${s}, offset ${e}`),e>n.byteLength&&g(`Invalid TIFF value offset. block: ${i.toUpperCase()}, tag: ${t.toString(16)}, type: ${s}, offset ${e} is outside of chunk size ${n.byteLength}`),1===s)return n.getUint8Array(e,r);if(2===s)return m(n.getString(e,r));if(7===s)return n.getUint8Array(e,r);if(1===r)return this.parseTagValue(s,e);{let t=new(function(e){switch(e){case 1:return Uint8Array;case 3:return Uint16Array;case 4:return Uint32Array;case 5:return Array;case 6:return Int8Array;case 8:return Int16Array;case 9:return Int32Array;case 10:return Array;case 11:return Float32Array;case 12:return Float64Array;default:return Array}}(s))(r),i=a;for(let n=0;n<r;n++)t[n]=this.parseTagValue(s,e),e+=i;return t}}parseTagValue(e,t){let{chunk:i}=this;switch(e){case 1:return i.getUint8(t);case 3:return i.getUint16(t);case 4:return i.getUint32(t);case 5:return i.getUint32(t)/i.getUint32(t+4);case 6:return i.getInt8(t);case 8:return i.getInt16(t);case 9:return i.getInt32(t);case 10:return i.getInt32(t)/i.getInt32(t+4);case 11:return i.getFloat(t);case 12:return i.getDouble(t);case 13:return i.getUint32(t);default:g(`Invalid tiff type ${e}`);}}}class fe extends ce{static canHandle(e,t){return 225===e.getUint8(t+1)&&1165519206===e.getUint32(t+4)&&0===e.getUint16(t+8)}async parse(){this.parseHeader();let{options:e}=this;return e.ifd0.enabled&&await this.parseIfd0Block(),e.exif.enabled&&await this.safeParse("parseExifBlock"),e.gps.enabled&&await this.safeParse("parseGpsBlock"),e.interop.enabled&&await this.safeParse("parseInteropBlock"),e.ifd1.enabled&&await this.safeParse("parseThumbnailBlock"),this.createOutput()}safeParse(e){let t=this[e]();return void 0!==t.catch&&(t=t.catch(this.handleError)),t}findIfd0Offset(){void 0===this.ifd0Offset&&(this.ifd0Offset=this.chunk.getUint32(4));}findIfd1Offset(){if(void 0===this.ifd1Offset){this.findIfd0Offset();let e=this.chunk.getUint16(this.ifd0Offset),t=this.ifd0Offset+2+12*e;this.ifd1Offset=this.chunk.getUint32(t);}}parseBlock(e,t){let i=new Map;return this[t]=i,this.parseTags(e,t,i),i}async parseIfd0Block(){if(this.ifd0)return;let{file:e}=this;this.findIfd0Offset(),this.ifd0Offset<8&&g("Malformed EXIF data"),!e.chunked&&this.ifd0Offset>e.byteLength&&g(`IFD0 offset points to outside of file.\nthis.ifd0Offset: ${this.ifd0Offset}, file.byteLength: ${e.byteLength}`),e.tiff&&await e.ensureChunk(this.ifd0Offset,S(this.options));let t=this.parseBlock(this.ifd0Offset,"ifd0");return 0!==t.size?(this.exifOffset=t.get(34665),this.interopOffset=t.get(40965),this.gpsOffset=t.get(34853),this.xmp=t.get(700),this.iptc=t.get(33723),this.icc=t.get(34675),this.options.sanitize&&(t.delete(34665),t.delete(40965),t.delete(34853),t.delete(700),t.delete(33723),t.delete(34675)),t):void 0}async parseExifBlock(){if(this.exif)return;if(this.ifd0||await this.parseIfd0Block(),void 0===this.exifOffset)return;this.file.tiff&&await this.file.ensureChunk(this.exifOffset,S(this.options));let e=this.parseBlock(this.exifOffset,"exif");return this.interopOffset||(this.interopOffset=e.get(40965)),this.makerNote=e.get(37500),this.userComment=e.get(37510),this.options.sanitize&&(e.delete(40965),e.delete(37500),e.delete(37510)),this.unpack(e,41728),this.unpack(e,41729),e}unpack(e,t){let i=e.get(t);i&&1===i.length&&e.set(t,i[0]);}async parseGpsBlock(){if(this.gps)return;if(this.ifd0||await this.parseIfd0Block(),void 0===this.gpsOffset)return;let e=this.parseBlock(this.gpsOffset,"gps");return e&&e.has(2)&&e.has(4)&&(e.set("latitude",de(...e.get(2),e.get(1))),e.set("longitude",de(...e.get(4),e.get(3)))),e}async parseInteropBlock(){if(!this.interop&&(this.ifd0||await this.parseIfd0Block(),void 0!==this.interopOffset||this.exif||await this.parseExifBlock(),void 0!==this.interopOffset))return this.parseBlock(this.interopOffset,"interop")}async parseThumbnailBlock(e=!1){if(!this.ifd1&&!this.ifd1Parsed&&(!this.options.mergeOutput||e))return this.findIfd1Offset(),this.ifd1Offset>0&&(this.parseBlock(this.ifd1Offset,"ifd1"),this.ifd1Parsed=!0),this.ifd1}async extractThumbnail(){if(this.headerParsed||this.parseHeader(),this.ifd1Parsed||await this.parseThumbnailBlock(!0),void 0===this.ifd1)return;let e=this.ifd1.get(513),t=this.ifd1.get(514);return this.chunk.getUint8Array(e,t)}get image(){return this.ifd0}get thumbnail(){return this.ifd1}createOutput(){let e,t,i,n={};for(t of H)if(e=this[t],!p(e))if(i=this.canTranslate?this.translateBlock(e,t):Object.fromEntries(e),this.options.mergeOutput){if("ifd1"===t)continue;Object.assign(n,i);}else n[t]=i;return this.makerNote&&(n.makerNote=this.makerNote),this.userComment&&(n.userComment=this.userComment),n}assignToOutput(e,t){if(this.globalOptions.mergeOutput)Object.assign(e,t);else for(let[i,n]of Object.entries(t))this.assignObjectToOutput(e,i,n);}}function de(e,t,i,n){var s=e+t/60+i/3600;return "S"!==n&&"W"!==n||(s*=-1),s}c(fe,"type","tiff"),c(fe,"headerLength",10),T.set("tiff",fe);var pe=Object.freeze({__proto__:null,default:ne,Exifr:te,fileParsers:w,segmentParsers:T,fileReaders:A,tagKeys:E,tagValues:B,tagRevivers:N,createDictionary:U,extendDictionary:F,fetchUrlAsArrayBuffer:M,readBlobAsArrayBuffer:R,chunkedProps:G,otherSegments:V,segments:z,tiffBlocks:H,segmentsAndBlocks:j,tiffExtractables:W,inheritables:K,allFormatters:X,Options:q,parse:ie});const ge={ifd0:!1,ifd1:!1,exif:!1,gps:!1,interop:!1,sanitize:!1,reviveValues:!0,translateKeys:!1,translateValues:!1,mergeOutput:!1},me=Object.assign({},ge,{firstChunkSize:4e4,gps:[1,2,3,4]});async function Se(e){let t=new te(me);await t.read(e);let i=await t.parse();if(i&&i.gps){let{latitude:e,longitude:t}=i.gps;return {latitude:e,longitude:t}}}const Ce=Object.assign({},ge,{tiff:!1,ifd1:!0,mergeOutput:!1});async function ye(e){let t=new te(Ce);await t.read(e);let i=await t.extractThumbnail();return i&&a?s.from(i):i}async function be(e){let t=await this.thumbnail(e);if(void 0!==t){let e=new Blob([t]);return URL.createObjectURL(e)}}const Ie=Object.assign({},ge,{firstChunkSize:4e4,ifd0:[274]});async function Pe(e){let t=new te(Ie);await t.read(e);let i=await t.parse();if(i&&i.ifd0)return i.ifd0[274]}const ke=Object.freeze({1:{dimensionSwapped:!1,scaleX:1,scaleY:1,deg:0,rad:0},2:{dimensionSwapped:!1,scaleX:-1,scaleY:1,deg:0,rad:0},3:{dimensionSwapped:!1,scaleX:1,scaleY:1,deg:180,rad:180*Math.PI/180},4:{dimensionSwapped:!1,scaleX:-1,scaleY:1,deg:180,rad:180*Math.PI/180},5:{dimensionSwapped:!0,scaleX:1,scaleY:-1,deg:90,rad:90*Math.PI/180},6:{dimensionSwapped:!0,scaleX:1,scaleY:1,deg:90,rad:90*Math.PI/180},7:{dimensionSwapped:!0,scaleX:1,scaleY:-1,deg:270,rad:270*Math.PI/180},8:{dimensionSwapped:!0,scaleX:1,scaleY:1,deg:270,rad:270*Math.PI/180}});let we=!0,Te=!0;if("object"==typeof navigator){let e=navigator.userAgent;if(e.includes("iPad")||e.includes("iPhone")){let t=e.match(/OS (\d+)_(\d+)/);if(t){let[,e,i]=t,n=Number(e)+.1*Number(i);we=n<13.4,Te=!1;}}else if(e.includes("OS X 10")){let[,t]=e.match(/OS X 10[_.](\d+)/);we=Te=Number(t)<15;}if(e.includes("Chrome/")){let[,t]=e.match(/Chrome\/(\d+)/);we=Te=Number(t)<81;}else if(e.includes("Firefox/")){let[,t]=e.match(/Firefox\/(\d+)/);we=Te=Number(t)<77;}}async function Ae(e){let t=await Pe(e);return Object.assign({canvas:we,css:Te},ke[t])}class De extends I{constructor(...e){super(...e),c(this,"ranges",new Oe),0!==this.byteLength&&this.ranges.add(0,this.byteLength);}_tryExtend(e,t,i){if(0===e&&0===this.byteLength&&i){let e=new DataView(i.buffer||i,i.byteOffset,i.byteLength);this._swapDataView(e);}else {let i=e+t;if(i>this.byteLength){let{dataView:e}=this._extend(i);this._swapDataView(e);}}}_extend(e){let t;t=a?s.allocUnsafe(e):new Uint8Array(e);let i=new DataView(t.buffer,t.byteOffset,t.byteLength);return t.set(new Uint8Array(this.buffer,this.byteOffset,this.byteLength),0),{uintView:t,dataView:i}}subarray(e,t,i=!1){return t=t||this._lengthToEnd(e),i&&this._tryExtend(e,t),this.ranges.add(e,t),super.subarray(e,t)}set(e,t,i=!1){i&&this._tryExtend(t,e.byteLength,e);let n=super.set(e,t);return this.ranges.add(t,n.byteLength),n}async ensureChunk(e,t){this.chunked&&(this.ranges.available(e,t)||await this.readChunk(e,t));}available(e,t){return this.ranges.available(e,t)}}class Oe{constructor(){c(this,"list",[]);}get length(){return this.list.length}add(e,t,i=0){let n=e+t,s=this.list.filter((t=>xe(e,t.offset,n)||xe(e,t.end,n)));if(s.length>0){e=Math.min(e,...s.map((e=>e.offset))),n=Math.max(n,...s.map((e=>e.end))),t=n-e;let i=s.shift();i.offset=e,i.length=t,i.end=n,this.list=this.list.filter((e=>!s.includes(e)));}else this.list.push({offset:e,length:t,end:n});}available(e,t){let i=e+t;return this.list.some((t=>t.offset<=e&&i<=t.end))}}function xe(e,t,i){return e<=t&&t<=i}class ve extends De{constructor(e,t){super(0),c(this,"chunksRead",0),this.input=e,this.options=t;}async readWhole(){this.chunked=!1,await this.readChunk(this.nextChunkOffset);}async readChunked(){this.chunked=!0,await this.readChunk(0,this.options.firstChunkSize);}async readNextChunk(e=this.nextChunkOffset){if(this.fullyRead)return this.chunksRead++,!1;let t=this.options.chunkSize,i=await this.readChunk(e,t);return !!i&&i.byteLength===t}async readChunk(e,t){if(this.chunksRead++,0!==(t=this.safeWrapAddress(e,t)))return this._readChunk(e,t)}safeWrapAddress(e,t){return void 0!==this.size&&e+t>this.size?Math.max(0,this.size-e):t}get nextChunkOffset(){if(0!==this.ranges.list.length)return this.ranges.list[0].length}get canReadNextChunk(){return this.chunksRead<this.options.chunkLimit}get fullyRead(){return void 0!==this.size&&this.nextChunkOffset===this.size}read(){return this.options.chunked?this.readChunked():this.readWhole()}close(){}}A.set("blob",class extends ve{async readWhole(){this.chunked=!1;let e=await R(this.input);this._swapArrayBuffer(e);}readChunked(){return this.chunked=!0,this.size=this.input.size,super.readChunked()}async _readChunk(e,t){let i=t?e+t:void 0,n=this.input.slice(e,i),s=await R(n);return this.set(s,e,!0)}});var Me=Object.freeze({__proto__:null,default:pe,Exifr:te,fileParsers:w,segmentParsers:T,fileReaders:A,tagKeys:E,tagValues:B,tagRevivers:N,createDictionary:U,extendDictionary:F,fetchUrlAsArrayBuffer:M,readBlobAsArrayBuffer:R,chunkedProps:G,otherSegments:V,segments:z,tiffBlocks:H,segmentsAndBlocks:j,tiffExtractables:W,inheritables:K,allFormatters:X,Options:q,parse:ie,gpsOnlyOptions:me,gps:Se,thumbnailOnlyOptions:Ce,thumbnail:ye,thumbnailUrl:be,orientationOnlyOptions:Ie,orientation:Pe,rotations:ke,get rotateCanvas(){return we},get rotateCss(){return Te},rotation:Ae});A.set("url",class extends ve{async readWhole(){this.chunked=!1;let e=await M(this.input);e instanceof ArrayBuffer?this._swapArrayBuffer(e):e instanceof Uint8Array&&this._swapBuffer(e);}async _readChunk(e,t){let i=t?e+t-1:void 0,n=this.options.httpHeaders||{};(e||i)&&(n.range=`bytes=${[e,i].join("-")}`);let s=await h(this.input,{headers:n}),r=await s.arrayBuffer(),a=r.byteLength;if(416!==s.status)return a!==t&&(this.size=e+a),this.set(r,e,!0)}});I.prototype.getUint64=function(e){let t=this.getUint32(e),i=this.getUint32(e+4);return t<1048575?t<<32|i:void 0!==typeof r?(console.warn("Using BigInt because of type 64uint but JS can only handle 53b numbers."),r(t)<<r(32)|r(i)):void g("Trying to read 64b value but JS can only handle 53b numbers.")};class Re extends se{parseBoxes(e=0){let t=[];for(;e<this.file.byteLength-4;){let i=this.parseBoxHead(e);if(t.push(i),0===i.length)break;e+=i.length;}return t}parseSubBoxes(e){e.boxes=this.parseBoxes(e.start);}findBox(e,t){return void 0===e.boxes&&this.parseSubBoxes(e),e.boxes.find((e=>e.kind===t))}parseBoxHead(e){let t=this.file.getUint32(e),i=this.file.getString(e+4,4),n=e+8;return 1===t&&(t=this.file.getUint64(e+8),n+=8),{offset:e,length:t,kind:i,start:n}}parseBoxFullHead(e){if(void 0!==e.version)return;let t=this.file.getUint32(e.start);e.version=t>>24,e.start+=4;}}class Le extends Re{static canHandle(e,t){if(0!==t)return !1;let i=e.getUint16(2);if(i>50)return !1;let n=16,s=[];for(;n<i;)s.push(e.getString(n,4)),n+=4;return s.includes(this.type)}async parse(){let e=this.file.getUint32(0),t=this.parseBoxHead(e);for(;"meta"!==t.kind;)e+=t.length,await this.file.ensureChunk(e,16),t=this.parseBoxHead(e);await this.file.ensureChunk(t.offset,t.length),this.parseBoxFullHead(t),this.parseSubBoxes(t),this.options.icc.enabled&&await this.findIcc(t),this.options.tiff.enabled&&await this.findExif(t);}async registerSegment(e,t,i){await this.file.ensureChunk(t,i);let n=this.file.subarray(t,i);this.createParser(e,n);}async findIcc(e){let t=this.findBox(e,"iprp");if(void 0===t)return;let i=this.findBox(t,"ipco");if(void 0===i)return;let n=this.findBox(i,"colr");void 0!==n&&await this.registerSegment("icc",n.offset+12,n.length);}async findExif(e){let t=this.findBox(e,"iinf");if(void 0===t)return;let i=this.findBox(e,"iloc");if(void 0===i)return;let n=this.findExifLocIdInIinf(t),s=this.findExtentInIloc(i,n);if(void 0===s)return;let[r,a]=s;await this.file.ensureChunk(r,a);let o=4+this.file.getUint32(r);r+=o,a-=o,await this.registerSegment("tiff",r,a);}findExifLocIdInIinf(e){this.parseBoxFullHead(e);let t,i,n,s,r=e.start,a=this.file.getUint16(r);for(r+=2;a--;){if(t=this.parseBoxHead(r),this.parseBoxFullHead(t),i=t.start,t.version>=2&&(n=3===t.version?4:2,s=this.file.getString(i+n+2,4),"Exif"===s))return this.file.getUintBytes(i,n);r+=t.length;}}get8bits(e){let t=this.file.getUint8(e);return [t>>4,15&t]}findExtentInIloc(e,t){this.parseBoxFullHead(e);let i=e.start,[n,s]=this.get8bits(i++),[r,a]=this.get8bits(i++),o=2===e.version?4:2,l=1===e.version||2===e.version?2:0,h=a+n+s,u=2===e.version?4:2,c=this.file.getUintBytes(i,u);for(i+=u;c--;){let e=this.file.getUintBytes(i,o);i+=o+l+2+r;let u=this.file.getUint16(i);if(i+=2,e===t)return u>1&&console.warn("ILOC box has more than one extent but we're only processing one\nPlease create an issue at https://github.com/MikeKovarik/exifr with this file"),[this.file.getUintBytes(i+a,n),this.file.getUintBytes(i+a+n,s)];i+=u*h;}}}class Ue extends Le{}c(Ue,"type","heic");class Fe extends Le{}c(Fe,"type","avif"),w.set("heic",Ue),w.set("avif",Fe),U(E,["ifd0","ifd1"],[[256,"ImageWidth"],[257,"ImageHeight"],[258,"BitsPerSample"],[259,"Compression"],[262,"PhotometricInterpretation"],[270,"ImageDescription"],[271,"Make"],[272,"Model"],[273,"StripOffsets"],[274,"Orientation"],[277,"SamplesPerPixel"],[278,"RowsPerStrip"],[279,"StripByteCounts"],[282,"XResolution"],[283,"YResolution"],[284,"PlanarConfiguration"],[296,"ResolutionUnit"],[301,"TransferFunction"],[305,"Software"],[306,"ModifyDate"],[315,"Artist"],[316,"HostComputer"],[317,"Predictor"],[318,"WhitePoint"],[319,"PrimaryChromaticities"],[513,"ThumbnailOffset"],[514,"ThumbnailLength"],[529,"YCbCrCoefficients"],[530,"YCbCrSubSampling"],[531,"YCbCrPositioning"],[532,"ReferenceBlackWhite"],[700,"ApplicationNotes"],[33432,"Copyright"],[33723,"IPTC"],[34665,"ExifIFD"],[34675,"ICC"],[34853,"GpsIFD"],[330,"SubIFD"],[40965,"InteropIFD"],[40091,"XPTitle"],[40092,"XPComment"],[40093,"XPAuthor"],[40094,"XPKeywords"],[40095,"XPSubject"]]),U(E,"exif",[[33434,"ExposureTime"],[33437,"FNumber"],[34850,"ExposureProgram"],[34852,"SpectralSensitivity"],[34855,"ISO"],[34858,"TimeZoneOffset"],[34859,"SelfTimerMode"],[34864,"SensitivityType"],[34865,"StandardOutputSensitivity"],[34866,"RecommendedExposureIndex"],[34867,"ISOSpeed"],[34868,"ISOSpeedLatitudeyyy"],[34869,"ISOSpeedLatitudezzz"],[36864,"ExifVersion"],[36867,"DateTimeOriginal"],[36868,"CreateDate"],[36873,"GooglePlusUploadCode"],[36880,"OffsetTime"],[36881,"OffsetTimeOriginal"],[36882,"OffsetTimeDigitized"],[37121,"ComponentsConfiguration"],[37122,"CompressedBitsPerPixel"],[37377,"ShutterSpeedValue"],[37378,"ApertureValue"],[37379,"BrightnessValue"],[37380,"ExposureCompensation"],[37381,"MaxApertureValue"],[37382,"SubjectDistance"],[37383,"MeteringMode"],[37384,"LightSource"],[37385,"Flash"],[37386,"FocalLength"],[37393,"ImageNumber"],[37394,"SecurityClassification"],[37395,"ImageHistory"],[37396,"SubjectArea"],[37500,"MakerNote"],[37510,"UserComment"],[37520,"SubSecTime"],[37521,"SubSecTimeOriginal"],[37522,"SubSecTimeDigitized"],[37888,"AmbientTemperature"],[37889,"Humidity"],[37890,"Pressure"],[37891,"WaterDepth"],[37892,"Acceleration"],[37893,"CameraElevationAngle"],[40960,"FlashpixVersion"],[40961,"ColorSpace"],[40962,"ExifImageWidth"],[40963,"ExifImageHeight"],[40964,"RelatedSoundFile"],[41483,"FlashEnergy"],[41486,"FocalPlaneXResolution"],[41487,"FocalPlaneYResolution"],[41488,"FocalPlaneResolutionUnit"],[41492,"SubjectLocation"],[41493,"ExposureIndex"],[41495,"SensingMethod"],[41728,"FileSource"],[41729,"SceneType"],[41730,"CFAPattern"],[41985,"CustomRendered"],[41986,"ExposureMode"],[41987,"WhiteBalance"],[41988,"DigitalZoomRatio"],[41989,"FocalLengthIn35mmFormat"],[41990,"SceneCaptureType"],[41991,"GainControl"],[41992,"Contrast"],[41993,"Saturation"],[41994,"Sharpness"],[41996,"SubjectDistanceRange"],[42016,"ImageUniqueID"],[42032,"OwnerName"],[42033,"SerialNumber"],[42034,"LensInfo"],[42035,"LensMake"],[42036,"LensModel"],[42037,"LensSerialNumber"],[42080,"CompositeImage"],[42081,"CompositeImageCount"],[42082,"CompositeImageExposureTimes"],[42240,"Gamma"],[59932,"Padding"],[59933,"OffsetSchema"],[65e3,"OwnerName"],[65001,"SerialNumber"],[65002,"Lens"],[65100,"RawFile"],[65101,"Converter"],[65102,"WhiteBalance"],[65105,"Exposure"],[65106,"Shadows"],[65107,"Brightness"],[65108,"Contrast"],[65109,"Saturation"],[65110,"Sharpness"],[65111,"Smoothness"],[65112,"MoireFilter"],[40965,"InteropIFD"]]),U(E,"gps",[[0,"GPSVersionID"],[1,"GPSLatitudeRef"],[2,"GPSLatitude"],[3,"GPSLongitudeRef"],[4,"GPSLongitude"],[5,"GPSAltitudeRef"],[6,"GPSAltitude"],[7,"GPSTimeStamp"],[8,"GPSSatellites"],[9,"GPSStatus"],[10,"GPSMeasureMode"],[11,"GPSDOP"],[12,"GPSSpeedRef"],[13,"GPSSpeed"],[14,"GPSTrackRef"],[15,"GPSTrack"],[16,"GPSImgDirectionRef"],[17,"GPSImgDirection"],[18,"GPSMapDatum"],[19,"GPSDestLatitudeRef"],[20,"GPSDestLatitude"],[21,"GPSDestLongitudeRef"],[22,"GPSDestLongitude"],[23,"GPSDestBearingRef"],[24,"GPSDestBearing"],[25,"GPSDestDistanceRef"],[26,"GPSDestDistance"],[27,"GPSProcessingMethod"],[28,"GPSAreaInformation"],[29,"GPSDateStamp"],[30,"GPSDifferential"],[31,"GPSHPositioningError"]]),U(B,["ifd0","ifd1"],[[274,{1:"Horizontal (normal)",2:"Mirror horizontal",3:"Rotate 180",4:"Mirror vertical",5:"Mirror horizontal and rotate 270 CW",6:"Rotate 90 CW",7:"Mirror horizontal and rotate 90 CW",8:"Rotate 270 CW"}],[296,{1:"None",2:"inches",3:"cm"}]]);let Ee=U(B,"exif",[[34850,{0:"Not defined",1:"Manual",2:"Normal program",3:"Aperture priority",4:"Shutter priority",5:"Creative program",6:"Action program",7:"Portrait mode",8:"Landscape mode"}],[37121,{0:"-",1:"Y",2:"Cb",3:"Cr",4:"R",5:"G",6:"B"}],[37383,{0:"Unknown",1:"Average",2:"CenterWeightedAverage",3:"Spot",4:"MultiSpot",5:"Pattern",6:"Partial",255:"Other"}],[37384,{0:"Unknown",1:"Daylight",2:"Fluorescent",3:"Tungsten (incandescent light)",4:"Flash",9:"Fine weather",10:"Cloudy weather",11:"Shade",12:"Daylight fluorescent (D 5700 - 7100K)",13:"Day white fluorescent (N 4600 - 5400K)",14:"Cool white fluorescent (W 3900 - 4500K)",15:"White fluorescent (WW 3200 - 3700K)",17:"Standard light A",18:"Standard light B",19:"Standard light C",20:"D55",21:"D65",22:"D75",23:"D50",24:"ISO studio tungsten",255:"Other"}],[37385,{0:"Flash did not fire",1:"Flash fired",5:"Strobe return light not detected",7:"Strobe return light detected",9:"Flash fired, compulsory flash mode",13:"Flash fired, compulsory flash mode, return light not detected",15:"Flash fired, compulsory flash mode, return light detected",16:"Flash did not fire, compulsory flash mode",24:"Flash did not fire, auto mode",25:"Flash fired, auto mode",29:"Flash fired, auto mode, return light not detected",31:"Flash fired, auto mode, return light detected",32:"No flash function",65:"Flash fired, red-eye reduction mode",69:"Flash fired, red-eye reduction mode, return light not detected",71:"Flash fired, red-eye reduction mode, return light detected",73:"Flash fired, compulsory flash mode, red-eye reduction mode",77:"Flash fired, compulsory flash mode, red-eye reduction mode, return light not detected",79:"Flash fired, compulsory flash mode, red-eye reduction mode, return light detected",89:"Flash fired, auto mode, red-eye reduction mode",93:"Flash fired, auto mode, return light not detected, red-eye reduction mode",95:"Flash fired, auto mode, return light detected, red-eye reduction mode"}],[41495,{1:"Not defined",2:"One-chip color area sensor",3:"Two-chip color area sensor",4:"Three-chip color area sensor",5:"Color sequential area sensor",7:"Trilinear sensor",8:"Color sequential linear sensor"}],[41728,{1:"Film Scanner",2:"Reflection Print Scanner",3:"Digital Camera"}],[41729,{1:"Directly photographed"}],[41985,{0:"Normal",1:"Custom",2:"HDR (no original saved)",3:"HDR (original saved)",4:"Original (for HDR)",6:"Panorama",7:"Portrait HDR",8:"Portrait"}],[41986,{0:"Auto",1:"Manual",2:"Auto bracket"}],[41987,{0:"Auto",1:"Manual"}],[41990,{0:"Standard",1:"Landscape",2:"Portrait",3:"Night",4:"Other"}],[41991,{0:"None",1:"Low gain up",2:"High gain up",3:"Low gain down",4:"High gain down"}],[41996,{0:"Unknown",1:"Macro",2:"Close",3:"Distant"}],[42080,{0:"Unknown",1:"Not a Composite Image",2:"General Composite Image",3:"Composite Image Captured While Shooting"}]]);const Be={1:"No absolute unit of measurement",2:"Inch",3:"Centimeter"};Ee.set(37392,Be),Ee.set(41488,Be);const Ne={0:"Normal",1:"Low",2:"High"};function Ge(e){return "object"==typeof e&&void 0!==e.length?e[0]:e}function Ve(e){let t=Array.from(e).slice(1);return t[1]>15&&(t=t.map((e=>String.fromCharCode(e)))),"0"!==t[2]&&0!==t[2]||t.pop(),t.join(".")}function ze(e){if("string"==typeof e){var[t,i,n,s,r,a]=e.trim().split(/[-: ]/g).map(Number),o=new Date(t,i-1,n);return Number.isNaN(s)||Number.isNaN(r)||Number.isNaN(a)||(o.setHours(s),o.setMinutes(r),o.setSeconds(a)),Number.isNaN(+o)?e:o}}function He(e){if("string"==typeof e)return e;let t=[];if(0===e[1]&&0===e[e.length-1])for(let i=0;i<e.length;i+=2)t.push(je(e[i+1],e[i]));else for(let i=0;i<e.length;i+=2)t.push(je(e[i],e[i+1]));return m(String.fromCodePoint(...t))}function je(e,t){return e<<8|t}Ee.set(41992,Ne),Ee.set(41993,Ne),Ee.set(41994,Ne),U(N,["ifd0","ifd1"],[[50827,function(e){return "string"!=typeof e?b(e):e}],[306,ze],[40091,He],[40092,He],[40093,He],[40094,He],[40095,He]]),U(N,"exif",[[40960,Ve],[36864,Ve],[36867,ze],[36868,ze],[40962,Ge],[40963,Ge]]),U(N,"gps",[[0,e=>Array.from(e).join(".")],[7,e=>Array.from(e).join(":")]]);class We extends re{static canHandle(e,t){return 225===e.getUint8(t+1)&&1752462448===e.getUint32(t+4)&&"http://ns.adobe.com/"===e.getString(t+4,"http://ns.adobe.com/".length)}static headerLength(e,t){return "http://ns.adobe.com/xmp/extension/"===e.getString(t+4,"http://ns.adobe.com/xmp/extension/".length)?79:4+"http://ns.adobe.com/xap/1.0/".length+1}static findPosition(e,t){let i=super.findPosition(e,t);return i.multiSegment=i.extended=79===i.headerLength,i.multiSegment?(i.chunkCount=e.getUint8(t+72),i.chunkNumber=e.getUint8(t+76),0!==e.getUint8(t+77)&&i.chunkNumber++):(i.chunkCount=1/0,i.chunkNumber=-1),i}static handleMultiSegments(e){return e.map((e=>e.chunk.getString())).join("")}normalizeInput(e){return "string"==typeof e?e:I.from(e).getString()}parse(e=this.chunk){if(!this.localOptions.parse)return e;e=function(e){let t={},i={};for(let e of Ze)t[e]=[],i[e]=0;return e.replace(et,((e,n,s)=>{if("<"===n){let n=++i[s];return t[s].push(n),`${e}#${n}`}return `${e}#${t[s].pop()}`}))}(e);let t=Xe.findAll(e,"rdf","Description");0===t.length&&t.push(new Xe("rdf","Description",void 0,e));let i,n={};for(let e of t)for(let t of e.properties)i=Je(t.ns,n),_e(t,i);return function(e){let t;for(let i in e)t=e[i]=f(e[i]),void 0===t&&delete e[i];return f(e)}(n)}assignToOutput(e,t){if(this.localOptions.parse)for(let[i,n]of Object.entries(t))switch(i){case"tiff":this.assignObjectToOutput(e,"ifd0",n);break;case"exif":this.assignObjectToOutput(e,"exif",n);break;case"xmlns":break;default:this.assignObjectToOutput(e,i,n);}else e.xmp=t;}}c(We,"type","xmp"),c(We,"multiSegment",!0),T.set("xmp",We);class Ke{static findAll(e){return qe(e,/([a-zA-Z0-9-]+):([a-zA-Z0-9-]+)=("[^"]*"|'[^']*')/gm).map(Ke.unpackMatch)}static unpackMatch(e){let t=e[1],i=e[2],n=e[3].slice(1,-1);return n=Qe(n),new Ke(t,i,n)}constructor(e,t,i){this.ns=e,this.name=t,this.value=i;}serialize(){return this.value}}class Xe{static findAll(e,t,i){if(void 0!==t||void 0!==i){t=t||"[\\w\\d-]+",i=i||"[\\w\\d-]+";var n=new RegExp(`<(${t}):(${i})(#\\d+)?((\\s+?[\\w\\d-:]+=("[^"]*"|'[^']*'))*\\s*)(\\/>|>([\\s\\S]*?)<\\/\\1:\\2\\3>)`,"gm");}else n=/<([\w\d-]+):([\w\d-]+)(#\d+)?((\s+?[\w\d-:]+=("[^"]*"|'[^']*'))*\s*)(\/>|>([\s\S]*?)<\/\1:\2\3>)/gm;return qe(e,n).map(Xe.unpackMatch)}static unpackMatch(e){let t=e[1],i=e[2],n=e[4],s=e[8];return new Xe(t,i,n,s)}constructor(e,t,i,n){this.ns=e,this.name=t,this.attrString=i,this.innerXml=n,this.attrs=Ke.findAll(i),this.children=Xe.findAll(n),this.value=0===this.children.length?Qe(n):void 0,this.properties=[...this.attrs,...this.children];}get isPrimitive(){return void 0!==this.value&&0===this.attrs.length&&0===this.children.length}get isListContainer(){return 1===this.children.length&&this.children[0].isList}get isList(){let{ns:e,name:t}=this;return "rdf"===e&&("Seq"===t||"Bag"===t||"Alt"===t)}get isListItem(){return "rdf"===this.ns&&"li"===this.name}serialize(){if(0===this.properties.length&&void 0===this.value)return;if(this.isPrimitive)return this.value;if(this.isListContainer)return this.children[0].serialize();if(this.isList)return $e(this.children.map(Ye));if(this.isListItem&&1===this.children.length&&0===this.attrs.length)return this.children[0].serialize();let e={};for(let t of this.properties)_e(t,e);return void 0!==this.value&&(e.value=this.value),f(e)}}function _e(e,t){let i=e.serialize();void 0!==i&&(t[e.name]=i);}var Ye=e=>e.serialize(),$e=e=>1===e.length?e[0]:e,Je=(e,t)=>t[e]?t[e]:t[e]={};function qe(e,t){let i,n=[];if(!e)return n;for(;null!==(i=t.exec(e));)n.push(i);return n}function Qe(e){if(function(e){return null==e||"null"===e||"undefined"===e||""===e||""===e.trim()}(e))return;let t=Number(e);if(!Number.isNaN(t))return t;let i=e.toLowerCase();return "true"===i||"false"!==i&&e.trim()}const Ze=["rdf:li","rdf:Seq","rdf:Bag","rdf:Alt","rdf:Description"],et=new RegExp(`(<|\\/)(${Ze.join("|")})`,"g");var tt=Object.freeze({__proto__:null,default:Me,Exifr:te,fileParsers:w,segmentParsers:T,fileReaders:A,tagKeys:E,tagValues:B,tagRevivers:N,createDictionary:U,extendDictionary:F,fetchUrlAsArrayBuffer:M,readBlobAsArrayBuffer:R,chunkedProps:G,otherSegments:V,segments:z,tiffBlocks:H,segmentsAndBlocks:j,tiffExtractables:W,inheritables:K,allFormatters:X,Options:q,parse:ie,gpsOnlyOptions:me,gps:Se,thumbnailOnlyOptions:Ce,thumbnail:ye,thumbnailUrl:be,orientationOnlyOptions:Ie,orientation:Pe,rotations:ke,get rotateCanvas(){return we},get rotateCss(){return Te},rotation:Ae});let at=l("fs",(e=>e.promises));A.set("fs",class extends ve{async readWhole(){this.chunked=!1,this.fs=await at;let e=await this.fs.readFile(this.input);this._swapBuffer(e);}async readChunked(){this.chunked=!0,this.fs=await at,await this.open(),await this.readChunk(0,this.options.firstChunkSize);}async open(){void 0===this.fh&&(this.fh=await this.fs.open(this.input,"r"),this.size=(await this.fh.stat(this.input)).size);}async _readChunk(e,t){void 0===this.fh&&await this.open(),e+t>this.size&&(t=this.size-e);var i=this.subarray(e,t,!0);return await this.fh.read(i.dataView,0,t,e),i}async close(){if(this.fh){let e=this.fh;this.fh=void 0,await e.close();}}});A.set("base64",class extends ve{constructor(...e){super(...e),this.input=this.input.replace(/^data:([^;]+);base64,/gim,""),this.size=this.input.length/4*3,this.input.endsWith("==")?this.size-=2:this.input.endsWith("=")&&(this.size-=1);}async _readChunk(e,t){let i,n,r=this.input;void 0===e?(e=0,i=0,n=0):(i=4*Math.floor(e/3),n=e-i/4*3),void 0===t&&(t=this.size);let o=e+t,l=i+4*Math.ceil(o/3);r=r.slice(i,l);let h=Math.min(t,this.size-e);if(a){let t=s.from(r,"base64").slice(n,n+h);return this.set(t,e,!0)}{let t=this.subarray(e,h,!0),i=atob(r),s=t.toUint8();for(let e=0;e<h;e++)s[e]=i.charCodeAt(n+e);return t}}});class ot extends se{static canHandle(e,t){return 18761===t||19789===t}extendOptions(e){let{ifd0:t,xmp:i,iptc:n,icc:s}=e;i.enabled&&t.deps.add(700),n.enabled&&t.deps.add(33723),s.enabled&&t.deps.add(34675),t.finalizeFilters();}async parse(){let{tiff:e,xmp:t,iptc:i,icc:n}=this.options;if(e.enabled||t.enabled||i.enabled||n.enabled){let e=Math.max(S(this.options),this.options.chunkSize);await this.file.ensureChunk(0,e),this.createParser("tiff",this.file),this.parsers.tiff.parseHeader(),await this.parsers.tiff.parseIfd0Block(),this.adaptTiffPropAsSegment("xmp"),this.adaptTiffPropAsSegment("iptc"),this.adaptTiffPropAsSegment("icc");}}adaptTiffPropAsSegment(e){if(this.parsers.tiff[e]){let t=this.parsers.tiff[e];this.injectSegment(e,t);}}}c(ot,"type","tiff"),w.set("tiff",ot);let lt=l("zlib");const ht=["ihdr","iccp","text","itxt","exif"];class ut extends se{constructor(...e){super(...e),c(this,"catchError",(e=>this.errors.push(e))),c(this,"metaChunks",[]),c(this,"unknownChunks",[]);}static canHandle(e,t){return 35152===t&&2303741511===e.getUint32(0)&&218765834===e.getUint32(4)}async parse(){let{file:e}=this;await this.findPngChunksInRange("PNG\r\n\n".length,e.byteLength),await this.readSegments(this.metaChunks),this.findIhdr(),this.parseTextChunks(),await this.findExif().catch(this.catchError),await this.findXmp().catch(this.catchError),await this.findIcc().catch(this.catchError);}async findPngChunksInRange(e,t){let{file:i}=this;for(;e<t;){let t=i.getUint32(e),n=i.getUint32(e+4),s=i.getString(e+4,4).toLowerCase(),r=t+4+4+4,a={type:s,offset:e,length:r,start:e+4+4,size:t,marker:n};ht.includes(s)?this.metaChunks.push(a):this.unknownChunks.push(a),e+=r;}}parseTextChunks(){let e=this.metaChunks.filter((e=>"text"===e.type));for(let t of e){let[e,i]=this.file.getString(t.start,t.size).split("\0");this.injectKeyValToIhdr(e,i);}}injectKeyValToIhdr(e,t){let i=this.parsers.ihdr;i&&i.raw.set(e,t);}findIhdr(){let e=this.metaChunks.find((e=>"ihdr"===e.type));e&&!1!==this.options.ihdr.enabled&&this.createParser("ihdr",e.chunk);}async findExif(){let e=this.metaChunks.find((e=>"exif"===e.type));e&&this.injectSegment("tiff",e.chunk);}async findXmp(){let e=this.metaChunks.filter((e=>"itxt"===e.type));for(let t of e){"XML:com.adobe.xmp"===t.chunk.getString(0,"XML:com.adobe.xmp".length)&&this.injectSegment("xmp",t.chunk);}}async findIcc(){let e=this.metaChunks.find((e=>"iccp"===e.type));if(!e)return;let{chunk:t}=e,i=t.getUint8Array(0,81),s=0;for(;s<80&&0!==i[s];)s++;let r=s+2,a=t.getString(0,s);if(this.injectKeyValToIhdr("ProfileName",a),n){let e=await lt,i=t.getUint8Array(r);i=e.inflateSync(i),this.injectSegment("icc",i);}}}c(ut,"type","png"),w.set("png",ut),U(E,"interop",[[1,"InteropIndex"],[2,"InteropVersion"],[4096,"RelatedImageFileFormat"],[4097,"RelatedImageWidth"],[4098,"RelatedImageHeight"]]),F(E,"ifd0",[[11,"ProcessingSoftware"],[254,"SubfileType"],[255,"OldSubfileType"],[263,"Thresholding"],[264,"CellWidth"],[265,"CellLength"],[266,"FillOrder"],[269,"DocumentName"],[280,"MinSampleValue"],[281,"MaxSampleValue"],[285,"PageName"],[286,"XPosition"],[287,"YPosition"],[290,"GrayResponseUnit"],[297,"PageNumber"],[321,"HalftoneHints"],[322,"TileWidth"],[323,"TileLength"],[332,"InkSet"],[337,"TargetPrinter"],[18246,"Rating"],[18249,"RatingPercent"],[33550,"PixelScale"],[34264,"ModelTransform"],[34377,"PhotoshopSettings"],[50706,"DNGVersion"],[50707,"DNGBackwardVersion"],[50708,"UniqueCameraModel"],[50709,"LocalizedCameraModel"],[50736,"DNGLensInfo"],[50739,"ShadowScale"],[50740,"DNGPrivateData"],[33920,"IntergraphMatrix"],[33922,"ModelTiePoint"],[34118,"SEMInfo"],[34735,"GeoTiffDirectory"],[34736,"GeoTiffDoubleParams"],[34737,"GeoTiffAsciiParams"],[50341,"PrintIM"],[50721,"ColorMatrix1"],[50722,"ColorMatrix2"],[50723,"CameraCalibration1"],[50724,"CameraCalibration2"],[50725,"ReductionMatrix1"],[50726,"ReductionMatrix2"],[50727,"AnalogBalance"],[50728,"AsShotNeutral"],[50729,"AsShotWhiteXY"],[50730,"BaselineExposure"],[50731,"BaselineNoise"],[50732,"BaselineSharpness"],[50734,"LinearResponseLimit"],[50735,"CameraSerialNumber"],[50741,"MakerNoteSafety"],[50778,"CalibrationIlluminant1"],[50779,"CalibrationIlluminant2"],[50781,"RawDataUniqueID"],[50827,"OriginalRawFileName"],[50828,"OriginalRawFileData"],[50831,"AsShotICCProfile"],[50832,"AsShotPreProfileMatrix"],[50833,"CurrentICCProfile"],[50834,"CurrentPreProfileMatrix"],[50879,"ColorimetricReference"],[50885,"SRawType"],[50898,"PanasonicTitle"],[50899,"PanasonicTitle2"],[50931,"CameraCalibrationSig"],[50932,"ProfileCalibrationSig"],[50933,"ProfileIFD"],[50934,"AsShotProfileName"],[50936,"ProfileName"],[50937,"ProfileHueSatMapDims"],[50938,"ProfileHueSatMapData1"],[50939,"ProfileHueSatMapData2"],[50940,"ProfileToneCurve"],[50941,"ProfileEmbedPolicy"],[50942,"ProfileCopyright"],[50964,"ForwardMatrix1"],[50965,"ForwardMatrix2"],[50966,"PreviewApplicationName"],[50967,"PreviewApplicationVersion"],[50968,"PreviewSettingsName"],[50969,"PreviewSettingsDigest"],[50970,"PreviewColorSpace"],[50971,"PreviewDateTime"],[50972,"RawImageDigest"],[50973,"OriginalRawFileDigest"],[50981,"ProfileLookTableDims"],[50982,"ProfileLookTableData"],[51043,"TimeCodes"],[51044,"FrameRate"],[51058,"TStop"],[51081,"ReelName"],[51089,"OriginalDefaultFinalSize"],[51090,"OriginalBestQualitySize"],[51091,"OriginalDefaultCropSize"],[51105,"CameraLabel"],[51107,"ProfileHueSatMapEncoding"],[51108,"ProfileLookTableEncoding"],[51109,"BaselineExposureOffset"],[51110,"DefaultBlackRender"],[51111,"NewRawImageDigest"],[51112,"RawToPreviewGain"]]);let ct=[[273,"StripOffsets"],[279,"StripByteCounts"],[288,"FreeOffsets"],[289,"FreeByteCounts"],[291,"GrayResponseCurve"],[292,"T4Options"],[293,"T6Options"],[300,"ColorResponseUnit"],[320,"ColorMap"],[324,"TileOffsets"],[325,"TileByteCounts"],[326,"BadFaxLines"],[327,"CleanFaxData"],[328,"ConsecutiveBadFaxLines"],[330,"SubIFD"],[333,"InkNames"],[334,"NumberofInks"],[336,"DotRange"],[338,"ExtraSamples"],[339,"SampleFormat"],[340,"SMinSampleValue"],[341,"SMaxSampleValue"],[342,"TransferRange"],[343,"ClipPath"],[344,"XClipPathUnits"],[345,"YClipPathUnits"],[346,"Indexed"],[347,"JPEGTables"],[351,"OPIProxy"],[400,"GlobalParametersIFD"],[401,"ProfileType"],[402,"FaxProfile"],[403,"CodingMethods"],[404,"VersionYear"],[405,"ModeNumber"],[433,"Decode"],[434,"DefaultImageColor"],[435,"T82Options"],[437,"JPEGTables"],[512,"JPEGProc"],[515,"JPEGRestartInterval"],[517,"JPEGLosslessPredictors"],[518,"JPEGPointTransforms"],[519,"JPEGQTables"],[520,"JPEGDCTables"],[521,"JPEGACTables"],[559,"StripRowCounts"],[999,"USPTOMiscellaneous"],[18247,"XP_DIP_XML"],[18248,"StitchInfo"],[28672,"SonyRawFileType"],[28688,"SonyToneCurve"],[28721,"VignettingCorrection"],[28722,"VignettingCorrParams"],[28724,"ChromaticAberrationCorrection"],[28725,"ChromaticAberrationCorrParams"],[28726,"DistortionCorrection"],[28727,"DistortionCorrParams"],[29895,"SonyCropTopLeft"],[29896,"SonyCropSize"],[32781,"ImageID"],[32931,"WangTag1"],[32932,"WangAnnotation"],[32933,"WangTag3"],[32934,"WangTag4"],[32953,"ImageReferencePoints"],[32954,"RegionXformTackPoint"],[32955,"WarpQuadrilateral"],[32956,"AffineTransformMat"],[32995,"Matteing"],[32996,"DataType"],[32997,"ImageDepth"],[32998,"TileDepth"],[33300,"ImageFullWidth"],[33301,"ImageFullHeight"],[33302,"TextureFormat"],[33303,"WrapModes"],[33304,"FovCot"],[33305,"MatrixWorldToScreen"],[33306,"MatrixWorldToCamera"],[33405,"Model2"],[33421,"CFARepeatPatternDim"],[33422,"CFAPattern2"],[33423,"BatteryLevel"],[33424,"KodakIFD"],[33445,"MDFileTag"],[33446,"MDScalePixel"],[33447,"MDColorTable"],[33448,"MDLabName"],[33449,"MDSampleInfo"],[33450,"MDPrepDate"],[33451,"MDPrepTime"],[33452,"MDFileUnits"],[33589,"AdventScale"],[33590,"AdventRevision"],[33628,"UIC1Tag"],[33629,"UIC2Tag"],[33630,"UIC3Tag"],[33631,"UIC4Tag"],[33918,"IntergraphPacketData"],[33919,"IntergraphFlagRegisters"],[33921,"INGRReserved"],[34016,"Site"],[34017,"ColorSequence"],[34018,"IT8Header"],[34019,"RasterPadding"],[34020,"BitsPerRunLength"],[34021,"BitsPerExtendedRunLength"],[34022,"ColorTable"],[34023,"ImageColorIndicator"],[34024,"BackgroundColorIndicator"],[34025,"ImageColorValue"],[34026,"BackgroundColorValue"],[34027,"PixelIntensityRange"],[34028,"TransparencyIndicator"],[34029,"ColorCharacterization"],[34030,"HCUsage"],[34031,"TrapIndicator"],[34032,"CMYKEquivalent"],[34152,"AFCP_IPTC"],[34232,"PixelMagicJBIGOptions"],[34263,"JPLCartoIFD"],[34306,"WB_GRGBLevels"],[34310,"LeafData"],[34687,"TIFF_FXExtensions"],[34688,"MultiProfiles"],[34689,"SharedData"],[34690,"T88Options"],[34732,"ImageLayer"],[34750,"JBIGOptions"],[34856,"Opto-ElectricConvFactor"],[34857,"Interlace"],[34908,"FaxRecvParams"],[34909,"FaxSubAddress"],[34910,"FaxRecvTime"],[34929,"FedexEDR"],[34954,"LeafSubIFD"],[37387,"FlashEnergy"],[37388,"SpatialFrequencyResponse"],[37389,"Noise"],[37390,"FocalPlaneXResolution"],[37391,"FocalPlaneYResolution"],[37392,"FocalPlaneResolutionUnit"],[37397,"ExposureIndex"],[37398,"TIFF-EPStandardID"],[37399,"SensingMethod"],[37434,"CIP3DataFile"],[37435,"CIP3Sheet"],[37436,"CIP3Side"],[37439,"StoNits"],[37679,"MSDocumentText"],[37680,"MSPropertySetStorage"],[37681,"MSDocumentTextPosition"],[37724,"ImageSourceData"],[40965,"InteropIFD"],[40976,"SamsungRawPointersOffset"],[40977,"SamsungRawPointersLength"],[41217,"SamsungRawByteOrder"],[41218,"SamsungRawUnknown"],[41484,"SpatialFrequencyResponse"],[41485,"Noise"],[41489,"ImageNumber"],[41490,"SecurityClassification"],[41491,"ImageHistory"],[41494,"TIFF-EPStandardID"],[41995,"DeviceSettingDescription"],[42112,"GDALMetadata"],[42113,"GDALNoData"],[44992,"ExpandSoftware"],[44993,"ExpandLens"],[44994,"ExpandFilm"],[44995,"ExpandFilterLens"],[44996,"ExpandScanner"],[44997,"ExpandFlashLamp"],[46275,"HasselbladRawImage"],[48129,"PixelFormat"],[48130,"Transformation"],[48131,"Uncompressed"],[48132,"ImageType"],[48256,"ImageWidth"],[48257,"ImageHeight"],[48258,"WidthResolution"],[48259,"HeightResolution"],[48320,"ImageOffset"],[48321,"ImageByteCount"],[48322,"AlphaOffset"],[48323,"AlphaByteCount"],[48324,"ImageDataDiscard"],[48325,"AlphaDataDiscard"],[50215,"OceScanjobDesc"],[50216,"OceApplicationSelector"],[50217,"OceIDNumber"],[50218,"OceImageLogic"],[50255,"Annotations"],[50459,"HasselbladExif"],[50547,"OriginalFileName"],[50560,"USPTOOriginalContentType"],[50656,"CR2CFAPattern"],[50710,"CFAPlaneColor"],[50711,"CFALayout"],[50712,"LinearizationTable"],[50713,"BlackLevelRepeatDim"],[50714,"BlackLevel"],[50715,"BlackLevelDeltaH"],[50716,"BlackLevelDeltaV"],[50717,"WhiteLevel"],[50718,"DefaultScale"],[50719,"DefaultCropOrigin"],[50720,"DefaultCropSize"],[50733,"BayerGreenSplit"],[50737,"ChromaBlurRadius"],[50738,"AntiAliasStrength"],[50752,"RawImageSegmentation"],[50780,"BestQualityScale"],[50784,"AliasLayerMetadata"],[50829,"ActiveArea"],[50830,"MaskedAreas"],[50935,"NoiseReductionApplied"],[50974,"SubTileBlockSize"],[50975,"RowInterleaveFactor"],[51008,"OpcodeList1"],[51009,"OpcodeList2"],[51022,"OpcodeList3"],[51041,"NoiseProfile"],[51114,"CacheVersion"],[51125,"DefaultUserCrop"],[51157,"NikonNEFInfo"],[65024,"KdcIFD"]];F(E,"ifd0",ct),F(E,"exif",ct),U(B,"gps",[[23,{M:"Magnetic North",T:"True North"}],[25,{K:"Kilometers",M:"Miles",N:"Nautical Miles"}]]);class ft extends re{static canHandle(e,t){return 224===e.getUint8(t+1)&&1246120262===e.getUint32(t+4)&&0===e.getUint8(t+8)}parse(){return this.parseTags(),this.translate(),this.output}parseTags(){this.raw=new Map([[0,this.chunk.getUint16(0)],[2,this.chunk.getUint8(2)],[3,this.chunk.getUint16(3)],[5,this.chunk.getUint16(5)],[7,this.chunk.getUint8(7)],[8,this.chunk.getUint8(8)]]);}}c(ft,"type","jfif"),c(ft,"headerLength",9),T.set("jfif",ft),U(E,"jfif",[[0,"JFIFVersion"],[2,"ResolutionUnit"],[3,"XResolution"],[5,"YResolution"],[7,"ThumbnailWidth"],[8,"ThumbnailHeight"]]);class dt extends re{parse(){return this.parseTags(),this.translate(),this.output}parseTags(){this.raw=new Map([[0,this.chunk.getUint32(0)],[4,this.chunk.getUint32(4)],[8,this.chunk.getUint8(8)],[9,this.chunk.getUint8(9)],[10,this.chunk.getUint8(10)],[11,this.chunk.getUint8(11)],[12,this.chunk.getUint8(12)],...Array.from(this.raw)]);}}c(dt,"type","ihdr"),T.set("ihdr",dt),U(E,"ihdr",[[0,"ImageWidth"],[4,"ImageHeight"],[8,"BitDepth"],[9,"ColorType"],[10,"Compression"],[11,"Filter"],[12,"Interlace"]]),U(B,"ihdr",[[9,{0:"Grayscale",2:"RGB",3:"Palette",4:"Grayscale with Alpha",6:"RGB with Alpha",DEFAULT:"Unknown"}],[10,{0:"Deflate/Inflate",DEFAULT:"Unknown"}],[11,{0:"Adaptive",DEFAULT:"Unknown"}],[12,{0:"Noninterlaced",1:"Adam7 Interlace",DEFAULT:"Unknown"}]]);class pt extends re{static canHandle(e,t){return 226===e.getUint8(t+1)&&1229144927===e.getUint32(t+4)}static findPosition(e,t){let i=super.findPosition(e,t);return i.chunkNumber=e.getUint8(t+16),i.chunkCount=e.getUint8(t+17),i.multiSegment=i.chunkCount>1,i}static handleMultiSegments(e){return function(e){let t=function(e){let t=e[0].constructor,i=0;for(let t of e)i+=t.length;let n=new t(i),s=0;for(let t of e)n.set(t,s),s+=t.length;return n}(e.map((e=>e.chunk.toUint8())));return new I(t)}(e)}parse(){return this.raw=new Map,this.parseHeader(),this.parseTags(),this.translate(),this.output}parseHeader(){let{raw:e}=this;this.chunk.byteLength<84&&g("ICC header is too short");for(let[t,i]of Object.entries(gt)){t=parseInt(t,10);let n=i(this.chunk,t);"\0\0\0\0"!==n&&e.set(t,n);}}parseTags(){let e,t,i,n,s,{raw:r}=this,a=this.chunk.getUint32(128),o=132,l=this.chunk.byteLength;for(;a--;){if(e=this.chunk.getString(o,4),t=this.chunk.getUint32(o+4),i=this.chunk.getUint32(o+8),n=this.chunk.getString(t,4),t+i>l)return void console.warn("reached the end of the first ICC chunk. Enable options.tiff.multiSegment to read all ICC segments.");s=this.parseTag(n,t,i),void 0!==s&&"\0\0\0\0"!==s&&r.set(e,s),o+=12;}}parseTag(e,t,i){switch(e){case"desc":return this.parseDesc(t);case"mluc":return this.parseMluc(t);case"text":return this.parseText(t,i);case"sig ":return this.parseSig(t)}if(!(t+i>this.chunk.byteLength))return this.chunk.getUint8Array(t,i)}parseDesc(e){let t=this.chunk.getUint32(e+8)-1;return m(this.chunk.getString(e+12,t))}parseText(e,t){return m(this.chunk.getString(e+8,t-8))}parseSig(e){return m(this.chunk.getString(e+8,4))}parseMluc(e){let{chunk:t}=this,i=t.getUint32(e+8),n=t.getUint32(e+12),s=e+16,r=[];for(let a=0;a<i;a++){let i=t.getString(s+0,2),a=t.getString(s+2,2),o=t.getUint32(s+4),l=t.getUint32(s+8)+e,h=m(t.getUnicodeString(l,o));r.push({lang:i,country:a,text:h}),s+=n;}return 1===i?r[0].text:r}translateValue(e,t){return "string"==typeof e?t[e]||t[e.toLowerCase()]||e:t[e]||e}}c(pt,"type","icc"),c(pt,"multiSegment",!0),c(pt,"headerLength",18);const gt={4:mt,8:function(e,t){return [e.getUint8(t),e.getUint8(t+1)>>4,e.getUint8(t+1)%16].map((e=>e.toString(10))).join(".")},12:mt,16:mt,20:mt,24:function(e,t){const i=e.getUint16(t),n=e.getUint16(t+2)-1,s=e.getUint16(t+4),r=e.getUint16(t+6),a=e.getUint16(t+8),o=e.getUint16(t+10);return new Date(Date.UTC(i,n,s,r,a,o))},36:mt,40:mt,48:mt,52:mt,64:(e,t)=>e.getUint32(t),80:mt};function mt(e,t){return m(e.getString(t,4))}T.set("icc",pt),U(E,"icc",[[4,"ProfileCMMType"],[8,"ProfileVersion"],[12,"ProfileClass"],[16,"ColorSpaceData"],[20,"ProfileConnectionSpace"],[24,"ProfileDateTime"],[36,"ProfileFileSignature"],[40,"PrimaryPlatform"],[44,"CMMFlags"],[48,"DeviceManufacturer"],[52,"DeviceModel"],[56,"DeviceAttributes"],[64,"RenderingIntent"],[68,"ConnectionSpaceIlluminant"],[80,"ProfileCreator"],[84,"ProfileID"],["Header","ProfileHeader"],["MS00","WCSProfiles"],["bTRC","BlueTRC"],["bXYZ","BlueMatrixColumn"],["bfd","UCRBG"],["bkpt","MediaBlackPoint"],["calt","CalibrationDateTime"],["chad","ChromaticAdaptation"],["chrm","Chromaticity"],["ciis","ColorimetricIntentImageState"],["clot","ColorantTableOut"],["clro","ColorantOrder"],["clrt","ColorantTable"],["cprt","ProfileCopyright"],["crdi","CRDInfo"],["desc","ProfileDescription"],["devs","DeviceSettings"],["dmdd","DeviceModelDesc"],["dmnd","DeviceMfgDesc"],["dscm","ProfileDescriptionML"],["fpce","FocalPlaneColorimetryEstimates"],["gTRC","GreenTRC"],["gXYZ","GreenMatrixColumn"],["gamt","Gamut"],["kTRC","GrayTRC"],["lumi","Luminance"],["meas","Measurement"],["meta","Metadata"],["mmod","MakeAndModel"],["ncl2","NamedColor2"],["ncol","NamedColor"],["ndin","NativeDisplayInfo"],["pre0","Preview0"],["pre1","Preview1"],["pre2","Preview2"],["ps2i","PS2RenderingIntent"],["ps2s","PostScript2CSA"],["psd0","PostScript2CRD0"],["psd1","PostScript2CRD1"],["psd2","PostScript2CRD2"],["psd3","PostScript2CRD3"],["pseq","ProfileSequenceDesc"],["psid","ProfileSequenceIdentifier"],["psvm","PS2CRDVMSize"],["rTRC","RedTRC"],["rXYZ","RedMatrixColumn"],["resp","OutputResponse"],["rhoc","ReflectionHardcopyOrigColorimetry"],["rig0","PerceptualRenderingIntentGamut"],["rig2","SaturationRenderingIntentGamut"],["rpoc","ReflectionPrintOutputColorimetry"],["sape","SceneAppearanceEstimates"],["scoe","SceneColorimetryEstimates"],["scrd","ScreeningDesc"],["scrn","Screening"],["targ","CharTarget"],["tech","Technology"],["vcgt","VideoCardGamma"],["view","ViewingConditions"],["vued","ViewingCondDesc"],["wtpt","MediaWhitePoint"]]);const St={"4d2p":"Erdt Systems",AAMA:"Aamazing Technologies",ACER:"Acer",ACLT:"Acolyte Color Research",ACTI:"Actix Sytems",ADAR:"Adara Technology",ADBE:"Adobe",ADI:"ADI Systems",AGFA:"Agfa Graphics",ALMD:"Alps Electric",ALPS:"Alps Electric",ALWN:"Alwan Color Expertise",AMTI:"Amiable Technologies",AOC:"AOC International",APAG:"Apago",APPL:"Apple Computer",AST:"AST","AT&T":"AT&T",BAEL:"BARBIERI electronic",BRCO:"Barco NV",BRKP:"Breakpoint",BROT:"Brother",BULL:"Bull",BUS:"Bus Computer Systems","C-IT":"C-Itoh",CAMR:"Intel",CANO:"Canon",CARR:"Carroll Touch",CASI:"Casio",CBUS:"Colorbus PL",CEL:"Crossfield",CELx:"Crossfield",CGS:"CGS Publishing Technologies International",CHM:"Rochester Robotics",CIGL:"Colour Imaging Group, London",CITI:"Citizen",CL00:"Candela",CLIQ:"Color IQ",CMCO:"Chromaco",CMiX:"CHROMiX",COLO:"Colorgraphic Communications",COMP:"Compaq",COMp:"Compeq/Focus Technology",CONR:"Conrac Display Products",CORD:"Cordata Technologies",CPQ:"Compaq",CPRO:"ColorPro",CRN:"Cornerstone",CTX:"CTX International",CVIS:"ColorVision",CWC:"Fujitsu Laboratories",DARI:"Darius Technology",DATA:"Dataproducts",DCP:"Dry Creek Photo",DCRC:"Digital Contents Resource Center, Chung-Ang University",DELL:"Dell Computer",DIC:"Dainippon Ink and Chemicals",DICO:"Diconix",DIGI:"Digital","DL&C":"Digital Light & Color",DPLG:"Doppelganger",DS:"Dainippon Screen",DSOL:"DOOSOL",DUPN:"DuPont",EPSO:"Epson",ESKO:"Esko-Graphics",ETRI:"Electronics and Telecommunications Research Institute",EVER:"Everex Systems",EXAC:"ExactCODE",Eizo:"Eizo",FALC:"Falco Data Products",FF:"Fuji Photo Film",FFEI:"FujiFilm Electronic Imaging",FNRD:"Fnord Software",FORA:"Fora",FORE:"Forefront Technology",FP:"Fujitsu",FPA:"WayTech Development",FUJI:"Fujitsu",FX:"Fuji Xerox",GCC:"GCC Technologies",GGSL:"Global Graphics Software",GMB:"Gretagmacbeth",GMG:"GMG",GOLD:"GoldStar Technology",GOOG:"Google",GPRT:"Giantprint",GTMB:"Gretagmacbeth",GVC:"WayTech Development",GW2K:"Sony",HCI:"HCI",HDM:"Heidelberger Druckmaschinen",HERM:"Hermes",HITA:"Hitachi America",HP:"Hewlett-Packard",HTC:"Hitachi",HiTi:"HiTi Digital",IBM:"IBM",IDNT:"Scitex",IEC:"Hewlett-Packard",IIYA:"Iiyama North America",IKEG:"Ikegami Electronics",IMAG:"Image Systems",IMI:"Ingram Micro",INTC:"Intel",INTL:"N/A (INTL)",INTR:"Intra Electronics",IOCO:"Iocomm International Technology",IPS:"InfoPrint Solutions Company",IRIS:"Scitex",ISL:"Ichikawa Soft Laboratory",ITNL:"N/A (ITNL)",IVM:"IVM",IWAT:"Iwatsu Electric",Idnt:"Scitex",Inca:"Inca Digital Printers",Iris:"Scitex",JPEG:"Joint Photographic Experts Group",JSFT:"Jetsoft Development",JVC:"JVC Information Products",KART:"Scitex",KFC:"KFC Computek Components",KLH:"KLH Computers",KMHD:"Konica Minolta",KNCA:"Konica",KODA:"Kodak",KYOC:"Kyocera",Kart:"Scitex",LCAG:"Leica",LCCD:"Leeds Colour",LDAK:"Left Dakota",LEAD:"Leading Technology",LEXM:"Lexmark International",LINK:"Link Computer",LINO:"Linotronic",LITE:"Lite-On",Leaf:"Leaf",Lino:"Linotronic",MAGC:"Mag Computronic",MAGI:"MAG Innovision",MANN:"Mannesmann",MICN:"Micron Technology",MICR:"Microtek",MICV:"Microvitec",MINO:"Minolta",MITS:"Mitsubishi Electronics America",MITs:"Mitsuba",MNLT:"Minolta",MODG:"Modgraph",MONI:"Monitronix",MONS:"Monaco Systems",MORS:"Morse Technology",MOTI:"Motive Systems",MSFT:"Microsoft",MUTO:"MUTOH INDUSTRIES",Mits:"Mitsubishi Electric",NANA:"NANAO",NEC:"NEC",NEXP:"NexPress Solutions",NISS:"Nissei Sangyo America",NKON:"Nikon",NONE:"none",OCE:"Oce Technologies",OCEC:"OceColor",OKI:"Oki",OKID:"Okidata",OKIP:"Okidata",OLIV:"Olivetti",OLYM:"Olympus",ONYX:"Onyx Graphics",OPTI:"Optiquest",PACK:"Packard Bell",PANA:"Matsushita Electric Industrial",PANT:"Pantone",PBN:"Packard Bell",PFU:"PFU",PHIL:"Philips Consumer Electronics",PNTX:"HOYA",POne:"Phase One A/S",PREM:"Premier Computer Innovations",PRIN:"Princeton Graphic Systems",PRIP:"Princeton Publishing Labs",QLUX:"Hong Kong",QMS:"QMS",QPCD:"QPcard AB",QUAD:"QuadLaser",QUME:"Qume",RADI:"Radius",RDDx:"Integrated Color Solutions",RDG:"Roland DG",REDM:"REDMS Group",RELI:"Relisys",RGMS:"Rolf Gierling Multitools",RICO:"Ricoh",RNLD:"Edmund Ronald",ROYA:"Royal",RPC:"Ricoh Printing Systems",RTL:"Royal Information Electronics",SAMP:"Sampo",SAMS:"Samsung",SANT:"Jaime Santana Pomares",SCIT:"Scitex",SCRN:"Dainippon Screen",SDP:"Scitex",SEC:"Samsung",SEIK:"Seiko Instruments",SEIk:"Seikosha",SGUY:"ScanGuy.com",SHAR:"Sharp Laboratories",SICC:"International Color Consortium",SONY:"Sony",SPCL:"SpectraCal",STAR:"Star",STC:"Sampo Technology",Scit:"Scitex",Sdp:"Scitex",Sony:"Sony",TALO:"Talon Technology",TAND:"Tandy",TATU:"Tatung",TAXA:"TAXAN America",TDS:"Tokyo Denshi Sekei",TECO:"TECO Information Systems",TEGR:"Tegra",TEKT:"Tektronix",TI:"Texas Instruments",TMKR:"TypeMaker",TOSB:"Toshiba",TOSH:"Toshiba",TOTK:"TOTOKU ELECTRIC",TRIU:"Triumph",TSBT:"Toshiba",TTX:"TTX Computer Products",TVM:"TVM Professional Monitor",TW:"TW Casper",ULSX:"Ulead Systems",UNIS:"Unisys",UTZF:"Utz Fehlau & Sohn",VARI:"Varityper",VIEW:"Viewsonic",VISL:"Visual communication",VIVO:"Vivo Mobile Communication",WANG:"Wang",WLBR:"Wilbur Imaging",WTG2:"Ware To Go",WYSE:"WYSE Technology",XERX:"Xerox",XRIT:"X-Rite",ZRAN:"Zoran",Zebr:"Zebra Technologies",appl:"Apple Computer",bICC:"basICColor",berg:"bergdesign",ceyd:"Integrated Color Solutions",clsp:"MacDermid ColorSpan",ds:"Dainippon Screen",dupn:"DuPont",ffei:"FujiFilm Electronic Imaging",flux:"FluxData",iris:"Scitex",kart:"Scitex",lcms:"Little CMS",lino:"Linotronic",none:"none",ob4d:"Erdt Systems",obic:"Medigraph",quby:"Qubyx Sarl",scit:"Scitex",scrn:"Dainippon Screen",sdp:"Scitex",siwi:"SIWI GRAFIKA",yxym:"YxyMaster"},Ct={scnr:"Scanner",mntr:"Monitor",prtr:"Printer",link:"Device Link",abst:"Abstract",spac:"Color Space Conversion Profile",nmcl:"Named Color",cenc:"ColorEncodingSpace profile",mid:"MultiplexIdentification profile",mlnk:"MultiplexLink profile",mvis:"MultiplexVisualization profile",nkpf:"Nikon Input Device Profile (NON-STANDARD!)"};U(B,"icc",[[4,St],[12,Ct],[40,Object.assign({},St,Ct)],[48,St],[80,St],[64,{0:"Perceptual",1:"Relative Colorimetric",2:"Saturation",3:"Absolute Colorimetric"}],["tech",{amd:"Active Matrix Display",crt:"Cathode Ray Tube Display",kpcd:"Photo CD",pmd:"Passive Matrix Display",dcam:"Digital Camera",dcpj:"Digital Cinema Projector",dmpc:"Digital Motion Picture Camera",dsub:"Dye Sublimation Printer",epho:"Electrophotographic Printer",esta:"Electrostatic Printer",flex:"Flexography",fprn:"Film Writer",fscn:"Film Scanner",grav:"Gravure",ijet:"Ink Jet Printer",imgs:"Photo Image Setter",mpfr:"Motion Picture Film Recorder",mpfs:"Motion Picture Film Scanner",offs:"Offset Lithography",pjtv:"Projection Television",rpho:"Photographic Paper Printer",rscn:"Reflective Scanner",silk:"Silkscreen",twax:"Thermal Wax Printer",vidc:"Video Camera",vidm:"Video Monitor"}]]);class yt extends re{static canHandle(e,t,i){return 237===e.getUint8(t+1)&&"Photoshop"===e.getString(t+4,9)&&void 0!==this.containsIptc8bim(e,t,i)}static headerLength(e,t,i){let n,s=this.containsIptc8bim(e,t,i);if(void 0!==s)return n=e.getUint8(t+s+7),n%2!=0&&(n+=1),0===n&&(n=4),s+8+n}static containsIptc8bim(e,t,i){for(let n=0;n<i;n++)if(this.isIptcSegmentHead(e,t+n))return n}static isIptcSegmentHead(e,t){return 56===e.getUint8(t)&&943868237===e.getUint32(t)&&1028===e.getUint16(t+4)}parse(){let{raw:e}=this,t=this.chunk.byteLength-1,i=!1;for(let n=0;n<t;n++)if(28===this.chunk.getUint8(n)&&2===this.chunk.getUint8(n+1)){i=!0;let t=this.chunk.getUint16(n+3),s=this.chunk.getUint8(n+2),r=this.chunk.getLatin1String(n+5,t);e.set(s,this.pluralizeValue(e.get(s),r)),n+=4+t;}else if(i)break;return this.translate(),this.output}pluralizeValue(e,t){return void 0!==e?e instanceof Array?(e.push(t),e):[e,t]:t}}c(yt,"type","iptc"),c(yt,"translateValues",!1),c(yt,"reviveValues",!1),T.set("iptc",yt),U(E,"iptc",[[0,"ApplicationRecordVersion"],[3,"ObjectTypeReference"],[4,"ObjectAttributeReference"],[5,"ObjectName"],[7,"EditStatus"],[8,"EditorialUpdate"],[10,"Urgency"],[12,"SubjectReference"],[15,"Category"],[20,"SupplementalCategories"],[22,"FixtureIdentifier"],[25,"Keywords"],[26,"ContentLocationCode"],[27,"ContentLocationName"],[30,"ReleaseDate"],[35,"ReleaseTime"],[37,"ExpirationDate"],[38,"ExpirationTime"],[40,"SpecialInstructions"],[42,"ActionAdvised"],[45,"ReferenceService"],[47,"ReferenceDate"],[50,"ReferenceNumber"],[55,"DateCreated"],[60,"TimeCreated"],[62,"DigitalCreationDate"],[63,"DigitalCreationTime"],[65,"OriginatingProgram"],[70,"ProgramVersion"],[75,"ObjectCycle"],[80,"Byline"],[85,"BylineTitle"],[90,"City"],[92,"Sublocation"],[95,"State"],[100,"CountryCode"],[101,"Country"],[103,"OriginalTransmissionReference"],[105,"Headline"],[110,"Credit"],[115,"Source"],[116,"CopyrightNotice"],[118,"Contact"],[120,"Caption"],[121,"LocalCaption"],[122,"Writer"],[125,"RasterizedCaption"],[130,"ImageType"],[131,"ImageOrientation"],[135,"LanguageIdentifier"],[150,"AudioType"],[151,"AudioSamplingRate"],[152,"AudioSamplingResolution"],[153,"AudioDuration"],[154,"AudioOutcue"],[184,"JobID"],[185,"MasterDocumentID"],[186,"ShortDocumentID"],[187,"UniqueDocumentID"],[188,"OwnerID"],[200,"ObjectPreviewFileFormat"],[201,"ObjectPreviewFileVersion"],[202,"ObjectPreviewData"],[221,"Prefs"],[225,"ClassifyState"],[228,"SimilarityIndex"],[230,"DocumentNotes"],[231,"DocumentHistory"],[232,"ExifCameraInfo"],[255,"CatalogSets"]]),U(B,"iptc",[[10,{0:"0 (reserved)",1:"1 (most urgent)",2:"2",3:"3",4:"4",5:"5 (normal urgency)",6:"6",7:"7",8:"8 (least urgent)",9:"9 (user-defined priority)"}],[75,{a:"Morning",b:"Both Morning and Evening",p:"Evening"}],[131,{L:"Landscape",P:"Portrait",S:"Square"}]]);

function checkImageSupport() {
    const elem = document.createElement('canvas');
    elem.id = 'webp-support';
    const webp = elem.toDataURL('image/webp').indexOf('data:image/webp') === 0;
    const heic = elem.toDataURL('image/heic').indexOf('data:image/heic') === 0;
    return {
        webp, heic
    };
}
const supportedPreviewTypes = new Set(['image/png', 'image/jpeg', 'image/svg+xml']);
const { webp, heic } = checkImageSupport();
if (webp) {
    supportedPreviewTypes.add('image/webp');
}
if (heic) {
    supportedPreviewTypes.add('image/heic');
}
function createWorkerFromInlineCode(code) {
    const blob = new Blob([code], { type: 'application/javascript' });
    return new Worker(URL.createObjectURL(blob), { type: 'module' });
}
function loadImage(url) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject();
        image.src = url;
    });
}

const MAX_IMAGE_AREA = 4096 * 4096;
/**
 * The Thumbnail Generator plugin
 */
class ThumbnailGenerator extends Plugin {
    constructor(uppy, opts) {
        super(uppy, opts);
        this.onFileAddedBinded = this.onFileAdded.bind(this);
        this.onCancelRequestBinded = this.onCancelRequest.bind(this);
        this.onFileRemovedBinded = this.onFileRemoved.bind(this);
        this.onRestoredBinded = this.onRestored.bind(this);
        this.waitUntilAllProcessedBinded = this.waitUntilAllProcessed.bind(this);
        this.promises = {};
        this.type = 'modifier';
        this.id = this.opts.id || 'ThumbnailGenerator';
        this.title = 'Thumbnail Generator';
        this.queue = [];
        this.queueProcessing = false;
        this.defaultThumbnailDimension = 200;
        this.thumbnailType = this.opts.thumbnailType || 'image/jpeg';
        this.clearMemoryAfterUpload = this.opts.clearMemoryAfterUpload ?? true;
        const defaultOptions = {
            thumbnailWidth: null,
            thumbnailHeight: null,
            waitForThumbnailsBeforeUpload: false,
            lazy: false,
        };
        this.opts = { ...defaultOptions, ...opts };
        if (this.opts.lazy && this.opts.waitForThumbnailsBeforeUpload) {
            throw new Error('ThumbnailGenerator: The `lazy` and `waitForThumbnailsBeforeUpload` options are mutually exclusive. Please ensure at most one of them is set to `true`.');
        }
        if (typeof OffscreenCanvas !== 'undefined') {
            try {
                this.worker = createWorkerFromInlineCode(thumbnailWorker);
                this.worker.addEventListener('message', this.onWorkerMessage.bind(this));
                this.worker.addEventListener('error', (e) => {
                    console.error(e);
                });
            }
            catch {
                // failed to create worker, fallback to main thread
            }
        }
    }
    onWorkerMessage(e) {
        const output = e.data;
        const promise = this.promises[output.id];
        if (output.state === 'error') {
            promise.reject(output.error);
        }
        else {
            promise.resolve(output);
        }
    }
    minimizeCanvasMemoryUsage(canvas) {
        canvas.height = 0;
        canvas.width = 0;
    }
    isBrowserSupportsImageOrientation() {
        const initialValue = getComputedStyle(document.body).imageOrientation;
        return initialValue === 'from-image';
    }
    /**
     * Create a thumbnail for the given Uppy file object.
     */
    async createThumbnail(file, targetWidth, targetHeight) {
        if (this.worker && file.data.type !== 'image/svg+xml') {
            try {
                const workerInput = { file: file.data, id: file.id };
                this.worker.postMessage(workerInput);
                return new Promise((resolve, reject) => {
                    this.promises[file.id] = { resolve, reject };
                }).then((data) => {
                    return {
                        preview: data.url,
                        width: data.width,
                        height: data.height,
                    };
                });
            }
            catch {
                // failed to generate in worker, fallback to main thread
            }
        }
        const originalUrl = URL.createObjectURL(file.data);
        try {
            const orientationPromise = tt.rotation(file.data).catch(() => ({ deg: 0 }));
            const [image, orientation] = await Promise.all([loadImage(originalUrl), orientationPromise]);
            const { width, height } = image;
            if (Math.max(width, height) > MAX_IMAGE_AREA) {
                return {
                    preview: undefined,
                    width,
                    height,
                };
            }
            const dimensions = this.getProportionalDimensions(image, targetWidth, targetHeight, orientation);
            const allowTransparency = this.thumbnailType === 'image/png';
            const originalAspectRatio = image.width / image.height;
            const correctAspectRatio = dimensions.width / dimensions.height;
            const isImageDataRotated = Math.abs(1 - originalAspectRatio / correctAspectRatio) > 0.01 &&
                !this.isBrowserSupportsImageOrientation();
            const resizedImage = isImageDataRotated
                ? this.resizeImage(image, dimensions.height, dimensions.width, allowTransparency)
                : this.resizeImage(image, dimensions.width, dimensions.height, allowTransparency);
            const rotatedImage = this.isBrowserSupportsImageOrientation()
                ? resizedImage
                : this.rotateImage(resizedImage, orientation, allowTransparency);
            URL.revokeObjectURL(originalUrl);
            try {
                const blob = await new Promise((resolve, reject) => {
                    rotatedImage.toBlob((blob) => {
                        if (!blob) {
                            return reject(new Error('Could not create thumbnail'));
                        }
                        resolve(blob);
                    }, this.thumbnailType);
                });
                this.minimizeCanvasMemoryUsage(resizedImage);
                this.minimizeCanvasMemoryUsage(rotatedImage);
                return {
                    preview: URL.createObjectURL(blob),
                    width,
                    height,
                };
            }
            catch (err) {
                console.error(err);
                return {
                    preview: undefined,
                    width,
                    height,
                };
            }
        }
        catch (err) {
            console.error(err);
            URL.revokeObjectURL(originalUrl);
            throw err;
        }
    }
    /**
     * Get the new calculated dimensions for the given image and a target width
     * or height. If both width and height are given, only width is taken into
     * account. If neither width nor height are given, the default dimension
     * is used.
     */
    getProportionalDimensions(img, width, height, rotation) {
        let aspect = img.width / img.height;
        if ((rotation.deg === 90 || rotation.deg === 270) &&
            !this.isBrowserSupportsImageOrientation()) {
            aspect = img.height / img.width;
        }
        if (width != null) {
            return {
                width: width,
                height: Math.round(width / aspect),
            };
        }
        if (height != null) {
            return {
                width: Math.round(height * aspect),
                height: height,
            };
        }
        return {
            width: this.defaultThumbnailDimension,
            height: Math.round(this.defaultThumbnailDimension / aspect),
        };
    }
    /**
     * Resize an image to the target `width` and `height`.
     *
     * Returns a Canvas with the resized image on it.
     */
    resizeImage(image, targetWidth, targetHeight, allowTransparency) {
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const context = canvas.getContext('2d');
        if (!allowTransparency) {
            context.fillStyle = 'white';
            context.fillRect(0, 0, canvas.width, canvas.height);
        }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        return canvas;
    }
    rotateImage(image, orientation, allowTransparency) {
        if (orientation.deg === 0) {
            return image;
        }
        let w = image.width;
        let h = image.height;
        if (orientation.deg === 90 || orientation.deg === 270) {
            w = image.height;
            h = image.width;
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const context = canvas.getContext('2d');
        if (!allowTransparency) {
            context.fillStyle = 'white';
            context.fillRect(0, 0, canvas.width, canvas.height);
        }
        context.translate(w / 2, h / 2);
        if (orientation.canvas) {
            context.rotate(orientation.rad);
            context.scale(orientation.scaleX, orientation.scaleY);
        }
        context.drawImage(image, -image.width / 2, -image.height / 2, image.width, image.height);
        return canvas;
    }
    addToQueue(item) {
        this.queue.push(item);
        if (this.queueProcessing === false) {
            this.processQueue();
        }
    }
    processQueue() {
        this.queueProcessing = true;
        if (this.queue.length > 0) {
            const current = this.uppy.getFile(this.queue.shift());
            if (!current) {
                this.uppy.log('[ThumbnailGenerator] file was removed before a thumbnail could be generated, but not removed from the queue. This is probably a bug', 'error');
                return;
            }
            return this.requestThumbnail(current)
                .catch((err) => {
                console.error(err);
            })
                .then(() => this.processQueue());
        }
        else {
            this.queueProcessing = false;
            this.uppy.log('[ThumbnailGenerator] Emptied thumbnail queue');
            this.uppy.emit('thumbnail:all-generated');
        }
    }
    requestThumbnail(file) {
        if (supportedPreviewTypes.has(file.type) && !file.isRemote) {
            return this.createThumbnail(file, this.opts.thumbnailWidth, this.opts.thumbnailHeight)
                .then(({ preview, width, height }) => {
                this.uppy.setFileState(file.id, { preview, width, height });
                if (!preview) {
                    return this.uppy.emit('thumbnail:generatedEmpty', this.uppy.getFile(file.id));
                }
                this.uppy.log(`[ThumbnailGenerator] Generated thumbnail for ${file.id}`);
                this.uppy.emit('thumbnail:generated', this.uppy.getFile(file.id));
            })
                .catch((err) => {
                this.uppy.log(`[ThumbnailGenerator] Failed thumbnail for ${file.id}:`, 'warning');
                this.uppy.log(err, 'warning');
                this.uppy.emit('thumbnail:error', this.uppy.getFile(file.id), err);
            });
        }
        return Promise.resolve();
    }
    onFileAdded(file) {
        if (!file.preview && supportedPreviewTypes.has(file.type) && !file.isRemote) {
            this.addToQueue(file.id);
        }
    }
    /**
     * Cancel a lazy request for a thumbnail if the thumbnail has not yet been generated.
     */
    onCancelRequest(file) {
        const index = this.queue.indexOf(file.id);
        if (index !== -1) {
            this.queue.splice(index, 1);
        }
    }
    /**
     * Clean up the thumbnail for a file. Cancel lazy requests and free the thumbnail URL.
     */
    onFileRemoved(file) {
        const index = this.queue.indexOf(file.id);
        if (index !== -1) {
            this.queue.splice(index, 1);
        }
        // Clean up object URLs.
        if (this.clearMemoryAfterUpload && file.preview && isObjectURL(file.preview)) {
            URL.revokeObjectURL(file.preview);
        }
    }
    onRestored() {
        const { files } = this.uppy.getState();
        const fileIDs = Object.keys(files);
        fileIDs.forEach((fileID) => {
            const file = this.uppy.getFile(fileID);
            if (!file.isRestored)
                return;
            // Only add blob URLs; they are likely invalid after being restored.
            if (!file.preview || isObjectURL(file.preview)) {
                this.addToQueue(file.id);
            }
        });
    }
    waitUntilAllProcessed(fileIDs) {
        fileIDs.forEach((fileID) => {
            const file = this.uppy.getFile(fileID);
            this.uppy?.i18n;
            this.uppy.emit('preprocess-progress', file, {
                mode: 'indeterminate',
                message: 'Generating thumbnails...',
            });
        });
        const emitPreprocessCompleteForAll = () => {
            fileIDs.forEach((fileID) => {
                const file = this.uppy.getFile(fileID);
                this.uppy.emit('preprocess-complete', file);
            });
        };
        return new Promise((resolve) => {
            if (this.queueProcessing) {
                this.uppy.once('thumbnail:all-generated', () => {
                    emitPreprocessCompleteForAll();
                    resolve();
                });
            }
            else {
                emitPreprocessCompleteForAll();
                resolve();
            }
        });
    }
    install() {
        this.uppy.on('file-removed', this.onFileRemovedBinded);
        if (this.opts.lazy) {
            this.uppy.on('thumbnail:request', this.onFileAddedBinded);
            this.uppy.on('thumbnail:cancel', this.onCancelRequestBinded);
        }
        else {
            this.uppy.on('file-added', this.onFileAddedBinded);
            this.uppy.on('restored', this.onRestoredBinded);
        }
        if (this.opts.waitForThumbnailsBeforeUpload) {
            this.uppy.addPreProcessor(this.waitUntilAllProcessedBinded);
        }
    }
    uninstall() {
        this.uppy.off('file-removed', this.onFileRemovedBinded);
        if (this.opts.lazy) {
            this.uppy.off('thumbnail:request', this.onFileAddedBinded);
            this.uppy.off('thumbnail:cancel', this.onCancelRequestBinded);
        }
        else {
            this.uppy.off('file-added', this.onFileAddedBinded);
            this.uppy.off('restored', this.onRestoredBinded);
        }
        if (this.opts.waitForThumbnailsBeforeUpload) {
            this.uppy.removePreProcessor(this.waitUntilAllProcessedBinded);
        }
    }
}

// /** @jsx h */
class Shutterstock extends Plugin {
    constructor(uppy, options) {
        super(uppy, options);
        this.options = options;
        this.id = options.id ?? 'Shutterstock';
        this.openShutterstock = options.openShutterstock;
        Provider.initPlugin(this, options);
        this.title = options.title ?? 'Shutterstock';
        this.icon = () => (h$1("svg", { width: "18", height: "21", viewBox: "0 0 18 21", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
            h$1("path", { d: "M10.8517 4.58317H6.30022C5.57721 4.58317 4.98929 5.1834 4.98929 5.91918V10.9607H0.5V5.91918C0.499899 5.14164 0.649884 4.37169 0.941364 3.65334C1.23284 2.935 1.66012 2.28233 2.19877 1.73263C2.73742 1.18293 3.37689 0.746978 4.08064 0.449686C4.78438 0.152395 5.53861 -0.000410957 6.30022 8.30062e-07H10.8517V4.58317ZM7.14758 16.416H11.7021C12.0491 16.415 12.3816 16.2737 12.6269 16.023C12.8721 15.7724 13.0101 15.4328 13.0107 15.0785V10.0393H17.5V15.0793C17.5 16.6493 16.8892 18.155 15.8019 19.2653C14.7147 20.3756 13.2399 20.9996 11.7021 21H7.14758V16.416Z", fill: "#EE3124" })));
        this.render = this.render.bind(this);
        this.install = this.install.bind(this);
    }
    install() {
        const target = this.options.target;
        if (target) {
            this.mount(target, this);
        }
    }
    render() {
        this.openShutterstock();
    }
}

var de_DE = {};
de_DE.strings = {
  addMoreFiles: 'Dateien hinzufügen',
  addingMoreFiles: 'Dateien hinzufügen',
  allowAccessDescription: 'Um Bilder oder Videos mit Ihrer Kamera aufzunehmen, erlauben Sie dieser Website bitte Zugriff auf Ihre Kamera.',
  allowAccessTitle: 'Bitte erlauben Sie Zugriff auf Ihre Kamera',
  authenticateWith: 'Mit %{pluginName} verbinden',
  authenticateWithTitle: 'Bitte authentifizieren Sie sich mit %{pluginName}, um Dateien auszuwählen',
  back: 'Zurück',
  addMore: 'Dateien hinzufügen',
  browse: 'Suche',
  browseFiles: 'Suche',
  cancel: 'Abbrechen',
  cancelUpload: 'Upload abbrechen',
  chooseFiles: 'Dateien auswählen',
  closeModal: 'Modal schließen',
  companionError: 'Verbindung zu Companion fehlgeschlagen',
  complete: 'Fertig',
  connectedToInternet: 'Verbunden mit dem Internet',
  copyLink: 'Link kopieren',
  copyLinkToClipboardFallback: 'Untenstehenden URL kopieren',
  copyLinkToClipboardSuccess: 'Link in die Zwischenablage kopiert',
  creatingAssembly: 'Upload vorbereiten...',
  creatingAssemblyFailed: 'Transloadit: Konnte Assembly nicht erstellen',
  dashboardTitle: 'Datei Uploader',
  dashboardWindowTitle: 'Datei Uploader Fenster (Esc drücken zum Schließen)',
  dataUploadedOfTotal: '%{complete} von %{total}',
  done: 'Abgeschlossen',
  dropHereOr: 'Dateien können über Drag/Drop oder per %{browse} hinzugefügt werden',
  dropHint: 'Dateien können über Drag/Drop hinzugefügt werden',
  dropPasteBoth: 'Dateien können über Drag/Drop, Einfügen oder per %{browse} hinzugefügt werden',
  dropPasteFiles: 'Dateien können über Drag/Drop, Einfügen oder per %{browse} hinzugefügt werden',
  dropPasteFolders: 'Dateien können über Drag/Drop, Einfügen oder per %{browse} hinzugefügt werden',
  dropPasteImportBoth: 'Dateien können über Drag/Drop, Einfügen, per %{browse} oder von folgenden Services hinzugefügt werden',
  dropPasteImportFiles: 'Dateien können über Drag/Drop, Einfügen, per %{browse} oder von folgenden Services hinzugefügt werden',
  dropPasteImportFolders: 'Dateien können über Drag/Drop, Einfügen, per %{browse} oder von folgenden Services hinzugefügt werden',
  editFile: 'Datei bearbeiten',
  editing: '%{file} bearbeiten',
  emptyFolderAdded: 'Keine Dateien konnten hinzugefügt werden, da der Ordner leer war',
  encoding: 'Enkodieren...',
  enterCorrectUrl: 'Falsche URL: Bitte stellen Sie sicher, dass Sie einen direkten Link zu einer Datei eingeben',
  enterUrlToImport: 'URL zum Importieren einer Datei eingeben',
  exceedsSize: 'Diese Datei ist größer als die maximal erlaubte Dateigröße',
  failedToFetch: 'Companion konnte diese URL nicht verarbeiten - stellen Sie bitte sicher, dass sie korrekt ist',
  failedToUpload: 'Fehler beim Upload von Datei %{file}',
  fileSource: 'Dateiquelle: %{name}',
  filesUploadedOfTotal: {
    '0': '%{complete} von %{smart_count} Datei hochgeladen',
    '1': '%{complete} von %{smart_count} Dateien hochgeladen',
    '2': '%{complete} von %{smart_count} Dateien hochgeladen'
  },
  filter: 'Filter',
  finishEditingFile: 'Dateibearbeitung beenden',
  folderAdded: {
    '0': '%{smart_count} Datei von %{folder} hinzugefügt',
    '1': '%{smart_count} Dateien von %{folder} hinzugefügt',
    '2': '%{smart_count} Dateien von %{folder} hinzugefügt'
  },
  import: 'Import',
  importFrom: 'Importieren von %{name}',
  loading: 'Laden...',
  logOut: 'Ausloggen',
  myDevice: 'Mein Gerät',
  noCameraDescription: 'Bitte Kamera anschließen, um Bilder oder Videos aufzunehmen',
  noCameraTitle: 'Kamera nicht verfügbar',
  noDuplicates: 'Datei \'%{fileName}\' existiert bereits und kann nicht erneut hinzugefügt werden',
  noFilesFound: 'Sie haben keine Dateien oder Ordner hier',
  noInternetConnection: 'Keine Internetverbindung',
  noNewAlreadyUploading: 'Während der Upload läuft, können keine weiteren Dateien hinzugefügt werden',
  pause: 'Pausieren',
  pauseUpload: 'Upload pausieren',
  paused: 'Pausiert',
  poweredBy: 'Angetrieben von',
  processingXFiles: {
    '0': '%{smart_count} Datei verarbeiten',
    '1': '%{smart_count} Dateien verarbeiten',
    '2': '%{smart_count} Dateien verarbeiten'
  },
  recordingLength: 'Aufnahmedauer %{recording_length}',
  removeFile: 'Datei entfernen',
  resetFilter: 'Filter zurücksetzen',
  resume: 'Fortsetzen',
  resumeUpload: 'Upload fortsetzen',
  retry: 'Erneut versuchen',
  retryUpload: 'Upload erneut versuchen',
  saveChanges: 'Änderungen speichern',
  selectX: {
    '0': 'Wählen Sie %{smart_count}',
    '1': 'Wählen Sie %{smart_count}',
    '2': 'Wählen Sie %{smart_count}'
  },
  smile: 'Bitte lächeln!',
  startRecording: 'Videoaufnahme starten',
  stopRecording: 'Videoaufnahme beenden',
  takePicture: 'Ein Foto machen',
  timedOut: 'Upload für %{seconds} Sekunden stehen geblieben, breche ab.',
  upload: 'Upload',
  uploadComplete: 'Upload beendet',
  uploadFailed: 'Upload fehlgeschlagen',
  uploadPaused: 'Upload pausiert',
  uploadXFiles: {
    '0': '%{smart_count} Datei hochladen',
    '1': '%{smart_count} Dateien hochladen',
    '2': '%{smart_count} Dateien hochladen'
  },
  uploadXNewFiles: {
    '0': '+%{smart_count} Datei hochladen',
    '1': '+%{smart_count} Dateien hochladen',
    '2': '+%{smart_count} Dateien hochladen'
  },
  uploading: 'Wird hochgeladen',
  uploadingXFiles: {
    '0': '%{smart_count} Datei wird hochgeladen',
    '1': '%{smart_count} Dateien werden hochgeladen',
    '2': '%{smart_count} Dateien werden hochgeladen'
  },
  xFilesSelected: {
    '0': '%{smart_count} Datei ausgewählt',
    '1': '%{smart_count} Dateien ausgewählt',
    '2': '%{smart_count} Dateien ausgewählt'
  },
  xMoreFilesAdded: {
    '0': '%{smart_count} weitere Datei hinzugefügt',
    '1': '%{smart_count} weitere Dateien hinzugefügt',
    '2': '%{smart_count} weitere Dateien hinzugefügt'
  },
  xTimeLeft: '%{time} verbleibend',
  youCanOnlyUploadFileTypes: 'Sie können nur folgende Dateitypen hochladen: %{types}',
  youCanOnlyUploadX: {
    '0': 'Sie können nur %{smart_count} Datei hochladen',
    '1': 'Sie können nur %{smart_count} Dateien hochladen',
    '2': 'Sie können nur %{smart_count} Dateien hochladen'
  },
  youHaveToAtLeastSelectX: {
    '0': 'Sie müssen mindestens %{smart_count} Datei auswählen',
    '1': 'Sie müssen mindestens %{smart_count} Dateien auswählen',
    '2': 'Sie müssen mindestens %{smart_count} Dateien auswählen'
  },
  selectAllFilesFromFolderNamed: 'Wählen Sie alle Dateien aus dem Ordner %{name}',
  unselectAllFilesFromFolderNamed: 'Heben Sie die Auswahl aller Dateien aus dem Ordner auf %{name}',
  selectFileNamed: 'Datei aussuchen %{name}',
  unselectFileNamed: 'Datei abwählen %{name}',
  openFolderNamed: 'Ordner öffnen %{name}'
};

de_DE.pluralize = function (n) {
  if (n === 1) {
    return 0;
  }

  return 1;
};

if (typeof window !== 'undefined' && typeof window.Uppy !== 'undefined') {
  window.Uppy.locales.de_DE = de_DE;
}

var de_DE_1 = de_DE;

var en_US = {};
en_US.strings = {
  addBulkFilesFailed: {
    '0': 'Failed to add %{smart_count} file due to an internal error',
    '1': 'Failed to add %{smart_count} files due to internal errors'
  },
  addMore: 'Add more',
  addMoreFiles: 'Add more files',
  addingMoreFiles: 'Adding more files',
  allowAccessDescription: 'In order to take pictures or record video with your camera, please allow camera access for this site.',
  allowAccessTitle: 'Please allow access to your camera',
  aspectRatioLandscape: 'Crop landscape (16:9)',
  aspectRatioPortrait: 'Crop portrait (9:16)',
  aspectRatioSquare: 'Crop square',
  authenticateWith: 'Connect to %{pluginName}',
  authenticateWithTitle: 'Please authenticate with %{pluginName} to select files',
  back: 'Back',
  backToSearch: 'Back to Search',
  browse: 'browse',
  browseFiles: 'browse files',
  browseFolders: 'browse folders',
  cancel: 'Cancel',
  cancelUpload: 'Cancel upload',
  chooseFiles: 'Choose files',
  closeModal: 'Close Modal',
  companionError: 'Connection with Companion failed',
  companionUnauthorizeHint: 'To unauthorize to your %{provider} account, please go to %{url}',
  complete: 'Complete',
  connectedToInternet: 'Connected to the Internet',
  copyLink: 'Copy link',
  copyLinkToClipboardFallback: 'Copy the URL below',
  copyLinkToClipboardSuccess: 'Link copied to clipboard',
  creatingAssembly: 'Preparing upload...',
  creatingAssemblyFailed: 'Transloadit: Could not create Assembly',
  dashboardTitle: 'File Uploader',
  dashboardWindowTitle: 'File Uploader Window (Press escape to close)',
  dataUploadedOfTotal: '%{complete} of %{total}',
  discardRecordedFile: 'Discard recorded file',
  done: 'Done',
  dropHereOr: 'Drop files here or %{browse}',
  dropHint: 'Drop your files here',
  dropPasteBoth: 'Drop files here, %{browseFiles} or %{browseFolders}',
  dropPasteFiles: 'Drop files here or %{browseFiles}',
  dropPasteFolders: 'Drop files here or %{browseFolders}',
  dropPasteImportBoth: 'Drop files here, %{browseFiles}, %{browseFolders} or import from:',
  dropPasteImportFiles: 'Drop files here, %{browseFiles} or import from:',
  dropPasteImportFolders: 'Drop files here, %{browseFolders} or import from:',
  editFile: 'Edit file',
  editing: 'Editing %{file}',
  emptyFolderAdded: 'No files were added from empty folder',
  encoding: 'Encoding...',
  enterCorrectUrl: 'Incorrect URL: Please make sure you are entering a direct link to a file',
  enterTextToSearch: 'Enter text to search for images',
  enterUrlToImport: 'Enter URL to import a file',
  exceedsSize: '%{file} exceeds maximum allowed size of',
  exceedsSize2: '%{backwardsCompat} %{size}',
  failedToFetch: 'Companion failed to fetch this URL, please make sure it’s correct',
  failedToUpload: 'Failed to upload %{file}',
  fileSource: 'File source: %{name}',
  filesUploadedOfTotal: {
    '0': '%{complete} of %{smart_count} file uploaded',
    '1': '%{complete} of %{smart_count} files uploaded'
  },
  filter: 'Filter',
  finishEditingFile: 'Finish editing file',
  flipHorizontal: 'Flip horizontal',
  folderAdded: {
    '0': 'Added %{smart_count} file from %{folder}',
    '1': 'Added %{smart_count} files from %{folder}'
  },
  generatingThumbnails: 'Generating thumbnails...',
  import: 'Import',
  importFiles: 'Import files from:',
  importFrom: 'Import from %{name}',
  inferiorSize: 'This file is smaller than the allowed size of %{size}',
  loading: 'Loading...',
  logOut: 'Log out',
  micDisabled: 'Microphone access denied by user',
  missingRequiredMetaField: 'Missing required meta fields',
  missingRequiredMetaFieldOnFile: 'Missing required meta fields in %{fileName}',
  myDevice: 'My Device',
  noCameraDescription: 'In order to take pictures or record video, please connect a camera device',
  noCameraTitle: 'Camera Not Available',
  noDuplicates: 'Cannot add the duplicate file \'%{fileName}\', it already exists',
  noFilesFound: 'You have no files or folders here',
  noInternetConnection: 'No Internet connection',
  noNewAlreadyUploading: 'Cannot add new files: already uploading',
  openFolderNamed: 'Open folder %{name}',
  pause: 'Pause',
  pauseUpload: 'Pause upload',
  paused: 'Paused',
  poweredBy: 'Powered by',
  poweredBy2: '%{backwardsCompat} %{uppy}',
  processingXFiles: {
    '0': 'Processing %{smart_count} file',
    '1': 'Processing %{smart_count} files'
  },
  reSelect: 'Re-select',
  recording: 'Recording',
  recordingLength: 'Recording length %{recording_length}',
  recordingStoppedMaxSize: 'Recording stopped because the file size is about to exceed the limit',
  recoveredAllFiles: 'We restored all files. You can now resume the upload.',
  recoveredXFiles: {
    '0': 'We could not fully recover 1 file. Please re-select it and resume the upload.',
    '1': 'We could not fully recover %{smart_count} files. Please re-select them and resume the upload.'
  },
  removeFile: 'Remove file',
  resetFilter: 'Reset filter',
  resume: 'Resume',
  resumeUpload: 'Resume upload',
  retry: 'Retry',
  retryUpload: 'Retry upload',
  revert: 'Revert',
  rotate: 'Rotate',
  save: 'Save',
  saveChanges: 'Save changes',
  searchImages: 'Search for images',
  selectAllFilesFromFolderNamed: 'Select all files from folder %{name}',
  selectFileNamed: 'Select file %{name}',
  selectX: {
    '0': 'Select %{smart_count}',
    '1': 'Select %{smart_count}'
  },
  sessionRestored: 'Session restored',
  smile: 'Smile!',
  startCapturing: 'Begin screen capturing',
  startRecording: 'Begin video recording',
  stopCapturing: 'Stop screen capturing',
  stopRecording: 'Stop video recording',
  streamActive: 'Stream active',
  streamPassive: 'Stream passive',
  submitRecordedFile: 'Submit recorded file',
  takePicture: 'Take a picture',
  timedOut: 'Upload stalled for %{seconds} seconds, aborting.',
  unselectAllFilesFromFolderNamed: 'Unselect all files from folder %{name}',
  unselectFileNamed: 'Unselect file %{name}',
  upload: 'Upload',
  uploadComplete: 'Upload complete',
  uploadFailed: 'Upload failed',
  uploadPaused: 'Upload paused',
  uploadXFiles: {
    '0': 'Upload %{smart_count} file',
    '1': 'Upload %{smart_count} files'
  },
  uploadXNewFiles: {
    '0': 'Upload +%{smart_count} file',
    '1': 'Upload +%{smart_count} files'
  },
  uploading: 'Uploading',
  uploadingXFiles: {
    '0': 'Uploading %{smart_count} file',
    '1': 'Uploading %{smart_count} files'
  },
  xFilesSelected: {
    '0': '%{smart_count} file selected',
    '1': '%{smart_count} files selected'
  },
  xMoreFilesAdded: {
    '0': '%{smart_count} more file added',
    '1': '%{smart_count} more files added'
  },
  xTimeLeft: '%{time} left',
  youCanOnlyUploadFileTypes: 'You can only upload: %{types}',
  youCanOnlyUploadX: {
    '0': 'You can only upload %{smart_count} file',
    '1': 'You can only upload %{smart_count} files'
  },
  youHaveToAtLeastSelectX: {
    '0': 'You have to select at least %{smart_count} file',
    '1': 'You have to select at least %{smart_count} files'
  },
  zoomIn: 'Zoom in',
  zoomOut: 'Zoom out'
};

en_US.pluralize = function (count) {
  if (count === 1) {
    return 0;
  }

  return 1;
};

if (typeof window !== 'undefined' && typeof window.Uppy !== 'undefined') {
  window.Uppy.locales.en_US = en_US;
}

var en_US_1 = en_US;

var fr_FR = {};
fr_FR.strings = {
  addBulkFilesFailed: {
    '0': 'L\'ajout de %{smart_count} fichier a échoué',
    '1': 'L\'ajout de %{smart_count} fichiers a échoué'
  },
  addMore: 'Ajouter d\'autres',
  addMoreFiles: 'Ajouter d\'autres fichiers',
  addingMoreFiles: 'En train d\'ajouter des fichiers',
  allowAccessDescription: 'Pour prendre des photos ou enregistrer une vidéo avec votre caméra, veuillez autoriser l\'accès à votre caméra pour ce site.',
  allowAccessTitle: 'Veuillez autoriser l\'accès à votre caméra',
  authenticateWith: 'Se connecter à %{pluginName}',
  authenticateWithTitle: 'Veuillez vous authentifier avec %{pluginName} pour sélectionner les fichiers',
  back: 'Retour',
  browse: 'naviguer',
  browseFiles: 'naviguer',
  cancel: 'Annuler',
  cancelUpload: 'Annuler téléchargement',
  chooseFiles: 'Choisir des fichiers',
  closeModal: 'Fermer Fenêtre',
  companionError: 'Connexion à Companion a échoué',
  companionUnauthorizeHint: 'Pour vous déconnecter de votre compte %{provider}, veuillez aller à %{url}',
  complete: 'Terminé',
  connectedToInternet: 'Connecté à Internet',
  copyLink: 'Copier lien',
  copyLinkToClipboardFallback: 'Copier le lien ci-dessous',
  copyLinkToClipboardSuccess: 'Lien copié',
  creatingAssembly: 'Préparation du téléchargement...',
  creatingAssemblyFailed: 'Transloadit: Impossible de créer Assembly',
  dashboardTitle: 'Téléchargeur de fichier',
  dashboardWindowTitle: 'Fenêtre de téléchargeur de fichier (Appuyez sur echap pour fermer)',
  dataUploadedOfTotal: '%{complete} sur %{total}',
  done: 'Terminé',
  dropHereOr: 'Déposer les fichiers ici ou %{browse}',
  dropHint: 'Déposez vos fichiers ici',
  dropPasteBoth: 'Déposer les fichiers ici, coller ou %{browse}',
  dropPasteFiles: 'Déposer les fichiers ici, coller ou %{browse}',
  dropPasteFolders: 'Déposer les fichiers ici, coller ou %{browse}',
  dropPasteImportBoth: 'Déposer les fichiers ici, coller, %{browse} ou importer de',
  dropPasteImportFiles: 'Déposer les fichiers ici, coller, %{browse} ou importer de',
  dropPasteImportFolders: 'Déposer les fichiers ici, coller, %{browse} ou importer de',
  editFile: 'Modifier fichier',
  editing: 'Modification en cours de %{file}',
  emptyFolderAdded: 'Aucun fichier n\'a été ajouté depuis un dossier vide',
  encoding: 'Traitement...',
  enterCorrectUrl: 'Lien incorrect: Assurez-vous que vous entrez un lien direct vers le fichier',
  enterUrlToImport: 'Entrez le lien pour importer un fichier',
  exceedsSize: 'Ce fichier dépasse la taille maximale autorisée de',
  failedToFetch: 'Companion a échoué à récupérer ce lien, assurez-vous qu\'il est correct',
  failedToUpload: 'Le téléchargement de %{file} a échoué',
  fileSource: 'Fichier source: %{name}',
  filesUploadedOfTotal: {
    '0': '%{complete} sur %{smart_count} fichier téléchargé',
    '1': '%{complete} sur %{smart_count} fichiers téléchargés',
    '2': '%{complete} sur %{smart_count} fichiers téléchargés'
  },
  filter: 'Filtrer',
  finishEditingFile: 'Terminer l\'édition du fichier',
  folderAdded: {
    '0': '%{smart_count} fichier ajouté de %{folder}',
    '1': '%{smart_count} fichiers ajoutés de %{folder}',
    '2': '%{smart_count} fichiers ajoutés de %{folder}'
  },
  generatingThumbnails: 'Generating thumbnails...',
  import: 'Importer',
  importFrom: 'Importer de %{name}',
  loading: 'Chargement...',
  logOut: 'Déconnexion',
  micDisabled: 'Accès au micro refusé par l\'utilisateur',
  myDevice: 'Mon Appareil',
  noDuplicates: 'Impossible d\'ajouter le fichier "%{fileName}", il existe déjà',
  noFilesFound: 'Vous n\'avez aucun fichier ou dossier ici',
  noInternetConnection: 'Pas de connexion à Internet',
  noNewAlreadyUploading: 'Impossible d\'ajouter de nouveaux fichiers: en cours de chargement ',
  openFolderNamed: 'Ouvrir %{name}',
  pause: 'Pause',
  pauseUpload: 'Mettre en pause le téléchargement',
  paused: 'En pause',
  poweredBy: 'Propulsé par',
  processingXFiles: {
    '0': 'Traitement de %{smart_count} fichier',
    '1': 'Traitement de %{smart_count} fichiers',
    '2': 'Traitement de %{smart_count} fichiers'
  },
  recording: 'Enregistrement',
  recordingLength: 'Durée d\'enregistrement %{recording_length}',
  recordingStoppedMaxSize: 'L\'enregistrement s\'est arrété car la taille du fichier dépasse la limite',
  removeFile: 'Effacer le fichier',
  resetFilter: 'Réinitialiser filtre',
  resume: 'Reprendre',
  resumeUpload: 'Reprendre le téléchargement',
  retry: 'Réessayer',
  retryUpload: 'Réessayer le téléchargement',
  saveChanges: 'Sauvegarder les modifications',
  selectAllFilesFromFolderNamed: 'Sélectionner tous les fichiers du dossier %{name}',
  selectFileNamed: 'Sélectionner le fichier %{name}',
  selectX: {
    '0': 'Sélectionner %{smart_count}',
    '1': 'Sélectionner %{smart_count}',
    '2': 'Sélectionner %{smart_count}'
  },
  smile: 'Souriez !',
  startRecording: 'Commencer l\'enregistrement vidéo',
  stopRecording: 'Arrêter l\'enregistrement vidéo',
  streamActive: 'Stream actif',
  streamPassive: 'Stream passif',
  submitRecordedFile: 'Envoyer la vidéo enregistrée',
  takePicture: 'Prendre une photo',
  timedOut: 'Téléchargement bloqué durant %{seconds} secondes, annulation.',
  unselectAllFilesFromFolderNamed: 'Désélectionner tous les fichiers du dossier %{name}',
  unselectFileNamed: 'Désélectionner le fichier %{name}',
  upload: 'Télécharger',
  uploadComplete: 'Téléchargement terminé',
  uploadFailed: 'Le téléchargement a échoué',
  uploadPaused: 'Téléchargement mis en pause',
  uploadXFiles: {
    '0': 'Télécharger %{smart_count} fichier',
    '1': 'Télécharger %{smart_count} fichiers',
    '2': 'Télécharger %{smart_count} fichiers'
  },
  uploadXNewFiles: {
    '0': 'Télécharger +%{smart_count} fichier',
    '1': 'Télécharger +%{smart_count} fichiers',
    '2': 'Télécharger +%{smart_count} fichiers'
  },
  uploading: 'Téléchargement en cours',
  uploadingXFiles: {
    '0': 'Téléchargement de %{smart_count} fichier',
    '1': 'Téléchargement de %{smart_count} fichiers',
    '2': 'Téléchargement de %{smart_count} fichiers'
  },
  xFilesSelected: {
    '0': '%{smart_count} fichier sélectionné',
    '1': '%{smart_count} fichiers sélectionnés',
    '2': '%{smart_count} fichiers sélectionnés'
  },
  xMoreFilesAdded: {
    '0': '%{smart_count} autre fichier ajouté',
    '1': '%{smart_count} autres fichiers ajoutés',
    '2': '%{smart_count} autres fichiers ajoutés'
  },
  xTimeLeft: '%{time} restantes',
  youCanOnlyUploadFileTypes: 'Vous pouvez seulement télécharger: %{types}',
  youCanOnlyUploadX: {
    '0': 'Vous pouvez seulement télécharger %{smart_count} fichier',
    '1': 'Vous pouvez seulement télécharger %{smart_count} fichiers',
    '2': 'Vous pouvez seulement télécharger %{smart_count} fichiers'
  },
  youHaveToAtLeastSelectX: {
    '0': 'Vous devez sélectionner au moins %{smart_count} fichier',
    '1': 'Vous devez sélectionner au moins %{smart_count} fichiers',
    '2': 'Vous devez sélectionner au moins %{smart_count} fichiers'
  }
};

fr_FR.pluralize = function (n) {
  if (n === 1) {
    return 0;
  }

  return 1;
};

if (typeof window !== 'undefined' && typeof window.Uppy !== 'undefined') {
  window.Uppy.locales.fr_FR = fr_FR;
}

var fr_FR_1 = fr_FR;

var nb_NO = {};
nb_NO.strings = {
  addBulkFilesFailed: {
    0: 'Kunne ikke legge til %{smart_count} fil på grunn av en intern feil',
    1: 'Kunne ikke legge til %{smart_count} filer på grunn av en intern feil'
  },
  addMore: 'Legg til',
  addMoreFiles: 'Legg til filer',
  addingMoreFiles: 'Legger til flere filer',
  allowAccessDescription: 'For å kunne ta bilder eller spille inn video må du gi siden tilgang til å bruke ditt kamera.',
  allowAccessTitle: 'Vennligst gi tilgang til ditt kamera',
  aspectRatioLandscape: 'Beskjær landskap (16:9)',
  aspectRatioPortrait: 'Beskjær portrett (9:16)',
  aspectRatioSquare: 'Beskjær kvadrat',
  authenticateWith: 'Koble til %{pluginName}',
  authenticateWithTitle: 'Koble til %{pluginName} for å velge filer',
  back: 'Tilbake',
  backToSearch: 'Tilbake til Søk',
  browse: 'velg',
  browseFiles: 'velg filer',
  browseFolders: 'velg mapper',
  cancel: 'Avbryt',
  cancelUpload: 'Avbryt opplasting',
  chooseFiles: 'Velg filer',
  closeModal: 'Lukk vindu',
  companionError: 'Kobling til Companion feilet',
  companionUnauthorizeHint: 'For å logge ut av din %{provider}-konto, gå til %{url}',
  complete: 'Ferdig',
  connectedToInternet: 'Koblet til internett',
  copyLink: 'Kopier lenke',
  copyLinkToClipboardFallback: 'Kopier URL under',
  copyLinkToClipboardSuccess: 'Lenke kopiert',
  creatingAssembly: 'Forbereder opplasting...',
  creatingAssemblyFailed: 'Transloadit: Kunne ikke opprette Assembly',
  dashboardTitle: 'Filopplaster',
  dashboardWindowTitle: 'Opplastingsvindu (Trykk Esc-knappen for å lukke)',
  dataUploadedOfTotal: '%{complete} av %{total}',
  dropHereOr: 'Dra filer hit eller %{browse}',
  dropHint: 'Dra filer hit',
  dropPasteBoth: 'Dra filer hit, %{browseFiles} eller %{browseFolders}',
  dropPasteFiles: 'Dra filer hit eller %{browseFiles}',
  dropPasteFolders: 'Dra filer hit eller %{browseFolders}',
  dropPasteImportBoth: 'Dra filer hit %{browseFiles}, %{browseFolders} eller importer fra:',
  dropPasteImportFiles: 'Dra filer hit %{browseFiles} eller importer fra:',
  dropPasteImportFolders: 'Dra filer hit %{browseFolders} eller importer fra:',
  editFile: 'Rediger fil',
  editing: 'Redigerer %{file}',
  emptyFolderAdded: 'Ingen filer ble lagt til fra tom mappe',
  encoding: 'Koder...',
  enterCorrectUrl: 'Ugyldig URL: Kontroller at adressen du angir er en direkte lenke til ei fil',
  enterTextToSearch: 'Skriv tekst for å søke etter bilder',
  enterUrlToImport: 'Angi URL for å importere fil',
  exceedsSize: 'Fila er større enn tillatt størrelse på',
  exceedsSize2: '%{backwardsCompat} %{size}',
  failedToFetch: 'Companion kunne ikke hente denne URLen, kontroller at den er riktig',
  failedToUpload: 'Opplasting feilet for %{file}',
  fileSource: 'Filkilde: %{name}',
  filesUploadedOfTotal: {
    0: '%{complete} av %{smart_count} fil opplastet',
    1: '%{complete} av %{smart_count} filer opplastet'
  },
  filter: 'Filtrer',
  finishEditingFile: 'Avslutt redigering av fil',
  flipHorizontal: 'Snu horisontalt',
  folderAdded: {
    0: 'La til %{smart_count} fil fra %{folder}',
    1: 'La til %{smart_count} filer fra %{folder}'
  },
  generatingThumbnails: 'Genererer miniatyrbilde...',
  import: 'Importer',
  importFrom: 'Importer fra %{name}',
  inferiorSize: 'Fila er mindre enn tillatt størrelse på %{size}',
  loading: 'Laster...',
  logOut: 'Logg ut',
  micDisabled: 'Mikrofontilgang nektet av bruker',
  myDevice: 'Min Enhet',
  noCameraDescription: 'Koble til kamera for å ta bilder eller ta opp video',
  noCameraTitle: 'Kamera ikke tilgjengelig',
  noDuplicates: 'Kan ikke legge til \'%{fileName}\', da den allerede eksisterer',
  noFilesFound: 'Du har ingen filer eller mapper her',
  noInternetConnection: 'Ingen internettilgang',
  noNewAlreadyUploading: 'Kan ikke legge til nye filer mens opplasting pågår',
  openFolderNamed: 'Åpne mappe %{name}',
  pause: 'Pause',
  pauseUpload: 'Stopp opplasting midlertidig',
  paused: 'Midlertidig stoppet',
  poweredBy: 'Drevet av',
  poweredBy2: '%{backwardsCompat} %{uppy}',
  processingXFiles: {
    0: 'Prosesserer %{smart_count} fil',
    1: 'Prosesserer %{smart_count} filer'
  },
  recording: 'Opptak pågår',
  recordingLength: 'Lengde på opptak: %{recording_length}',
  recordingStoppedMaxSize: 'Opptak stoppet fordi fila er i ferd med å overskride tillatt størrelse',
  removeFile: 'Fjern fil',
  resetFilter: 'Nullstill filter',
  resume: 'Fortsett',
  resumeUpload: 'Fortsett opplasting',
  retry: 'Prøv igjen',
  retryUpload: 'Prøv igjen',
  revert: 'Tilbakestill',
  rotate: 'Roter',
  save: 'Lagre',
  saveChanges: 'Lagre endringer',
  searchImages: 'Søk etter bilder',
  selectAllFilesFromFolderNamed: 'Velg alle filer fra mappen %{name}',
  selectFileNamed: 'Velg fil %{name}',
  selectX: {
    0: 'Velg %{smart_count}',
    1: 'Velg %{smart_count}'
  },
  smile: 'Smil!',
  startCapturing: 'Start skjermopptak',
  startRecording: 'Start videoopptak',
  stopCapturing: 'Stopp skjermopptak',
  stopRecording: 'Stopp videoopptak',
  streamActive: 'Strøm aktiv',
  streamPassive: 'Strøm passiv',
  submitRecordedFile: 'Send inn opptak',
  takePicture: 'Ta bilde',
  timedOut: 'Opplasting stoppet for %{seconds} sekunder, avbryter.',
  unselectAllFilesFromFolderNamed: 'Fjern markering for alle filer i mappen %{name}',
  unselectFileNamed: 'Fjern markering for %{name}',
  upload: 'Last opp',
  uploadComplete: 'Opplasting ferdig',
  uploadFailed: 'Opplasting feilet',
  uploadPaused: 'Opplasting midlertidig stoppet',
  uploadXFiles: {
    0: 'Last opp %{smart_count} fil',
    1: 'Last opp %{smart_count} filer'
  },
  uploadXNewFiles: {
    0: 'Last opp +%{smart_count} fil',
    1: 'Last opp +%{smart_count} filer'
  },
  uploading: 'Laster opp',
  uploadingXFiles: {
    0: 'Laster opp %{smart_count} fil',
    1: 'Laster opp %{smart_count} filer'
  },
  xFilesSelected: {
    0: '%{smart_count} fil valgt',
    1: '%{smart_count} filer valgt'
  },
  xMoreFilesAdded: {
    0: '%{smart_count} fil lagt til',
    1: '%{smart_count} filer lagt til'
  },
  xTimeLeft: '%{time} igjen',
  youCanOnlyUploadFileTypes: 'Du kan bare laste opp: %{types}',
  youCanOnlyUploadX: {
    0: 'Du kan bare laste opp %{smart_count} fil',
    1: 'Du kan bare laste opp %{smart_count} filer'
  },
  youHaveToAtLeastSelectX: {
    0: 'Du må velge minst %{smart_count} fil',
    1: 'Du må velge minst %{smart_count} filer'
  },
  zoomIn: 'Zoom inn',
  zoomOut: 'Zoom ut'
};

nb_NO.pluralize = function (count) {
  if (count === 1) {
    return 0;
  }

  return 1;
};

if (typeof window !== 'undefined' && typeof window.Uppy !== 'undefined') {
  window.Uppy.locales.nb_NO = nb_NO;
}

var nb_NO_1 = nb_NO;

var sv_SE = {};
sv_SE.strings = {
  addMore: 'Lägg till',
  addMoreFiles: 'Lägg till filer',
  addingMoreFiles: 'Lägger till fler filer',
  allowAccessDescription: 'För att kunna ta bilder eller spela in video behöver du ge sidan behörighet att använda din kamera.',
  allowAccessTitle: 'Tillåt användning av kameran',
  authenticateWith: 'Anslut till %{pluginName}',
  authenticateWithTitle: 'Anslut till %{pluginName} för att välja filer',
  back: 'Tillbaka',
  browse: 'bläddra',
  browseFiles: 'bläddra',
  cancel: 'Avbryt',
  cancelUpload: 'Avbryt uppladdning',
  chooseFiles: 'Välj filer',
  closeModal: 'Stäng fönster',
  companionError: 'Anslutning till Companion misslyckades',
  complete: 'Klart',
  connectedToInternet: 'Ansluten till internet',
  copyLink: 'Kopiera länk',
  copyLinkToClipboardFallback: 'Kopiera länken nedanför',
  copyLinkToClipboardSuccess: 'Länken kopierad till urklipp',
  creatingAssembly: 'Förbereder uppladdning...',
  creatingAssemblyFailed: 'Transloadit: Kunde inte skapa Assembly',
  dashboardTitle: 'Filuppladdare',
  dashboardWindowTitle: 'Uppladdningsfönster (Tryck på Esc-tangenten för att stänga)',
  dataUploadedOfTotal: '%{complete} av %{total}',
  done: 'Klart',
  dropHereOr: 'Släpp filer här eller %{browse}',
  dropHint: 'Släpp dina filer här',
  dropPasteBoth: 'Släpp filer här, klistra in eller %{browse}',
  dropPasteFiles: 'Släpp filer här, klistra in eller %{browse}',
  dropPasteFolders: 'Släpp filer här, klistra in eller %{browse}',
  dropPasteImportBoth: 'Släpp filer här, klistra in, %{browse} eller importera från',
  dropPasteImportFiles: 'Släpp filer här, klistra in, %{browse} eller importera från',
  dropPasteImportFolders: 'Släpp filer här, klistra in, %{browse} eller importera från',
  editFile: 'Redigera fil',
  editing: 'Redigerar %{file}',
  emptyFolderAdded: 'Inga filer lades till från en tom mapp',
  encoding: 'Kodar...',
  enterCorrectUrl: 'Ogiltig URL: Kontrollera att adressen du anger är en direktlänk till en fil.',
  enterUrlToImport: 'Ange URL för att importera en fil',
  exceedsSize: 'Storleken på filen överstiger den tillåtna maxgränsen på',
  failedToFetch: 'Companion kunde inte ladda ner filen, kontrollera att adressen är korrekt',
  failedToUpload: 'Kunde inte ladda upp %{file}',
  fileSource: 'Källa: %{name}',
  filesUploadedOfTotal: {
    '0': '%{complete} av %{smart_count} fil uppladdad',
    '1': '%{complete} av %{smart_count} filer uppladdade',
    '2': '%{complete} av %{smart_count} filer uppladdade'
  },
  filter: 'Filtrera',
  finishEditingFile: 'Avsluta redigering av filen',
  folderAdded: {
    '0': 'La till %{smart_count} fil från %{folder}',
    '1': 'La till %{smart_count} filer från %{folder}',
    '2': 'La till %{smart_count} filer från %{folder}'
  },
  import: 'Importera',
  importFrom: 'Importera från %{name}',
  loading: 'Laddar...',
  logOut: 'Logga ut',
  myDevice: 'Min enhet',
  noFilesFound: 'Du har inga filer eller mappar här',
  noInternetConnection: 'Ingen internetuppkoppling',
  openFolderNamed: 'Öppna mappen %{name}',
  pause: 'Pausa',
  pauseUpload: 'Pausa uppladdning',
  paused: 'Pausad',
  poweredBy: 'Drivs av',
  processingXFiles: {
    '0': 'Processerar %{smart_count} fil',
    '1': 'Processerar %{smart_count} filer',
    '2': 'Processerar %{smart_count} filer'
  },
  removeFile: 'Ta bort fil',
  resetFilter: 'Nollställ filter',
  resume: 'Återuppta',
  resumeUpload: 'Återuppta uppladdning',
  retry: 'Försök igen',
  retryUpload: 'Försök igen',
  saveChanges: 'Spara ändringar',
  selectAllFilesFromFolderNamed: 'Välj alla filer i mappen %{name}',
  selectFileNamed: 'Välj fil %{name}',
  selectX: {
    '0': 'Välj %{smart_count}',
    '1': 'Välj %{smart_count}',
    '2': 'Välj %{smart_count}'
  },
  smile: 'Säg omelett!',
  // translates to "Say cheese!" - which works well in this context in Swedish
  startRecording: 'Starta inspelning',
  stopRecording: 'Avbryt inspelning',
  takePicture: 'Ta bild',
  timedOut: 'Uppladdningen har stått stilla i %{seconds} sekunder; avbryter.',
  unselectAllFilesFromFolderNamed: 'Avmarkera alla filer i mappen %{name}',
  unselectFileNamed: 'Avmarkera filen %{name}',
  upload: 'Ladda upp',
  uploadComplete: 'Uppladdning slutförd',
  uploadFailed: 'Uppladdning misslyckad',
  uploadPaused: 'Uppladdning pausad',
  uploadXFiles: {
    '0': 'Ladda upp %{smart_count} fil',
    '1': 'Ladda upp %{smart_count} filer',
    '2': 'Ladda upp %{smart_count} filer'
  },
  uploadXNewFiles: {
    '0': 'Ladda upp %{smart_count} fil',
    '1': 'Ladda upp %{smart_count} filer',
    '2': 'Ladda upp %{smart_count} filer'
  },
  uploading: 'Laddar upp',
  uploadingXFiles: {
    '0': 'Ladda upp %{smart_count} fil',
    '1': 'Ladda upp %{smart_count} filer',
    '2': 'Ladda upp %{smart_count} filer'
  },
  xFilesSelected: {
    '0': '%{smart_count} fil vald',
    '1': '%{smart_count} filer valda',
    '2': '%{smart_count} filer valda'
  },
  xMoreFilesAdded: {
    '0': '%{smart_count} fil tillagd',
    '1': '%{smart_count} filer tillagda',
    '2': '%{smart_count} filer tillagda'
  },
  xTimeLeft: '%{time} återstår',
  youCanOnlyUploadFileTypes: 'Du kan endast ladda upp: %{types}',
  youCanOnlyUploadX: {
    '0': 'Du kan endast ladda upp %{smart_count} fil',
    '1': 'Du kan endast ladda upp %{smart_count} filer',
    '2': 'Du kan endast ladda upp %{smart_count} filer'
  },
  youHaveToAtLeastSelectX: {
    '0': 'Du måste välja minst %{smart_count} fil',
    '1': 'Du måste välja minst %{smart_count} filer',
    '2': 'Du måste välja minst %{smart_count} filer'
  }
};

sv_SE.pluralize = function (n) {
  if (n === 1) {
    return 0;
  }

  return 1;
};

if (typeof window !== 'undefined' && typeof window.Uppy !== 'undefined') {
  window.Uppy.locales.sv_SE = sv_SE;
}

var sv_SE_1 = sv_SE;

var es_ES = {};
es_ES.strings = {
  addMoreFiles: 'Agregar más archivos',
  addingMoreFiles: 'Agregando más archivos',
  allowAccessDescription: 'Para tomar fotos o grabar video con tu cámara, por favor permite a este sitio el acceso a la cámara.',
  allowAccessTitle: 'Por favor permite el acceso a tu cámara',
  authenticateWith: 'Conectar a %{pluginName}',
  authenticateWithTitle: 'Por favor autentícate con %{pluginName} para seleccionar archivos',
  back: 'Atrás',
  addMore: 'Agregar más',
  browse: 'navegar',
  browseFiles: 'navegar',
  cancel: 'Cancelar',
  cancelUpload: 'Cancelar subida',
  chooseFiles: 'Seleccionar archivos',
  closeModal: 'Cerrar ventana flotante',
  companionError: 'Conexión con Companion falló',
  complete: 'Completado',
  connectedToInternet: 'Conectado a Internet',
  copyLink: 'Copiar enlace',
  copyLinkToClipboardFallback: 'Copia la siguiente URL',
  copyLinkToClipboardSuccess: 'Enlace copiado al portapapeles',
  creatingAssembly: 'Preparando subida...',
  creatingAssemblyFailed: 'Transloadit: No se pudo crear un Assembly',
  dashboardTitle: 'Cargador de archivos',
  dashboardWindowTitle: 'Ventana para cargar archivos (Presiona escape para cerrar)',
  dataUploadedOfTotal: '%{complete} de %{total}',
  done: 'Hecho',
  dropHereOr: 'Soltar archivos aquí o %{browse}',
  dropHint: 'Suelta tus archivos aquí',
  dropPasteBoth: 'Soltar archivos aquí, pegar o %{browse}',
  dropPasteFiles: 'Soltar archivos aquí, pegar o %{browse}',
  dropPasteFolders: 'Soltar archivos aquí, pegar o %{browse}',
  dropPasteImportBoth: 'Soltar archivos aquí, pegar, %{browse} o importar desde',
  dropPasteImportFiles: 'Soltar archivos aquí, pegar, %{browse} o importar desde',
  dropPasteImportFolders: 'Soltar archivos aquí, pegar, %{browse} o importar desde',
  editFile: 'Editar archivo',
  editing: 'Editando %{file}',
  emptyFolderAdded: 'Ningún archivo fue agregado desde la carpeta vacía',
  encoding: 'Codificando...',
  enterCorrectUrl: 'URL incorrecta: Por favor asegúrate que estás ingresando un enlace a un archivo',
  enterUrlToImport: 'Ingresa una URL para importar un archivo',
  exceedsSize: 'Este archivo excede el tamaño máximo de',
  failedToFetch: 'Companion no ha podido recuperar esta URL, por favor asegúrate que sea correcta',
  failedToUpload: 'Error al subir %{file}',
  fileSource: 'Fuente de archivo: %{name}',
  filesUploadedOfTotal: {
    '0': '%{complete} de %{smart_count} archivo subido',
    '1': '%{complete} de %{smart_count} archivos subidos',
    '2': '%{complete} de %{smart_count} archivos subidos'
  },
  filter: 'Filtrar',
  finishEditingFile: 'Terminar edición de archivo',
  folderAdded: {
    '0': 'Agregado %{smart_count} archivo desde %{folder}',
    '1': 'Agregados %{smart_count} archivos desde %{folder}',
    '2': 'Agregados %{smart_count} archivos desde %{folder}'
  },
  import: 'Importar',
  importFrom: 'Importar desde %{name}',
  loading: 'Cargando...',
  logOut: 'Cerrar sesión',
  myDevice: 'Mi Dispositivo',
  noFilesFound: 'No existen archivos o carpetas aquí',
  noInternetConnection: 'Sin conexión a Internet',
  pause: 'Pausar',
  pauseUpload: 'Pausar subida',
  paused: 'En pausa',
  poweredBy: 'Soportado por',
  processingXFiles: {
    '0': 'Procesando %{smart_count} archivo',
    '1': 'Procesando %{smart_count} archivos',
    '2': 'Procesando %{smart_count} archivos'
  },
  recordingLength: 'Duración de grabación %{recording_length}',
  removeFile: 'Eliminar archivo',
  resetFilter: 'Limpiar filtro',
  resume: 'Reanudar',
  resumeUpload: 'Reanudar subida',
  retry: 'Intentar nuevamente',
  retryUpload: 'Intentar subida nuevamente',
  saveChanges: 'Guardar cambios',
  selectX: {
    '0': 'Seleccionar %{smart_count}',
    '1': 'Seleccionar %{smart_count}',
    '2': 'Seleccionar %{smart_count}'
  },
  smile: 'Sonríe!',
  startRecording: 'Comenzar la grabación de video',
  stopRecording: 'Detener la grabación de video',
  takePicture: 'Tomar una foto',
  timedOut: 'Subida estancada por %{seconds} segundos, anulando.',
  upload: 'Subir',
  uploadComplete: 'Subida terminada',
  uploadFailed: 'Subida falló',
  uploadPaused: 'Subida pausada',
  uploadXFiles: {
    '0': 'Subir %{smart_count} archivo',
    '1': 'Subir %{smart_count} archivos',
    '2': 'Subir %{smart_count} archivos'
  },
  uploadXNewFiles: {
    '0': 'Subir +%{smart_count} archivo',
    '1': 'Subir +%{smart_count} archivos',
    '2': 'Subir +%{smart_count} archivos'
  },
  uploading: 'Subiendo',
  uploadingXFiles: {
    '0': 'Subiendo %{smart_count} archivo',
    '1': 'Subiendo %{smart_count} archivos',
    '2': 'Subiendo %{smart_count} archivos'
  },
  xFilesSelected: {
    '0': '%{smart_count} archivo seleccionado',
    '1': '%{smart_count} archivos seleccionados',
    '2': '%{smart_count} archivos seleccionados'
  },
  xMoreFilesAdded: {
    '0': '%{smart_count} archivo más agregado',
    '1': '%{smart_count} archivos más agregados',
    '2': '%{smart_count} archivos más agregados'
  },
  xTimeLeft: '%{time} restantes',
  youCanOnlyUploadFileTypes: 'Solo puedes subir: %{types}',
  youCanOnlyUploadX: {
    '0': 'Solo puedes subir %{smart_count} archivo',
    '1': 'Solo puedes subir %{smart_count} archivos',
    '2': 'Solo puedes subir %{smart_count} archivos'
  },
  youHaveToAtLeastSelectX: {
    '0': 'Tienes que seleccionar al menos %{smart_count} archivo',
    '1': 'Tienes que seleccionar al menos %{smart_count} archivos',
    '2': 'Tienes que seleccionar al menos %{smart_count} archivos'
  },
  selectAllFilesFromFolderNamed: 'Seleccionar todos los archivos de la carpeta %{name}',
  unselectAllFilesFromFolderNamed: 'Deselecciona todos los archivos de la carpeta %{name}',
  selectFileNamed: 'Seleccione archivo %{name}',
  unselectFileNamed: 'Deseleccionar archivo %{name}',
  openFolderNamed: 'Carpeta abierta %{name}'
};

es_ES.pluralize = function (n) {
  if (n === 1) {
    return 0;
  }

  return 1;
};

if (typeof window !== 'undefined' && typeof window.Uppy !== 'undefined') {
  window.Uppy.locales.es_ES = es_ES;
}

var es_ES_1 = es_ES;

var it_IT = {};
it_IT.strings = {
  addMoreFiles: 'Aggiungi più file',
  addingMoreFiles: 'Sto aggiungendo altri file',
  allowAccessDescription: 'Per acquisire foto o video con la tua fotocamera, abilitane l\'accesso da questo sito.',
  allowAccessTitle: 'Abilita l\'accesso alla fotocamera',
  authenticateWith: 'Connetti a %{pluginName}',
  authenticateWithTitle: 'Autenticati con %{pluginName} per selezionare i file',
  back: 'Indietro',
  addMore: 'Aggiungi più',
  browse: 'sfoglia',
  browseFiles: 'sfoglia',
  cancel: 'Annulla',
  cancelUpload: 'Annulla upload',
  chooseFiles: 'Scegli i file',
  closeModal: 'Chiudi modale',
  companionError: 'Connessione con Companion fallita',
  complete: 'Completato',
  connectedToInternet: 'Connesso a internet',
  copyLink: 'Copia link',
  copyLinkToClipboardFallback: 'Copia l\'URL sottostante',
  copyLinkToClipboardSuccess: 'Link copiato',
  creatingAssembly: 'Upload in preparazione...',
  creatingAssemblyFailed: 'Transloadit: Non ho potuto creare l\'Assembly',
  dashboardTitle: 'File Uploader',
  dashboardWindowTitle: 'File Uploader (Premi Esc per chiudere)',
  dataUploadedOfTotal: '%{complete} di %{total}',
  done: 'Fatto',
  dropHereOr: 'Trascina i file qui o %{browse}',
  dropHint: 'Trascina i file qui',
  dropPasteBoth: 'Trascina i file qui, incolla o %{browse}',
  dropPasteFiles: 'Trascina i file qui, incolla o %{browse}',
  dropPasteFolders: 'Trascina i file qui, incolla o %{browse}',
  dropPasteImportBoth: 'Trascina i file qui, incolla, %{browse} o importa da',
  dropPasteImportFiles: 'Trascina i file qui, incolla, %{browse} o importa da',
  dropPasteImportFolders: 'Trascina i file qui, incolla, %{browse} o importa da',
  editFile: 'Modifica file',
  editing: 'Modifica %{file}',
  emptyFolderAdded: 'Nessun file aggiunto dalla cartella vuota',
  encoding: 'Encoding...',
  enterCorrectUrl: 'URL non corretta: assicurati che sia un link diretto ad un file',
  enterUrlToImport: 'Immetti l\'URL per importare un file',
  exceedsSize: 'Questo file supera la dimensione massima di',
  failedToFetch: 'Impossibile verificare questa URL, assicurati che sia corretta',
  failedToUpload: 'Upload del file %{file} non riuscito',
  fileSource: 'Sorgente file: %{name}',
  filesUploadedOfTotal: {
    '0': '%{complete} di %{smart_count} file caricato',
    '1': '%{complete} di %{smart_count} file caricati',
    '2': '%{complete} di %{smart_count} file caricati'
  },
  filter: 'Filter',
  finishEditingFile: 'Finish editing file',
  folderAdded: {
    '0': 'Aggiunto %{smart_count} file da %{folder}',
    '1': 'Aggiunti %{smart_count} file da %{folder}',
    '2': 'Aggiunti %{smart_count} file da %{folder}'
  },
  import: 'Importa',
  importFrom: 'Importa da %{name}',
  loading: 'Caricamento...',
  logOut: 'Logout',
  myDevice: 'Il mio computer',
  noFilesFound: 'Non hai file o cartelle qui',
  noInternetConnection: 'Nessuna connessione a internet',
  pause: 'Pausa',
  pauseUpload: 'Pausa upload',
  paused: 'In pausa',
  poweredBy: 'Powered by',
  processingXFiles: {
    '0': 'Sto processando %{smart_count} file',
    '1': 'Sto processando %{smart_count} file',
    '2': 'Sto processando %{smart_count} file'
  },
  removeFile: 'Rimuovi il file',
  resetFilter: 'Ripristina filtro',
  resume: 'Riprendi',
  resumeUpload: 'Riprendi l\'upload',
  retry: 'Riprova',
  retryUpload: 'Riprova l\'upload',
  saveChanges: 'Salva le modifiche',
  selectX: {
    '0': 'Seleziona %{smart_count}',
    '1': 'Seleziona %{smart_count}',
    '2': 'Seleziona %{smart_count}'
  },
  smile: 'Sorridi!',
  startRecording: 'Inizia la registrazione del video',
  stopRecording: 'Interrompi la registrazione del video',
  takePicture: 'Scatta una foto',
  timedOut: 'Upload fermo da %{seconds} secondi, sto annullando.',
  upload: 'Upload',
  uploadComplete: 'Upload completato',
  uploadFailed: 'Upload non riuscito',
  uploadPaused: 'Upload in pausa',
  uploadXFiles: {
    '0': 'Upload di %{smart_count} file',
    '1': 'Upload di %{smart_count} file',
    '2': 'Upload di %{smart_count} file'
  },
  uploadXNewFiles: {
    '0': 'Upload +%{smart_count} file',
    '1': 'Upload +%{smart_count} file',
    '2': 'Upload +%{smart_count} file'
  },
  uploading: 'In caricamento',
  uploadingXFiles: {
    '0': 'Caricando %{smart_count} file',
    '1': 'Caricando %{smart_count} file',
    '2': 'Caricando %{smart_count} file'
  },
  xFilesSelected: {
    '0': '%{smart_count} file selezionato',
    '1': '%{smart_count} file selezionati',
    '2': '%{smart_count} file selezionati'
  },
  xMoreFilesAdded: {
    '0': '%{smart_count} alto file aggiunto',
    '1': '%{smart_count} altri file aggiunti',
    '2': '%{smart_count} ltri file aggiunti'
  },
  xTimeLeft: '%{time} rimasto',
  youCanOnlyUploadFileTypes: 'Puoi caricare solamente: %{types}',
  youCanOnlyUploadX: {
    '0': 'Puoi caricare %{smart_count} solo file',
    '1': 'Puoi caricare solo %{smart_count} file',
    '2': 'Puoi caricare solo %{smart_count} file'
  },
  youHaveToAtLeastSelectX: {
    '0': 'Devi selezionare almeno %{smart_count} file',
    '1': 'Devi selezionare almeno %{smart_count} file',
    '2': 'Devi selezionare almeno %{smart_count} file'
  },
  selectAllFilesFromFolderNamed: 'Seleziona tutti i file dalla cartella %{name}',
  unselectAllFilesFromFolderNamed: 'Deseleziona tutti i file dalla cartella %{name}',
  selectFileNamed: 'Seleziona il file %{name}',
  unselectFileNamed: 'Deseleziona il file %{name}',
  openFolderNamed: 'Cartella aperta %{name}'
};

it_IT.pluralize = function (n) {
  if (n === 1) {
    return 0;
  }

  return 1;
};

if (typeof window !== 'undefined' && typeof window.Uppy !== 'undefined') {
  window.Uppy.locales.it_IT = it_IT;
}

var it_IT_1 = it_IT;

var ja_JP = {};
ja_JP.strings = {
  addMore: 'さらに追加',
  addMoreFiles: 'ファイルを追加',
  addingMoreFiles: 'ファイルを追加しています',
  allowAccessDescription: 'カメラやビデオの機能を使用するには、カメラへのアクセスを許可してください。',
  allowAccessTitle: 'カメラへのアクセスを許可してください',
  authenticateWith: '%{pluginName}に接続します',
  authenticateWithTitle: 'ファイルを選択するには%{pluginName}で認証してください',
  back: '戻る',
  browse: '参照',
  browseFiles: '参照',
  cancel: 'キャンセル',
  cancelUpload: 'アップロードをキャンセル',
  chooseFiles: 'ファイルを選択',
  closeModal: 'モーダルを閉じる',
  companionError: 'Companionとの接続に失敗しました',
  complete: '完了しました',
  connectedToInternet: 'インターネットに接続しました',
  copyLink: 'リンクをコピー',
  copyLinkToClipboardFallback: '以下のURLをコピー',
  copyLinkToClipboardSuccess: 'リンクをクリップボードにコピーしました',
  creatingAssembly: 'アップロードの準備をしています...',
  creatingAssemblyFailed: 'Transloadit: アセンブリを作成できませんでした',
  dashboardTitle: 'ファイルアップローダー',
  dashboardWindowTitle: 'ファイルアップローダーウィンドウ（閉じるにはEscapeキーを押してください）',
  dataUploadedOfTotal: '%{total} %{complete}',
  done: '完了しました',
  dropHereOr: 'ここにファイルをドロップするか%{browse}してください',
  dropHint: 'ここにファイルをドロップしてください',
  dropPasteBoth: 'ここにファイルをドロップするか、貼り付けるか、%{browse}してください',
  dropPasteFiles: 'ここにファイルをドロップするか、貼り付けるか、%{browse}してください',
  dropPasteFolders: 'ここにファイルをドロップするか、貼り付けるか、%{browse}してください',
  dropPasteImportBoth: 'ここにファイルをドロップするか、貼り付けるか、%{browse}するか、以下からインポートしてください',
  dropPasteImportFiles: 'ここにファイルをドロップするか、貼り付けるか、%{browse}するか、以下からインポートしてください',
  dropPasteImportFolders: 'ここにファイルをドロップするか、貼り付けるか、%{browse}するか、以下からインポートしてください',
  editFile: 'ファイルを編集',
  editing: '%{file}を編集しています',
  emptyFolderAdded: 'フォルダが空なためファイルが追加されませんでした',
  encoding: 'エンコードしています...',
  enterCorrectUrl: '不正なURL: ファイルへの直接リンクが入力されていることを確認してください',
  enterUrlToImport: 'ファイルをインポートするためのURLを入力してください',
  exceedsSize: 'ファイルサイズが大きすぎます',
  failedToFetch: 'CompanionがURLを取得できませんでした。URLが正しいか確認してください',
  failedToUpload: '%{file}のアップロードに失敗しました',
  fileSource: '元ファイル：%{name}',
  filesUploadedOfTotal: {
    '0': '%{smart_count} 個のファイルのアップロードが%{complete}',
    '1': '%{smart_count} 個のファイルのアップロードが%{complete}',
    '2': '%{cmart_count} 個のファイルのアップロードが%{complete}'
  },
  filter: 'フィルタ',
  finishEditingFile: 'ファイルの編集を終了',
  folderAdded: {
    '0': '%{folder} から% {smart_count} 個のファイルを追加しました',
    '1': '%{folder} から% {smart_count} 個のファイルを追加しました',
    '2': '%{folder} から% {smart_count} 個のファイルを追加しました'
  },
  import: 'インポート',
  importFrom: '%{name}からインポート',
  loading: 'ロード中...',
  logOut: 'ログアウト',
  myDevice: 'マイデバイス',
  noFilesFound: 'ファイルやフォルダがありません',
  noInternetConnection: 'インターネット接続がありません',
  pause: '一時停止',
  pauseUpload: 'アップロードを一時停止',
  paused: '停止中',
  poweredBy: 'Powered by',
  processingXFiles: {
    '0': '%{smart_count} ファイル処理中',
    '1': '%{smart_count} ファイル処理中',
    '2': '%{smart_count} ファイル処理中'
  },
  removeFile: 'ファイルを消す',
  resetFilter: 'フィルタをリセット',
  resume: '再開',
  resumeUpload: 'アップロードを再開',
  retry: 'リトライ',
  retryUpload: 'アップロードをリトライ',
  saveChanges: '変更を保存',
  selectX: {
    '0': '%{smart_count} を選択',
    '1': '%{smart_count} を選択',
    '2': '%{smart_count} を選択'
  },
  smile: 'スマイル〜！',
  startRecording: '録画開始',
  stopRecording: '録画停止',
  takePicture: '写真を撮る',
  timedOut: 'アップロードが %{seconds} 秒間止まった為中断しました',
  upload: 'アップロード',
  uploadComplete: 'アップロード完了',
  uploadFailed: 'アップロード失敗',
  uploadPaused: 'アップロード一時停止',
  uploadXFiles: {
    '0': '%{smart_count} ファイルをアップロード',
    '1': '%{smart_count} ファイルをアップロード',
    '2': '%{smart_count} ファイルをアップロード'
  },
  uploadXNewFiles: {
    '0': '+%{smart_count} ファイルをアップロード',
    '1': '+%{smart_count} ファイルをアップロード',
    '2': '+%{smart_count} ファイルをアップロード'
  },
  uploading: 'アップロード中',
  uploadingXFiles: {
    '0': '%{smart_count} ファイルアップロード中',
    '1': '%{smart_count} ファイルアップロード中',
    '2': '%{smart_count} ファイルアップロード中'
  },
  xFilesSelected: {
    '0': '%{smart_count} ファイルを選択',
    '1': '%{smart_count} ファイルを選択',
    '2': '%{smart_count} ファイルを選択'
  },
  xMoreFilesAdded: {
    '0': '%{smart_count} ファイルを追加',
    '1': '%{smart_count} ファイルを追加',
    '2': '%{smart_count} ファイルを追加'
  },
  xTimeLeft: '残り %{time}',
  youCanOnlyUploadFileTypes: '許可されているファイル形式は次の物です: %{types}',
  youCanOnlyUploadX: {
    '0': '%{smart_count} ファイル数のみアップロード可能です',
    '1': '%{smart_count} ファイル数のみアップロード可能です',
    '2': '%{smart_count} ファイル数のみアップロード可能です'
  },
  youHaveToAtLeastSelectX: {
    '0': '最低でも %{smart_count} ファイル選択してください',
    '1': '最低でも %{smart_count} ファイル選択してください',
    '2': '最低でも %{smart_count} ファイル選択してください'
  },
  selectAllFilesFromFolderNamed: 'フォルダからすべてのファイルを選択 %{name}',
  unselectAllFilesFromFolderNamed: 'フォルダからすべてのファイルを選択解除 %{name}',
  selectFileNamed: 'ファイルを選ぶ %{name}',
  unselectFileNamed: 'ファイルの選択を解除 %{name}',
  openFolderNamed: '開いたフォルダ %{name}'
};

ja_JP.pluralize = function (n) {
  if (n === 1) {
    return 0;
  }

  return 1;
};

if (typeof window !== 'undefined' && typeof window.Uppy !== 'undefined') {
  window.Uppy.locales.ja_JP = ja_JP;
}

var ja_JP_1 = ja_JP;

var FileUploaderMessage;
(function (FileUploaderMessage) {
    FileUploaderMessage["closed"] = "app.uploader-closed";
    FileUploaderMessage["opened"] = "app.uploader-opened";
    FileUploaderMessage["reset"] = "app.uploader-reset";
    FileUploaderMessage["previewGeneratedSuccess"] = "app.preview-generated-success";
    FileUploaderMessage["previewGeneratedEmptySuccess"] = "app.preview-generated-empty-success";
    FileUploaderMessage["previewGeneratedRemotelySuccess"] = "app.preview-generated-remotely-success";
    FileUploaderMessage["previewGeneratedError"] = "app.preview-generated-error";
    FileUploaderMessage["previewGeneratedRemotelyError"] = "app.preview-generated-remotely-error";
    FileUploaderMessage["uploadStart"] = "app.upload-start";
    FileUploaderMessage["uploadProgress"] = "app.upload-progress";
    FileUploaderMessage["uploadSuccess"] = "app.upload-success";
    FileUploaderMessage["uploadError"] = "app.upload-error";
    FileUploaderMessage["uploadRetry"] = "app.upload-retry";
    FileUploaderMessage["uploadComplete"] = "app.upload-complete";
    FileUploaderMessage["openModal"] = "app.open-modal";
    FileUploaderMessage["closeModal"] = "app.close-modal";
    FileUploaderMessage["fileAdded"] = "app.file-added";
    FileUploaderMessage["fileRemoved"] = "app.file-removed";
    FileUploaderMessage["providerOpened"] = "app.provider-opened";
    FileUploaderMessage["openShutterstock"] = "app.open-shutterstock";
})(FileUploaderMessage || (FileUploaderMessage = {}));

/* eslint-disable @typescript-eslint/ban-ts-comment */
// /** @jsx h */
const UPDATE_MESSAGE_THROTTLE_TIME = 1000;
const DEFAULT_LOCALE = 'en';
class FileUploader {
    constructor(config, eventCallback) {
        this.config = config;
        this.eventCallback = eventCallback;
        this.uppy = Uppy({
            id: 'uppy-uploader',
            ...(this.config.core ?? {}),
        });
        this.allPlugins = this.config?.allPlugins ?? false;
        this.dashboard = this.config?.dashboard !== false;
        this.companionUrl = this.config?.companionUrl;
        this.initPlugins();
        this.initEvents();
        this.initLocale();
    }
    initDashboard() {
        if (this.dashboard || this.allPlugins) {
            this.uppy.use(Dashboard, {
                inline: false,
                height: 300,
                waitForThumbnailsBeforeUpload: false,
                disableThumbnailGenerator: true,
                proudlyDisplayPoweredByUppy: false,
                ...(this.config?.dashboard ?? {}),
            });
            // Monkey patch for setPluginState to be able emit events on opening providers
            // Note! Could be broken, on Uppy version upgrade, if they change internal structure and formats
            const dashboardPlugin = this.uppy.getPlugin('Dashboard');
            const _setPluginState = dashboardPlugin.setPluginState;
            dashboardPlugin.setPluginState = (...params) => {
                const stateUpdate = params[0];
                if (stateUpdate && stateUpdate.activeOverlayType === 'PickerPanel') {
                    const providerId = (stateUpdate.activePickerPanel &&
                        stateUpdate.activePickerPanel.id) ||
                        null;
                    this.uppy.emit('open-provider', providerId);
                }
                return _setPluginState.apply(dashboardPlugin, params);
            };
        }
    }
    initStatusBar() {
        if (this.config?.statusBar !== false || this.allPlugins) {
            this.uppy.use(StatusBar, {
                id: 'StatusBar',
                target: 'body',
                hideAfterFinish: true,
                showProgressDetails: true,
                hideUploadButton: true,
                hideRetryButton: true,
                hidePauseResumeButton: true,
                hideCancelButton: true,
                ...(this.config?.statusBar ?? {}),
            });
        }
    }
    initDestination() {
        if (this.config?.s3 !== false || this.allPlugins) {
            const companionUrl = (this.config?.s3 !== false ? this.config?.s3?.companionUrl : null) ??
                this.companionUrl ??
                'https://uploader-backend.ie.test.gelato.tech/';
            this.uppy.use(AwsS3Multipart, {
                companionUrl: companionUrl,
                ...(this.config?.s3 ?? {}),
            });
        }
    }
    initGoogleDrive() {
        if ((this.config?.googleDrive && this.dashboard) || this.allPlugins) {
            const companionUrl = (this.config?.googleDrive !== false
                ? this.config?.googleDrive?.companionUrl
                : null) ??
                this.companionUrl ??
                'https://companion.uppy.io';
            this.uppy.use(GoogleDrive, {
                target: Dashboard,
                companionUrl,
                ...(this.config?.googleDrive ?? {}),
            });
        }
    }
    initGooglePhotos() {
        if ((this.config?.googlePhotos !== false && this.dashboard) ||
            this.allPlugins) {
            const companionUrl = (this.config?.googlePhotos !== false
                ? this.config?.googlePhotos?.companionUrl
                : null) ??
                this.companionUrl ??
                'https://uploader-backend.ie.test.gelato.tech/';
            this.uppy.use(GooglePhotos, {
                target: Dashboard,
                companionUrl,
                ...(this.config?.googlePhotos ?? {}),
            });
        }
    }
    initShutterstock() {
        if ((this.config?.shutterstock && this.dashboard) || this.allPlugins) {
            let companionUrl = this.companionUrl ?? 'https://uploader-backend.ie.test.gelato.tech/';
            if (this.config?.shutterstock) {
                companionUrl = this.config?.shutterstock?.companionUrl;
            }
            this.uppy.use(Shutterstock, {
                target: Dashboard,
                companionUrl,
                ...(this.config?.shutterstock ?? {}),
                openShutterstock: this.openShutterstockTab.bind(this),
            });
        }
    }
    initFacebook() {
        if ((this.config?.facebook !== false && this.dashboard) ||
            this.allPlugins) {
            const companionUrl = (this.config?.facebook !== false
                ? this.config?.facebook?.companionUrl
                : null) ??
                this.companionUrl ??
                'https://uploader-backend.ie.test.gelato.tech/';
            this.uppy.use(Facebook, {
                target: Dashboard,
                companionUrl,
                ...(this.config?.facebook ?? {}),
            });
        }
    }
    initInstagram() {
        if ((this.config?.instagram !== false && this.dashboard) ||
            this.allPlugins) {
            const companionUrl = (this.config?.instagram !== false
                ? this.config?.instagram?.companionUrl
                : null) ??
                this.companionUrl ??
                'https://uploader-backend.ie.test.gelato.tech/';
            this.uppy.use(Instagram, {
                target: Dashboard,
                companionUrl,
                ...(this.config?.instagram ?? {}),
            });
        }
    }
    initDropbox() {
        if ((this.config?.dropbox && this.dashboard) || this.allPlugins) {
            const companionUrl = (this.config?.dropbox !== false
                ? this.config?.dropbox?.companionUrl
                : null) ??
                this.companionUrl ??
                'https://companion.uppy.io';
            this.uppy.use(Dropbox, {
                target: Dashboard,
                companionUrl,
                ...(this.config?.dropbox ?? {}),
            });
        }
    }
    initThumbnailGenerator() {
        if (this.config?.thumbnailGenerator !== false || this.allPlugins) {
            this.uppy.use(ThumbnailGenerator, {
                thumbnailWidth: 280,
                waitForThumbnailsBeforeUpload: false,
                lazy: false,
                ...(this.config?.thumbnailGenerator ?? {}),
            });
        }
    }
    initPlugins() {
        this.initDashboard();
        this.initStatusBar();
        this.initShutterstock();
        this.initGoogleDrive();
        this.initGooglePhotos();
        this.initFacebook();
        this.initInstagram();
        this.initDropbox();
        this.initDestination();
        this.initThumbnailGenerator();
    }
    initLocale() {
        this.updateLocale(this.config?.locale ?? null, this.config?.localeOverride ?? null);
    }
    getLocale(localeString) {
        const supportedLocales = {
            de_DE: de_DE_1,
            en: en_US_1,
            fr_FR: fr_FR_1,
            nb_NO: nb_NO_1,
            sv_SE: sv_SE_1,
            es_ES: es_ES_1,
            it_IT: it_IT_1,
            ja_JP: ja_JP_1,
        };
        let localeName = DEFAULT_LOCALE;
        if (localeString && typeof supportedLocales[localeString] !== 'undefined') {
            localeName = localeString;
        }
        return supportedLocales[localeName];
    }
    initUploadEvents() {
        this.uppy.on('upload', (result) => {
            this.eventCallback(FileUploaderMessage.uploadStart, {
                id: result.id,
                fileIDs: result.fileIDs,
                files: result.fileIDs.map((fileID) => this.uppy.getFile(fileID)),
            });
            if (this.config?.hideModalOnUploadStart ?? true) {
                this.close();
            }
        });
        this.uppy.on('upload-success', async (file, response) => {
            try {
                this.eventCallback(FileUploaderMessage.uploadSuccess, {
                    file,
                    url: response.uploadURL,
                });
            }
            catch (error) {
                console.error(error);
                this.eventCallback(FileUploaderMessage.uploadError);
            }
        });
        this.uppy.on('upload-error', (file, error, response) => {
            this.eventCallback(FileUploaderMessage.uploadError, {
                file,
                error,
                response,
            });
        });
        const onUploadProgress = (file, progress) => {
            this.eventCallback(FileUploaderMessage.uploadProgress, {
                file,
                progress,
            });
        };
        const onUploadMap = {};
        this.uppy.on('upload-progress', (file, progress) => {
            if (!onUploadMap[file.id]) {
                onUploadMap[file.id] = throttle_1(onUploadProgress, UPDATE_MESSAGE_THROTTLE_TIME);
            }
            onUploadMap[file.id](file, progress);
        });
        this.uppy.on('upload-retry', (fileID) => {
            this.eventCallback(FileUploaderMessage.uploadRetry, fileID);
        });
        this.uppy.on('complete', (result) => {
            this.eventCallback(FileUploaderMessage.uploadComplete, result);
        });
    }
    initModalEvents() {
        this.uppy.on('dashboard:modal-closed', () => {
            this.eventCallback(FileUploaderMessage.closed);
        });
        this.uppy.on('dashboard:modal-open', () => {
            this.eventCallback(FileUploaderMessage.opened);
        });
        this.uppy.on('open-provider', (providerId) => {
            this.eventCallback(FileUploaderMessage.providerOpened, providerId);
        });
    }
    initThumbnailEvents() {
        this.uppy.on('thumbnail:generated', (file) => this.eventCallback(FileUploaderMessage.previewGeneratedSuccess, file));
        this.uppy.on('thumbnail:generatedRemote', (file) => {
            const fileObject = this.uppy.getFile(file.id);
            if (!fileObject.preview) {
                return;
            }
            this.eventCallback(FileUploaderMessage.previewGeneratedRemotelySuccess, file);
        });
        this.uppy.on('thumbnail:generatedEmpty', (file) => {
            this.eventCallback(FileUploaderMessage.previewGeneratedEmptySuccess, file);
        });
    }
    initFileEvents() {
        this.uppy.on('file-added', (file) => {
            this.uppy.emit('thumbnail:request', file);
            this.eventCallback(FileUploaderMessage.fileAdded, file);
            if (file.isRemote) {
                if (file.preview) {
                    this.eventCallback(FileUploaderMessage.previewGeneratedRemotelySuccess, file);
                }
                else {
                    this.eventCallback(FileUploaderMessage.previewGeneratedRemotelyError, file);
                }
            }
        });
        this.uppy.on('file-removed', (file) => {
            this.eventCallback(FileUploaderMessage.fileRemoved, file);
        });
    }
    initEvents() {
        this.initFileEvents();
        this.initUploadEvents();
        this.initModalEvents();
        this.initThumbnailEvents();
    }
    show() {
        const dashboardPlugin = this.uppy.getPlugin('Dashboard');
        if (dashboardPlugin) {
            dashboardPlugin.openModal();
            this.eventCallback(FileUploaderMessage.openModal);
        }
    }
    close() {
        const dashboardPlugin = this.uppy.getPlugin('Dashboard');
        if (dashboardPlugin) {
            dashboardPlugin.closeModal();
            this.eventCallback(FileUploaderMessage.closeModal);
        }
    }
    openShutterstockTab() {
        this.eventCallback(FileUploaderMessage.openShutterstock);
        this.close();
    }
    reset() {
        this.uppy.reset();
        this.eventCallback(FileUploaderMessage.reset);
    }
    updateLocale(newLocale, override) {
        const localeObject = this.getLocale(newLocale);
        if (override) {
            localeObject.strings = {
                ...localeObject.strings,
                ...override.strings,
            };
        }
        this.uppy.setOptions({
            locale: localeObject,
        });
    }
}

export { FileUploader, FileUploaderMessage, generateFileID, getFileType };
//# sourceMappingURL=index.js.map
